import React, { useState, useEffect, useRef, useMemo, useCallback, MouseEvent } from 'react';
import type { OrderItem, MenuItem as MenuItemType, Category, Customer, OrderType, Table, LoyaltyReward, Order, QuickAddItem, OrderStatus, Payment, SmartpayTerminal, RegisterSession, Reservation, StaffMember, InventoryItem, Recipe, FloorFixture, IntegrationSettings, DeliveryOrder, BuffetPackage, WeeklyBooking, MealPeriod, MealPlanType, MealPlanSale, MealPlanSubscription } from '../types';
import { getAvailableQuantity } from '../lib/dailyAvailability';
import { getOrderTotals, getOrderTotalFromItems, getAmountPaid, hasBalanceDue, orderHasMeaningfulItems, orderMeaningfulItemQty, getTableSeatsUsed, isPendingPosOrder, isCompletedPosOrder, isPosOrderFullySettled, isUnpaidOnlineWebOrder, isFloorPlanActiveTableOrder, orderMatchesFloorPlanTable, isActiveBuffetSession, orderHasAppliedDiscount, mergeOrderPreferComplete, mergeOrdersByIdPreferComplete, fetchOrderFromServer, normalizeOrderRecord, orderNeedsItemReconciliation, orderNeedsPersistedRepair, isOrderMoreCompleteThan, coerceOrderItemsArray, totalsFromGstInclusiveItemSum, getKitchenCompletedFloor, isLineFullyKitchenDone } from '../lib/orderUtils';
import {
  discountFieldsDiffer,
  liveDiscountIsSafeToAdopt,
  pickDiscountSyncFields,
} from '../lib/paymentChargeAmount';
import { generateOrderId } from '../lib/orderIdGenerator';
import { isSampleOrDemoOrderId } from '../lib/sampleOrderIds';
import {
  filterCategoriesForOrderType,
  filterMenuItemsForOrderType,
} from '../lib/menuChannelAvailability';
import { orderCreatedAtMs, orderSaleAtMs, orderSaleDateKey, ymdInTimeZone, ymdToEndMs, ymdToStartMs, addDaysToYmd, formatPickupTimeDisplay } from '../lib/orderTimestamps';
import { auditLogsService, couponCampaignsService, couponsService, couponRedemptionsService, couponUsageLogsService, ordersService, weeklyBookingsService, mealPlanWeeksService, customersService } from '../lib/database';
import { messItemsForPeriod } from '../lib/mealPlanRedeem';
import { mealPlanSaleLine, cancelMealPlanLocally } from '../lib/mealPlanSale';
import { nzDateKey } from '../lib/mealPlanWeek';
import { markUberEatsOrderReady, reportUberOrderIssue } from '../lib/uberEatsAuth';
import CategoryList from '../components/pos/CategoryList';
import MenuItemsGrid from '../components/pos/MenuItemsGrid';
import OrderSummary from '../components/pos/OrderSummary';
import POSHeader from '../components/pos/POSHeader';
import CustomerSelectModal from '../components/pos/CustomerSelectModal';
import RewardRedemptionModal from '../components/pos/RewardRedemptionModal';
import PrintableReceipt from '../components/pos/PrintableReceipt';
import PrintableKitchenOrder, { buildKitchenPrintOrder, snapshotKitchenLines } from '../components/pos/PrintableKitchenOrder';
import PrintTypeModal from '../components/pos/PrintTypeModal';
import PaymentModal from './PaymentModal';
import QuickAddBar from '../components/pos/QuickAddBar';
import CustomerConfirmationDisplay from '../components/pos/CustomerConfirmationDisplay';
import RegisterClosedGuard from '../components/pos/RegisterClosedGuard';
import type { QuickAddCombo } from '../types';
import FloorPlanView from '../components/pos/FloorPlanView';
import { getBuffetPackages } from '../lib/buffetPackages';
import PosGeneralReservationModal from '../components/pos/PosGeneralReservationModal';
import PosMenuManagementOverlay from '../components/pos/PosMenuManagementOverlay';
import PosCustomerManagementOverlay from '../components/pos/PosCustomerManagementOverlay';
import PosMealPlanOverlay from '../components/pos/PosMealPlanOverlay';
import { isGeneralReservation } from '../lib/reservationCapacity';
import OrderHistory from '../components/pos/OrderHistory';
import OrderDetailsModal from '../components/pos/OrderDetailsModal';
import OrderTypeSelector from '../components/pos/OrderTypeSelector';
import DiscountModal from '../components/pos/DiscountModal';
import ManagerApprovalModal from '../components/pos/ManagerApprovalModal';
import CancelOrderApprovalModal from '../components/pos/CancelOrderApprovalModal';
import MeatOptionModal from '../components/pos/MeatOptionModal';
import CheckCircleIcon from '../components/icons/CheckCircleIcon';
import TableMergeModal from '../components/pos/TableMergeModal';
import TableSplitModal from '../components/pos/TableSplitModal';
import OrderTransferModal from '../components/pos/OrderTransferModal';
import OrderOverrideModal from '../components/pos/OrderOverrideModal';
import FireModeModal from '../components/pos/FireModeModal';
import FireModeBanner from '../components/pos/FireModeBanner';
import RemakeOrderModal from '../components/pos/RemakeOrderModal';
import TableCustomerSelectModal from '../components/pos/TableCustomerSelectModal';
import DineInCoversModal from '../components/pos/DineInCoversModal';
import BuffetTypeSelector from '../components/pos/BuffetTypeSelector';
import UberDoordashOrderModal from '../components/pos/UberDoordashOrderModal';
import CustomerNameModal from '../components/pos/CustomerNameModal';
import BuffetExtensionModal from '../components/pos/BuffetExtensionModal';
import BuffetTimer from '../components/pos/BuffetTimer';
import BuffetPeopleModal from '../components/pos/BuffetPeopleModal';
import { buildEventTokenLabelById, formatEventTokenShortBadge, getEventTokenDisplayLabel } from '../lib/eventOrderTokens';

/** Skip the forced-server Pending/Floor Plan refetch if we already did one this recently (staff bounce between tabs a lot). */
const PENDING_ORDERS_REFRESH_INTERVAL_MS = 20_000;

const DRAFT_ORDER_KEY = 'kings_park_pos_draft_order';
const POS_VIEW_KEY = 'kings_park_pos_view';
const POS_ACTIVE_VIEW_KEY = 'kings_park_pos_active_view';

type PosViewType = 'active-orders' | 'floor' | 'new-order' | 'pending' | 'completed' | 'cancelled' | 'refunded' | 'reservations';
const VALID_POS_VIEWS: PosViewType[] = ['active-orders', 'floor', 'new-order', 'pending', 'completed', 'cancelled', 'refunded', 'reservations'];
type ActiveViewType = 'floor' | 'menu' | 'history';
const VALID_ACTIVE_VIEWS: ActiveViewType[] = ['floor', 'menu', 'history'];

/** Pending/Completed cards: fill horizontal space; column count follows viewport width. */
const POS_ORDER_LIST_GRID_CLASS =
  'grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,300px),1fr))]';
const POS_ORDER_LIST_SECTION_CLASS = 'w-full space-y-3';

function formatOrderPlacedDisplay(order: Order): string {
  const ms = orderCreatedAtMs(order);
  if (ms <= 0) return 'Invalid Date';
  return new Date(ms).toLocaleString('en-NZ', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function pendingPaymentStatusLine(order: Order): string {
  if (order.paymentStatus === 'paid' && (order.orderType === 'takeaway' || (order.orderType === 'online' && order.status !== 'served'))) {
    return 'Paid · Awaiting pickup';
  }
  if (order.paymentStatus === 'paid' && order.orderType === 'buffet') return 'Paid · Session active';
  if (order.paymentStatus === 'paid' && order.orderType === 'uber-doordash') return 'Paid via platform';
  if (order.paymentStatus === 'paid') return 'Paid';
  return 'Payment pending';
}

type HistoryOrdersDateFilter = 'today' | 'yesterday' | 'weekly' | 'custom';

function mergeOrderSnapshots(...candidates: (Order | null | undefined)[]): Order | null {
  let merged: Order | undefined;
  for (const candidate of candidates) {
    if (!candidate) continue;
    merged = merged ? mergeOrderPreferComplete(merged, candidate) : candidate;
  }
  return merged ?? null;
}

function mergeOrderSources(...lists: Order[][]): Order[] {
  return mergeOrdersByIdPreferComplete(lists.flat());
}

/** Content fingerprint for placed-order live sync — avoids infinite setState on new object refs. */
function placedOrderSyncSignature(order: Order): string {
  const items = order.items ?? [];
  const itemKey = items
    .map(
      (i) =>
        `${i.lineId ?? i.id}|${i.quantity}|${i.removed ? 1 : 0}|${i.status ?? ''}|${i.served ? 1 : 0}|${i.quantityCompleted ?? ''}|${i.quantityServed ?? ''}`,
    )
    .join(';');
  const payKey = (order.payments ?? [])
    .map((p) => `${p.method}:${p.amount}:${p.timestamp ?? ''}`)
    .join(',');
  return `${order.id}|${order.status}|${order.paymentStatus}|${order.totalAmount ?? 0}|${payKey}|${itemKey}|${order.subBills?.length ?? 0}`;
}

function filterOrdersByHistoryDate(
  orders: Order[],
  filter: HistoryOrdersDateFilter,
  opts: {
    todayKey: string;
    yesterdayKey: string;
    weekStartYmd: string;
    customDateStart: string;
    customDateEnd: string;
    getOrderDateKey: (order: Order) => string;
  },
): Order[] {
  switch (filter) {
    case 'today':
      return orders.filter((o) => opts.getOrderDateKey(o) === opts.todayKey);
    case 'yesterday':
      return orders.filter((o) => opts.getOrderDateKey(o) === opts.yesterdayKey);
    case 'weekly':
      return orders.filter((o) => {
        const d = opts.getOrderDateKey(o);
        return d >= opts.weekStartYmd && d <= opts.todayKey;
      });
    case 'custom':
      if (!opts.customDateStart || !opts.customDateEnd) return [];
      return orders.filter((o) => {
        const d = opts.getOrderDateKey(o);
        return d >= opts.customDateStart && d <= opts.customDateEnd;
      });
    default:
      return orders;
  }
}

const HistoryOrdersDateFilterBar: React.FC<{
  filter: HistoryOrdersDateFilter;
  onFilterChange: (filter: HistoryOrdersDateFilter) => void;
  customDateStart: string;
  customDateEnd: string;
  onCustomDateStartChange: (value: string) => void;
  onCustomDateEndChange: (value: string) => void;
  extraControls?: React.ReactNode;
}> = ({
  filter,
  onFilterChange,
  customDateStart,
  customDateEnd,
  onCustomDateStartChange,
  onCustomDateEndChange,
  extraControls,
}) => (
  <div className="flex flex-wrap gap-2 items-center">
    {extraControls}
    <div className={`flex gap-2 ${extraControls ? 'ml-2' : ''}`}>
      {(['today', 'yesterday', 'weekly', 'custom'] as const).map((key) => (
        <button
          key={key}
          type="button"
          onClick={() => onFilterChange(key)}
          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
            filter === key ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {key === 'today' ? 'Today' : key === 'yesterday' ? 'Yesterday' : key === 'weekly' ? 'Week' : 'By Date'}
        </button>
      ))}
    </div>
    {filter === 'custom' && (
      <div className="flex gap-2 items-center">
        <input
          type="date"
          value={customDateStart}
          onChange={(e) => onCustomDateStartChange(e.target.value)}
          className="px-2 py-1 text-sm border border-gray-300 rounded-md"
          aria-label="Start date"
        />
        <span className="text-gray-500">to</span>
        <input
          type="date"
          value={customDateEnd}
          onChange={(e) => onCustomDateEndChange(e.target.value)}
          className="px-2 py-1 text-sm border border-gray-300 rounded-md"
          aria-label="End date"
        />
      </div>
    )}
  </div>
);

function getStoredPosView(): PosViewType | null {
  try {
    const stored = sessionStorage.getItem(POS_VIEW_KEY);
    if (stored && VALID_POS_VIEWS.includes(stored as PosViewType)) return stored as PosViewType;
  } catch (_) {}
  return null;
}
function getStoredActiveView(): ActiveViewType | null {
  try {
    const stored = sessionStorage.getItem(POS_ACTIVE_VIEW_KEY);
    if (stored && VALID_ACTIVE_VIEWS.includes(stored as ActiveViewType)) return stored as ActiveViewType;
  } catch (_) {}
  return null;
}

interface POSPageProps {
    onPlaceOrder: (items: OrderItem[], orderType: OrderType, details: { tableId?: number, tableName?: string, preferredOrderId?: string, customerId?: number, customerNumber?: number, customerName?: string, notes?: string, appliedReward?: LoyaltyReward, manualDiscount?: number, discountRequiresApproval?: boolean, discountApprovedBy?: number, discountApprovalReason?: string, discountApprovalTimestamp?: number, buffetType?: 'breakfast' | 'lunch' | 'dinner', buffetPackageId?: string, buffetStartTime?: number, buffetEndTime?: number, servingStaffId?: number, servingStaffName?: string, deliveryInfo?: { platform: 'Uber Eats' | 'DoorDash'; customerName: string }, couponCode?: string, couponCampaignId?: string, couponDiscountType?: 'percentage' | 'fixed', couponDiscountValue?: number, couponDiscountAmount?: number, couponAppliedByStaffId?: number, couponAppliedAt?: number, couponUnitLabel?: string, couponBuildingWing?: 'hotel_wing' | 'tower_wing', residentDiscountPercent?: number, residentDiscountAmount?: number, mealPlanBookingId?: string, mealPlanWeekId?: string, mealPlanPeriod?: 'breakfast' | 'lunch' | 'dinner', mealPlanEntitlementId?: string, mealPlanDiscountAmount?: number, mealPlanSale?: MealPlanSale, mealPlanSales?: MealPlanSale[], deliveryCharge?: number }) => Order | void | Promise<Order | void>;
    onUpdateOrder: (order: Order) => void | Order | Promise<void | Order>;
    onRemoveOrder?: (orderId: string) => Promise<void>;
    onProcessPayment: (orderId: string, payment: Payment) => void;
    /** Manager-only: refund a single closed sub-bill (restores paidQuantity, writes negative payment record). */
    onRefundSubBill?: (orderId: string, subBillId: string) => Promise<void> | void;
    onMarkAsPaid?: (orderId: string) => void;
    menuItems: MenuItemType[];
    categories: Category[];
    customers: Customer[];
    tables: Table[];
    layoutComponents?: FloorFixture[];
    rewards: LoyaltyReward[];
    orders: Order[];
    /** Force a server read of the live orders window (bypasses stale offline cache). */
    onRefreshOrders?: () => Promise<void>;
    reservations?: Reservation[];
    onSaveReservation?: (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void> | void;
    onUpdateReservation?: (id: number, updates: Partial<Reservation>) => Promise<void> | void;
    onDeleteReservation?: (id: number) => Promise<void> | void;
    quickAddCombos?: QuickAddCombo[];
    terminals: SmartpayTerminal[];
    registerSession: RegisterSession | undefined;
    activeSessions?: RegisterSession[];
    /**
     * False until the global `subscribeActive` listener in App.tsx has delivered
     * its first snapshot. Forwarded to RegisterClosedGuard so the spinner is
     * shown instead of the Open Register button while we don't know whether
     * another device has already opened a shared session.
     */
    registerSessionsHydrated?: boolean;
    terminalId?: string;
    terminalName?: string;
    onTerminalNameChange?: (name: string) => void;
    onOpenRegister: (openingFloat: number) => void;
    onCloseRegister: (
      closingFloat: number,
      session: RegisterSession,
      todaysSales: { cash: number; card: number; cashDenominations?: { denom: number; count: number }[] },
    ) => void;
    onForceCloseSession?: (session: RegisterSession) => void;
    showForceClose?: boolean;
    onMoveCategory?: (categoryId: number | string, direction: 'up' | 'down') => void;
    onReorderCategory?: (categoryId: number | string, targetIndex: number) => void;
    staffMembers?: StaffMember[];
    currentUser?: StaffMember;
    inventoryItems?: InventoryItem[];
    recipes?: Recipe[];
    onStockTake?: (physicalCounts: { [itemId: number]: number }) => void;
    onRequireManagerPin: (action: 'open' | 'close', onApproved: () => void) => void;
    integrationSettings?: IntegrationSettings;
    onUpdateMenuItem?: (itemId: number, updates: Partial<MenuItemType>) => Promise<void>;
    /** Full menu CRUD for MNG MENU overlay (staff with canManageMenu). */
    onSaveMenuItem?: (item: MenuItemType) => void | Promise<void>;
    onDeleteMenuItem?: (itemId: number) => void | Promise<void>;
    onSaveCategory?: (category: Category) => void | Promise<void>;
    onDeleteCategory?: (categoryId: number | string) => void | Promise<void>;
    onBulkAddMenuItems?: (items: any[]) => Promise<{ successCount: number; failedItems: string[] }>;
    onSaveCombo?: (combo: Omit<QuickAddCombo, 'id'> | QuickAddCombo) => Promise<void>;
    onDeleteCombo?: (comboId: number) => Promise<void>;
    onSaveRecipe?: (recipe: Recipe) => void | Promise<void>;
    onSaveBuffetPackages?: (packages: BuffetPackage[]) => Promise<void> | void;
    /** Full customer CRUD for MNG CUST overlay (staff with canManageCustomers). */
    onSaveCustomer?: (customer: Customer) => void | Promise<void>;
    onDeleteCustomer?: (customerId: number) => void | Promise<void>;
    onMergeTables?: (
      tableIds: number[],
      options?: { allowReservedOrOccupied?: boolean; silent?: boolean },
    ) => Promise<boolean | void>;
    onSplitTables?: (tableId: number) => Promise<void>;
    onTransferOrder?: (orderId: string, targetTableId: number) => Promise<void>;
    /** Move pending/confirmed/seated reservation before an order exists */
    onTransferReservation?: (
      reservationId: number,
      targetTableId: number,
      sourceTableId?: number,
    ) => Promise<void>;
    onOverrideOrder?: (orderId: string, reason: string) => Promise<void>;
    onRemakeOrder?: (orderId: string, items: OrderItem[], reason: string) => Promise<void>;
    onExtendBuffetTime?: (orderId: string, extensionMinutes: number, reason?: string) => Promise<void>;
    fireModeActive?: boolean;
    onActivateFireMode?: () => void;
    onDeactivateFireMode?: () => void;
    onCreateCustomer?: (customer: Omit<Customer, 'id'>) => Promise<Customer>;
    onAuditPrint?: (type: 'customer' | 'kitchen', order: Order) => void;
    /** Optional: update order status (e.g. for Uber/DoorDash ready / picked up). */
    onUpdateOrderStatus?: (orderId: string, status: OrderStatus) => void;
    /** Incoming third-party delivery orders (Uber/DoorDash) that are waiting for acceptance. */
    uberPendingOrders?: DeliveryOrder[];
    /** Controls Uber incoming chime playback in POS (disable outside POS screens). */
    enableUberIncomingAlert?: boolean;
    onAcceptUberDeliveryOrder?: (order: DeliveryOrder) => void;
    onRejectUberDeliveryOrder?: (order: DeliveryOrder) => void;
}

const POSPage: React.FC<POSPageProps> = ({ 
    onPlaceOrder, 
    onUpdateOrder,
    onRemoveOrder,
    onProcessPayment,
    onRefundSubBill,
    onMarkAsPaid,
    menuItems,
    categories, 
    customers, 
    tables, 
    rewards, 
    orders, 
    onRefreshOrders,
    reservations = [],
    onSaveReservation,
    onUpdateReservation,
    onDeleteReservation,
    quickAddCombos = [],
    terminals,
    layoutComponents = [],
    registerSession,
    onCreateCustomer,
    activeSessions = [],
    registerSessionsHydrated = true,
    terminalId = '',
    terminalName = '',
    onTerminalNameChange,
    onOpenRegister,
    onCloseRegister,
    onForceCloseSession,
    showForceClose = false,
    onMoveCategory,
    onReorderCategory,
    staffMembers = [],
    currentUser,
    inventoryItems = [],
    recipes = [],
    onStockTake,
    integrationSettings,
    onUpdateMenuItem,
    onSaveMenuItem,
    onDeleteMenuItem,
    onSaveCategory,
    onDeleteCategory,
    onBulkAddMenuItems,
    onSaveCombo,
    onDeleteCombo,
    onSaveRecipe,
    onSaveBuffetPackages,
    onSaveCustomer,
    onDeleteCustomer,
    onMergeTables,
    onSplitTables,
    onTransferOrder,
    onTransferReservation,
    onOverrideOrder,
    onRemakeOrder,
    onExtendBuffetTime,
    onAuditPrint,
    fireModeActive = false,
    onActivateFireMode,
    onDeactivateFireMode,
    onRequireManagerPin,
    uberPendingOrders = [],
    enableUberIncomingAlert = true,
    onAcceptUberDeliveryOrder,
    onRejectUberDeliveryOrder,
    onUpdateOrderStatus,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(null);
  const [menuSearchQuery, setMenuSearchQuery] = useState('');
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  
  const [notes, setNotes] = useState('');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmationDisplayOpen, setIsConfirmationDisplayOpen] = useState(false);
  const [showUberDoordashModal, setShowUberDoordashModal] = useState(false);
  const [uberDoordashDetails, setUberDoordashDetails] = useState<{ platform: 'Uber Eats' | 'DoorDash'; customerName: string } | null>(null);
  const [showCustomerNameModal, setShowCustomerNameModal] = useState(false);
  const [pendingOrderTypeForCustomerName, setPendingOrderTypeForCustomerName] = useState<'takeaway' | 'delivery' | null>(null);
  const [orderCustomerName, setOrderCustomerName] = useState<string | null>(null);
  /** GST-inclusive manual delivery fee (manual delivery orders only). */
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [selectedServingStaff, setSelectedServingStaff] = useState<StaffMember | null>(null);
  const [orderType, setOrderType] = useState<OrderType | null>(null);

  const visibleCategories = useMemo(
    () => filterCategoriesForOrderType(categories, menuItems, orderType),
    [categories, menuItems, orderType]
  );

  const orderTypeFilteredMenuItems = useMemo(
    () => filterMenuItemsForOrderType(menuItems, categories, orderType),
    [menuItems, categories, orderType]
  );

  // Update selectedCategoryId when categories load (category id can be number or Firestore string)
  useEffect(() => {
    if (visibleCategories.length === 0) return;
    const first = visibleCategories[0];

    if (selectedCategoryId == null || selectedCategoryId === '') {
      if (first) setSelectedCategoryId(first.id);
      return;
    }
    const categoryExists = visibleCategories.some(
      (c) => c.id === selectedCategoryId || String(c.id) === String(selectedCategoryId)
    );
    if (!categoryExists) {
      if (first) setSelectedCategoryId(first.id);
    }
  }, [visibleCategories, selectedCategoryId]);

  const [customerNumber, setCustomerNumber] = useState<number | undefined>(undefined);
  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [showTableCustomerModal, setShowTableCustomerModal] = useState(false);
  const [pendingTableSelection, setPendingTableSelection] = useState<Table | null>(null);
  const [showDineInCoversModal, setShowDineInCoversModal] = useState(false);
  const [pendingDineInTable, setPendingDineInTable] = useState<Table | null>(null);
  const [pendingDineInSpareSeats, setPendingDineInSpareSeats] = useState<number>(0);
  const [pendingDineInCustomerName, setPendingDineInCustomerName] = useState<string | undefined>(undefined);
  const [pendingDineInBookedPartySize, setPendingDineInBookedPartySize] = useState<number | null>(null);
  /** When seating from a reservation — used for notes + optional “more guests” adjust. */
  const [activePartySize, setActivePartySize] = useState<number | null>(null);
  const [activeBookedPartySize, setActiveBookedPartySize] = useState<number | null>(null);
  const [adjustingReservationCovers, setAdjustingReservationCovers] = useState(false);
  const [showBuffetTypeSelector, setShowBuffetTypeSelector] = useState(false);
  const [selectedBuffetType, setSelectedBuffetType] = useState<'breakfast' | 'lunch' | 'dinner' | null>(null);
  const [selectedBuffetPackage, setSelectedBuffetPackage] = useState<BuffetPackage | null>(null);
  const selectedBuffetPackageRef = useRef<BuffetPackage | null>(null);
  const selectedBuffetTypeRef = useRef<'breakfast' | 'lunch' | 'dinner' | null>(null);
  selectedBuffetPackageRef.current = selectedBuffetPackage;
  selectedBuffetTypeRef.current = selectedBuffetType;
  const [showBuffetPeopleModal, setShowBuffetPeopleModal] = useState(false);
  const [buffetPlaceError, setBuffetPlaceError] = useState<string | null>(null);
  const [buffetNumberOfPeople, setBuffetNumberOfPeople] = useState<number>(1);
  /** Only true after staff explicitly choose “Add extras” — never enter menu during covers/payment. */
  const [buffetExtrasMenuOpen, setBuffetExtrasMenuOpen] = useState(false);
  const [appliedReward, setAppliedReward] = useState<LoyaltyReward | null>(null);
  const [appliedMealBooking, setAppliedMealBooking] = useState<WeeklyBooking | null>(null);
  const [todayMealBookings, setTodayMealBookings] = useState<WeeklyBooking[]>([]);
  /** Soft first-visit Student ID prompt before Collect — never blocks Collect. */
  const [pendingMealCollectVerify, setPendingMealCollectVerify] = useState<WeeklyBooking | null>(null);
  const [manualDiscount, setManualDiscount] = useState<number>(0);
  const [discountRequiresApproval, setDiscountRequiresApproval] = useState(false);
  const [discountApprovedBy, setDiscountApprovedBy] = useState<number | undefined>(undefined);
  const [discountApprovalReason, setDiscountApprovalReason] = useState<string | undefined>(undefined);
  const [discountApprovalTimestamp, setDiscountApprovalTimestamp] = useState<number | undefined>(undefined);
  const [discountMethod, setDiscountMethod] = useState<'manual' | 'first-table' | undefined>(undefined);
  const [firstTableDiscountAmount, setFirstTableDiscountAmount] = useState<number>(0);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [couponCampaignId, setCouponCampaignId] = useState<string | null>(null);
  const [couponDiscountType, setCouponDiscountType] = useState<'percentage' | 'fixed' | null>(null);
  const [couponDiscountValue, setCouponDiscountValue] = useState<number>(0);
  const [couponDiscountAmount, setCouponDiscountAmount] = useState<number>(0);
  const [couponUnitLabel, setCouponUnitLabel] = useState<string | undefined>(undefined);
  const [couponBuildingWing, setCouponBuildingWing] = useState<'hotel_wing' | 'tower_wing' | undefined>(undefined);
  const [couponError, setCouponError] = useState<string | null>(null);
  
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [pendingDiscount, setPendingDiscount] = useState<number>(0);
  const [pendingDiscountReason, setPendingDiscountReason] = useState<string | undefined>(undefined);
  const [pendingDiscountMethod, setPendingDiscountMethod] = useState<'manual' | 'first-table'>('manual');
  
  const [placedOrder, setPlacedOrder] = useState<Order | null>(null);
  const [orderToPrint, setOrderToPrint] = useState<Order | null>(null);
  const [printType, setPrintType] = useState<'customer' | 'kitchen'>('customer');
  const [printPendingOrder, setPrintPendingOrder] = useState<Order | null>(null);
  /** Frozen ticket for the last successful Update — Print Order → Kitchen must use this, not a stale snapshot. */
  const [lastKitchenPrintOrder, setLastKitchenPrintOrder] = useState<Order | null>(null);
  // Sub-bill receipt printing: either a closed SubBill (reprint) or a draft preview (before payment).
  const [subBillToPrint, setSubBillToPrint] = useState<{
    order: Order;
    subBill?: import('../types').SubBill;
    preview?: import('../components/pos/PrintableReceipt').SubBillPreview;
  } | null>(null);
  const [selectedHistoryOrder, setSelectedHistoryOrder] = useState<Order | null>(null);
  const [shouldAutoOpenPaymentModal, setShouldAutoOpenPaymentModal] = useState(false);
  const [isCompletingBuffet, setIsCompletingBuffet] = useState(false);
  const [uberIssueOrder, setUberIssueOrder] = useState<Order | null>(null);
  const [uberIssueSelectedItemIndices, setUberIssueSelectedItemIndices] = useState<number[]>([]);
  const [uberIssueReason, setUberIssueReason] = useState<'OUT_OF_STOCK' | 'ITEM_INCORRECT' | 'OTHER'>('OUT_OF_STOCK');
  const [uberIssueExplanation, setUberIssueExplanation] = useState('');
  const [isSubmittingUberIssue, setIsSubmittingUberIssue] = useState(false);
  const prevUberPendingCountRef = useRef<number>(uberPendingOrders.length);
  const enableUberIncomingAlertRef = useRef<boolean>(enableUberIncomingAlert);
  
  const playUberAlert = useCallback(() => {
    try {
      const audio = new Audio('/sounds/uber-chime.mp3');
      audio.volume = 1;
      // Fire and forget; browsers may block autoplay depending on user interaction.
      // Use .catch (not `void`) to swallow rejections like AbortError from interrupted play/pause.
      audio.play().catch(() => { /* autoplay restriction or interrupted */ });
    } catch {
      // Ignore audio errors (e.g. autoplay restrictions)
    }
  }, []);

  useEffect(() => {
    enableUberIncomingAlertRef.current = enableUberIncomingAlert;
  }, [enableUberIncomingAlert]);

  useEffect(() => {
    if (!enableUberIncomingAlertRef.current) return;
    if (uberPendingOrders.length > prevUberPendingCountRef.current) {
      playUberAlert();
    }
    prevUberPendingCountRef.current = uberPendingOrders.length;
  }, [uberPendingOrders.length, playUberAlert]);

  useEffect(() => {
    if (!enableUberIncomingAlertRef.current) return;
    if (uberPendingOrders.length === 0) return;
    const id = window.setInterval(() => {
      playUberAlert();
    }, 10000);
    return () => window.clearInterval(id);
  }, [uberPendingOrders.length, playUberAlert]);

  const handleSubmitUberIssue = useCallback(async () => {
    if (!uberIssueOrder) return;
    const uberOrderId = uberIssueOrder.deliveryInfo?.orderId;
    if (!uberOrderId) {
      alert('This order is missing the Uber order ID.');
      return;
    }
    if (!uberIssueSelectedItemIndices.length) {
      alert('Please select at least one item.');
      return;
    }
    const items = uberIssueSelectedItemIndices
      .map((idx) => uberIssueOrder.items[idx])
      .filter(Boolean)
      .map((it) => ({
        title: it.name,
        quantity: it.quantity,
        ...(it.cartItemId ? { cartItemId: it.cartItemId } : {}),
      }));
    if (!items.length) {
      alert('Please select at least one item.');
      return;
    }

    setIsSubmittingUberIssue(true);
    try {
      await reportUberOrderIssue(uberOrderId, {
        items,
        reasonCode: uberIssueReason,
        explanation: uberIssueExplanation.trim() || undefined,
      });
      alert('Issue reported to Uber. The customer will be asked to confirm changes.');
      setUberIssueOrder(null);
      setUberIssueSelectedItemIndices([]);
      setUberIssueExplanation('');
      setUberIssueReason('OUT_OF_STOCK');
    } catch (err) {
      console.error('reportUberOrderIssue failed:', err);
      alert((err as any)?.message || 'Failed to report issue to Uber.');
    } finally {
      setIsSubmittingUberIssue(false);
    }
  }, [uberIssueOrder, uberIssueSelectedItemIndices, uberIssueReason, uberIssueExplanation]);

  // History orders (Completed / Cancelled / Refunded) date filter — shared across those tabs
  const [historyOrdersDateFilter, setHistoryOrdersDateFilter] = useState<HistoryOrdersDateFilter>('today');
  const [completedOrderTypeFilter, setCompletedOrderTypeFilter] = useState<OrderType | 'all'>('all');
  const [customDateStart, setCustomDateStart] = useState<string>('');
  const [customDateEnd, setCustomDateEnd] = useState<string>('');
  const [historyOrdersFetched, setHistoryOrdersFetched] = useState<Order[]>([]);
  const [historyOrdersFetchLoading, setHistoryOrdersFetchLoading] = useState(false);
  const historyOrdersFetchKeyRef = useRef<string | null>(null);
  const [pendingOrdersFetched, setPendingOrdersFetched] = useState<Order[]>([]);
  const [pinnedOrdersById, setPinnedOrdersById] = useState<Record<string, Order>>({});
  const [pendingOrdersFetchLoading, setPendingOrdersFetchLoading] = useState(false);
  const pendingOrdersFetchedAtRef = useRef(0);

  const businessTimeZone = integrationSettings?.businessHours?.timezone || 'Pacific/Auckland';

  const generalReservationsToday = useMemo(() => {
    const todayIso = new Date().toISOString().split('T')[0];
    return reservations
      .filter(
        (r) =>
          isGeneralReservation(r) &&
          r.reservationDate === todayIso &&
          r.status !== 'cancelled' &&
          r.status !== 'completed' &&
          r.status !== 'waitlist',
      )
      .sort((a, b) => a.reservationTime.localeCompare(b.reservationTime));
  }, [reservations]);

  const completedReservationsToday = useMemo(() => {
    const todayIso = new Date().toISOString().split('T')[0];
    return reservations
      .filter(
        (r) =>
          isGeneralReservation(r) &&
          r.reservationDate === todayIso &&
          r.status === 'completed',
      )
      .sort((a, b) => a.reservationTime.localeCompare(b.reservationTime));
  }, [reservations]);

  const getCompletedOrdersWeekStartYmd = useCallback((): string => {
    const todayStr = ymdInTimeZone(businessTimeZone);
    const [year, month, day] = todayStr.split('-').map(Number);
    const nzDate = new Date(year, month - 1, day);
    const dayOfWeek = nzDate.getDay();
    const weekStart = new Date(nzDate);
    weekStart.setDate(nzDate.getDate() - dayOfWeek);
    return weekStart.toLocaleDateString('en-CA');
  }, [businessTimeZone]);

  const todayKey = useMemo(() => ymdInTimeZone(businessTimeZone), [businessTimeZone]);
  const yesterdayKey = useMemo(() => addDaysToYmd(todayKey, -1), [todayKey]);

  const hydrateOrder = useCallback(
    (order: Order) => normalizeOrderRecord(order, menuItems),
    [menuItems],
  );

  const pinOrderBriefly = useCallback((order: Order) => {
    const normalized = hydrateOrder(order);
    setPinnedOrdersById((prev) => ({ ...prev, [normalized.id]: normalized }));
    // Prefer-server one-shot so other tabs / hung cache catch up for this id only.
    void ordersService.getById(normalized.id, { preferServer: true }).then((fresh) => {
      if (!fresh || fresh.isDeleted) return;
      const next = hydrateOrder(fresh);
      setPinnedOrdersById((prev) => {
        const existing = prev[next.id];
        if (!existing) return { ...prev, [next.id]: next };
        // Don't clobber a richer just-saved pin with a thin/stale read.
        return { ...prev, [next.id]: mergeOrderPreferComplete(existing, next) };
      });
    }).catch(() => {});
  }, [hydrateOrder]);

  const patchPendingOrderFields = useCallback((orderId: string, patch: Partial<Order>) => {
    setPendingOrdersFetched((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, ...patch, deliveryInfo: { ...o.deliveryInfo, ...patch.deliveryInfo } } : o)),
    );
    setPinnedOrdersById((prev) => {
      const existing = prev[orderId];
      if (!existing) return prev;
      return {
        ...prev,
        [orderId]: {
          ...existing,
          ...patch,
          deliveryInfo: { ...existing.deliveryInfo, ...patch.deliveryInfo },
        },
      };
    });
  }, []);

  const pinRepairedOrder = useCallback((raw: Order, hydrated: Order) => {
    if (!orderNeedsPersistedRepair(raw, hydrated)) return;
    setPinnedOrdersById((prev) => {
      const existing = prev[hydrated.id];
      if (existing && !isOrderMoreCompleteThan(hydrated, existing)) return prev;
      return { ...prev, [hydrated.id]: hydrated };
    });
  }, []);

  const hydratedOrders = useMemo(
    () => orders.map(hydrateOrder),
    [orders, hydrateOrder],
  );

  useEffect(() => {
    for (const order of orders) {
      pinRepairedOrder(order, normalizeOrderRecord(order, menuItems));
    }
  }, [orders, menuItems, pinRepairedOrder]);

  const persistRepairedOrder = useCallback(
    async (repaired: Order) => {
      try {
        const serverOrder = await ordersService.getById(repaired.id, { preferServer: true });
        if (!serverOrder) return;

        const serverItemCount = coerceOrderItemsArray(serverOrder.items).length;
        const repairedItemCount = coerceOrderItemsArray(repaired.items).length;
        // Expand truncated rows, or shrink false online-* duplicate clones.
        if (repairedItemCount === serverItemCount) return;
        if (repairedItemCount < serverItemCount) {
          // Only persist shrink when collapsing online snapshot duplicates.
          if (!orderNeedsPersistedRepair(serverOrder, repaired)) return;
        } else if (!orderNeedsPersistedRepair(serverOrder, repaired)) {
          return;
        }

        const saved = await ordersService.update(repaired.id, {
          items: repaired.items,
          subtotal: repaired.subtotal,
          taxAmount: repaired.taxAmount,
          totalAmount: repaired.totalAmount,
          lastSentItemSnapshot: repaired.lastSentItemSnapshot ?? serverOrder.lastSentItemSnapshot,
        });
        const normalized = normalizeOrderRecord(saved, menuItems);
        setPinnedOrdersById((prev) => ({ ...prev, [normalized.id]: normalized }));
        onRefreshOrders?.();
      } catch (err) {
        console.warn(`Failed to persist repaired order ${repaired.id}`, err);
      }
    },
    [onRefreshOrders, menuItems],
  );

  const getOrderSaleDateKey = useCallback(
    (order: Order): string => orderSaleDateKey(order, businessTimeZone),
    [businessTimeZone],
  );

  const pendingOrdersSource = useMemo(
    () => mergeOrdersByIdPreferComplete([
      ...pendingOrdersFetched,
      ...Object.values(pinnedOrdersById),
      ...hydratedOrders, // live last so equal-completeness prefers fresh status
    ]),
    [pendingOrdersFetched, hydratedOrders, pinnedOrdersById],
  );

  const todaysOrders = useMemo(() => {
    return hydratedOrders.filter((o) => {
      return (
        getOrderSaleDateKey(o) === todayKey &&
        o.status !== 'cancelled' &&
        o.status !== 'refunded' &&
        !o.isDeleted &&
        orderHasMeaningfulItems(o) &&
        !isUnpaidOnlineWebOrder(o)
      );
    });
  }, [hydratedOrders, getOrderSaleDateKey, todayKey]);

  const historyOrdersSource = useMemo(
    () => mergeOrdersByIdPreferComplete([
      ...historyOrdersFetched,
      ...Object.values(pinnedOrdersById),
      ...hydratedOrders,
    ]),
    [historyOrdersFetched, hydratedOrders, pinnedOrdersById],
  );
  
  // Determine available views - moved here before any conditional returns
  const hasActiveOrders = hydratedOrders.filter(order => 
    order.paymentStatus === 'unpaid' &&
    !isUnpaidOnlineWebOrder(order) &&
    order.status !== 'cancelled' &&
    order.status !== 'refunded' &&
    new Date(order.createdAt).toDateString() === new Date().toDateString()
  ).length > 0;
  const defaultView = (hasActiveOrders && !orderType && !placedOrder) ? 'active-orders' : 'new-order';
  const initialPosView = getStoredPosView() ?? defaultView;
  const initialActiveView = getStoredActiveView() ?? 'floor';
  const [posView, setPosView] = useState<PosViewType>(initialPosView);
  const [activeView, setActiveView] = useState<ActiveViewType>(initialActiveView);

  useEffect(() => {
    const todayStr = ymdInTimeZone(businessTimeZone);
    const yesterdayStr = addDaysToYmd(todayStr, -1);

    let startYmd: string | null = null;
    let endYmd: string | null = null;
    let fetchKey: string | null = null;

    if (historyOrdersDateFilter === 'today') {
      startYmd = todayStr;
      endYmd = todayStr;
      fetchKey = `today:${todayStr}`;
    } else if (historyOrdersDateFilter === 'yesterday') {
      startYmd = yesterdayStr;
      endYmd = yesterdayStr;
      fetchKey = `yesterday:${yesterdayStr}`;
    } else if (historyOrdersDateFilter === 'weekly') {
      startYmd = getCompletedOrdersWeekStartYmd();
      endYmd = todayStr;
      fetchKey = `weekly:${startYmd}:${endYmd}`;
    } else if (historyOrdersDateFilter === 'custom' && customDateStart && customDateEnd) {
      startYmd = customDateStart;
      endYmd = customDateEnd;
      fetchKey = `custom:${customDateStart}:${customDateEnd}`;
    } else {
      setHistoryOrdersFetched([]);
      historyOrdersFetchKeyRef.current = null;
      setHistoryOrdersFetchLoading(false);
      return;
    }

    if (fetchKey === historyOrdersFetchKeyRef.current) return;

    let cancelled = false;
    setHistoryOrdersFetchLoading(true);

    void (async () => {
      try {
        const rows = await ordersService.getByDateRange(
          ymdToStartMs(startYmd!),
          ymdToEndMs(endYmd!),
        );
        if (cancelled) return;
        setHistoryOrdersFetched(rows);
        historyOrdersFetchKeyRef.current = fetchKey;
      } catch (err) {
        console.error('Failed to load history orders for range:', err);
        if (!cancelled) {
          setHistoryOrdersFetched([]);
        }
      } finally {
        if (!cancelled) {
          setHistoryOrdersFetchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    historyOrdersDateFilter,
    customDateStart,
    customDateEnd,
    getCompletedOrdersWeekStartYmd,
    businessTimeZone,
  ]);

  useEffect(() => {
    if (posView !== 'pending' && posView !== 'floor') return;

    // Staff flip between Pending/Floor Plan and the menu constantly during service — avoid
    // hitting the server with a fresh preferServer round-trip on every single tab switch.
    // The live `orders` listener already keeps data fresh in the background; this is just an
    // accuracy top-up, so it's safe to skip when we already did one moments ago.
    if (Date.now() - pendingOrdersFetchedAtRef.current < PENDING_ORDERS_REFRESH_INTERVAL_MS) return;

    let cancelled = false;
    // Only show the blocking "Loading…" state when we have nothing to show yet; otherwise
    // keep the last-known list on screen and refresh quietly in the background.
    setPendingOrdersFetchLoading((prev) => (pendingOrdersFetched.length === 0 ? true : prev));

    const loadPendingFromServer = async () => {
      try {
        const todayStr = ymdInTimeZone(businessTimeZone);
        const serverOpts = { preferServer: true } as const;
        const [unpaid, todayRows] = await Promise.all([
          ordersService.getOpenUnpaid(serverOpts),
          ordersService.getByDateRange(ymdToStartMs(todayStr), ymdToEndMs(todayStr), serverOpts),
          onRefreshOrders?.(),
        ]);
        if (cancelled) return;
        const merged = mergeOrdersByIdPreferComplete([...unpaid, ...todayRows]);
        const hydrated = merged.map(hydrateOrder);
        for (const order of hydrated) {
          const raw = merged.find((o) => o.id === order.id);
          if (!raw) continue;
          pinRepairedOrder(raw, order);
          if (orderNeedsPersistedRepair(raw, order)) {
            void persistRepairedOrder(order);
          }
        }
        setPendingOrdersFetched(hydrated);
        pendingOrdersFetchedAtRef.current = Date.now();
      } catch (err) {
        console.error('Failed to load pending orders:', err);
        if (!cancelled) setPendingOrdersFetched([]);
      } finally {
        if (!cancelled) setPendingOrdersFetchLoading(false);
      }
    };

    void loadPendingFromServer();
    const onOnline = () => {
      pendingOrdersFetchedAtRef.current = 0; // force a real refresh once connectivity returns
      void loadPendingFromServer();
    };
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingOrdersFetched read intentionally omitted (only used for one-off loading-flag decision, not a re-fetch trigger)
  }, [posView, businessTimeZone, onRefreshOrders, hydrateOrder, persistRepairedOrder, pinRepairedOrder]);

  const printRef = useRef<HTMLDivElement>(null);

  const currentPlacedOrder = placedOrder
    ? mergeOrderSnapshots(
        placedOrder,
        hydratedOrders.find((o) => o.id === placedOrder.id),
        pendingOrdersFetched.find((o) => o.id === placedOrder.id),
      )
    : null;
  // Prefer the live order from Firestore, but fall back to the locally-held copy when a live
  // snapshot arrives without meaningful items (avoids the sidebar briefly emptying).
  // While the payment modal is open, always use `placedOrder` (stable reference). Live payment
  // growth is merged into `placedOrder` in the effect below — never swap the object on every
  // snapshot (that remounted payment tabs and made them blink / unclickable).
  const resolvedPlacedOrder: Order | null =
    isPaymentModalOpen && placedOrder
      ? placedOrder
      : mergeOrderSnapshots(placedOrder, currentPlacedOrder);
  const orderFullySettled = resolvedPlacedOrder ? isPosOrderFullySettled(resolvedPlacedOrder) : false;
  /** Block nav clears while unpaid buffet payment is open or place is in flight. */
  const buffetPaymentLocked =
    (isPaymentModalOpen || isPlacingOrder) &&
    !!placedOrder &&
    placedOrder.orderType === 'buffet' &&
    placedOrder.paymentStatus === 'unpaid';
  const showOrderSummary = Boolean(
    placedOrder ||
    orderItems.length > 0 ||
    (posView === 'new-order' && orderType)
  );

  // Persist POS view state so background refresh or full-page reload doesn't reset the screen
  useEffect(() => {
    try {
      sessionStorage.setItem(POS_VIEW_KEY, posView);
      sessionStorage.setItem(POS_ACTIVE_VIEW_KEY, activeView);
    } catch (_) {}
  }, [posView, activeView]);

  // Update placedOrder when order is updated (e.g., after payment, kitchen completion, service).
  // Compare by content signature — mergeOrderPreferComplete always returns a new object, so
  // `merged === placedOrder` never settles and would infinite-loop setState.
  const placedOrderRef = useRef(placedOrder);
  placedOrderRef.current = placedOrder;
  useEffect(() => {
    const current = placedOrderRef.current;
    if (!current) return;
    const updatedOrder = mergeOrderSnapshots(
      hydratedOrders.find((o) => o.id === current.id),
      pendingOrdersFetched.find((o) => o.id === current.id),
    );
    if (!updatedOrder) return;

    if (isPaymentModalOpen) {
      const livePay = updatedOrder.payments?.length ?? 0;
      const localPay = current.payments?.length ?? 0;
      const discountChanged = discountFieldsDiffer(current, updatedOrder);
      // Keep locally applied discounts if live snapshot is still pre-discount (write in flight).
      const adoptDiscount = discountChanged && liveDiscountIsSafeToAdopt(current, updatedOrder);
      if (livePay <= localPay && !adoptDiscount) return;
      const next = {
        ...current,
        ...(livePay > localPay
          ? {
              payments: updatedOrder.payments,
              paymentStatus: updatedOrder.paymentStatus,
              subBills: updatedOrder.subBills ?? current.subBills,
            }
          : {}),
        ...(adoptDiscount ? pickDiscountSyncFields(updatedOrder) : {}),
      };
      if (placedOrderSyncSignature(next) === placedOrderSyncSignature(current)) return;
      setPlacedOrder(next);
      return;
    }
    // Don't overwrite good local items with an empty/partial/stale live snapshot.
    const merged = mergeOrderPreferComplete(current, updatedOrder);
    if (placedOrderSyncSignature(merged) === placedOrderSyncSignature(current)) return;
    setPlacedOrder(merged);
  }, [hydratedOrders, pendingOrdersFetched, isPaymentModalOpen, placedOrder?.id]);

  // Sync orderItems when resolvedPlacedOrder updates (kitchen/service changes) - only when data actually changed
  const orderSyncKeyRef = useRef<string>('');
  useEffect(() => {
    if (!resolvedPlacedOrder || !placedOrder || resolvedPlacedOrder.id !== placedOrder.id) return;
    const liveItems = resolvedPlacedOrder.items ?? [];
    const liveKey = liveItems.map(i => `${i.lineId}|${i.quantity}|${i.quantityCompleted ?? ''}|${i.quantityServed ?? ''}|${i.status}|${i.served}|${i.removed}`).join(';');
    if (orderSyncKeyRef.current === liveKey) return;
    orderSyncKeyRef.current = liveKey;
    setOrderItems(prev => {
      const prevRemoved = prev.filter((i) => i.removed).length;
      const liveRemoved = liveItems.filter((i) => i.removed).length;
      // Local soft-deletes in progress — don't let a stale live snapshot resurrect lines.
      if (prevRemoved > liveRemoved) return prev;
      const prevQty = orderMeaningfulItemQty({ ...resolvedPlacedOrder, items: prev });
      const liveQty = orderMeaningfulItemQty({ ...resolvedPlacedOrder, items: liveItems });
      // Staff may be mid-edit (cart has new lines not saved yet) — never shrink the cart from a stale snapshot.
      if (prevQty > liveQty) return prev;
      if (prev.length !== liveItems.length) return liveItems;
      const prevKey = prev.map(p => `${p.lineId}|${p.quantity}|${p.quantityCompleted ?? ''}|${p.quantityServed ?? ''}|${p.status}|${p.served}|${p.removed}`).join(';');
      return prevKey === liveKey ? prev : liveItems;
    });
  }, [resolvedPlacedOrder?.id, resolvedPlacedOrder?.items, placedOrder?.id]);

  // Reset sync key when switching to a different order
  useEffect(() => {
    if (!placedOrder) orderSyncKeyRef.current = '';
  }, [placedOrder?.id]);

  useEffect(() => {
    if (shouldAutoOpenPaymentModal && resolvedPlacedOrder) {
      setPlacedOrder(orderReadyForPayment(resolvedPlacedOrder));
      setIsPaymentModalOpen(true);
      setShouldAutoOpenPaymentModal(false);
    }
  }, [shouldAutoOpenPaymentModal, resolvedPlacedOrder]);

  // Helper: use payment timestamp (when sale actually occurred) for “today” calculations
  const getOrderSaleTimestamp = useCallback((order: Order): number => orderSaleAtMs(order), []);

  /** Event stall ticket # (same algorithm as Mobile POS). */
  const completedEventTokenLabelById = useMemo(
    () =>
      buildEventTokenLabelById(
        hydratedOrders,
        integrationSettings?.eventStall?.tokenCounterResetAt,
        integrationSettings?.eventStall?.eventTokenRollMax,
      ),
    [hydratedOrders, integrationSettings?.eventStall?.tokenCounterResetAt, integrationSettings?.eventStall?.eventTokenRollMax],
  );

  const historyDateFilterOpts = useMemo(
    () => ({
      todayKey,
      yesterdayKey,
      weekStartYmd: getCompletedOrdersWeekStartYmd(),
      customDateStart,
      customDateEnd,
      getOrderDateKey: getOrderSaleDateKey,
    }),
    [
      todayKey,
      yesterdayKey,
      getCompletedOrdersWeekStartYmd,
      customDateStart,
      customDateEnd,
      getOrderSaleDateKey,
    ],
  );

  const cancelledTabBadgeCount = useMemo(
    () =>
      filterOrdersByHistoryDate(
        historyOrdersSource.filter(
          (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'cancelled',
        ),
        historyOrdersDateFilter,
        historyDateFilterOpts,
      ).length,
    [historyOrdersSource, historyOrdersDateFilter, historyDateFilterOpts],
  );
  const refundedTabBadgeCount = useMemo(
    () =>
      filterOrdersByHistoryDate(
        historyOrdersSource.filter(
          (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'refunded',
        ),
        historyOrdersDateFilter,
        historyDateFilterOpts,
      ).length,
    [historyOrdersSource, historyOrdersDateFilter, historyDateFilterOpts],
  );

  // Orders that belong to this register session (for close modal sales and EOD).
  // Orders that belong to this register session (for close modal sales and EOD).
  // Included if order.sessionId matches this session OR order timestamp is within session start/end.
  // Orders without sessionId are only included when in the time window; otherwise they can appear "missing" from this register.
  const sessionOrders = useMemo(() => {
    if (!registerSession) return [];
    const sessionStart = registerSession.startTime;
    const sessionEnd = registerSession.endTime ?? Date.now();
    return orders.filter((order) => {
      if (isSampleOrDemoOrderId(order.id)) return false;
      if (order.isDeleted) return false;
      if (order.sessionId === registerSession.id) return true;
      if (
        !order.sessionId &&
        orderCreatedAtMs(order) >= sessionStart &&
        orderCreatedAtMs(order) <= sessionEnd &&
        orderHasMeaningfulItems(order)
      ) {
        return true;
      }
      return false;
    });
  }, [orders, registerSession]);

  useEffect(() => {
    try {
      if(placedOrder) return;
      const savedDraft = localStorage.getItem(DRAFT_ORDER_KEY);
      if (savedDraft) {
        const { items, notes, customer, table, type, reward, deliveryCharge: savedDelivery } = JSON.parse(savedDraft);
        if (Array.isArray(items)) setOrderItems(items.map((item: OrderItem) => ({...item, status: 'pending'})));
        if (notes) setNotes(notes);
        if (customer) setSelectedCustomer(customer);
        if (table) {
          setSelectedTable(table);
          // Buffet stays on floor / covers flow — do not force à la carte menu.
          if (type === 'buffet') {
            setBuffetExtrasMenuOpen(false);
            setActiveView('floor');
            setPosView((prev) => (prev === 'pending' || prev === 'completed' ? prev : 'floor'));
          } else {
            setActiveView('menu');
          }
        }
        if (type) setOrderType(type);
        if (reward) setAppliedReward(reward);
        if (typeof savedDelivery === 'number' && !Number.isNaN(savedDelivery) && savedDelivery >= 0) {
          setDeliveryCharge(savedDelivery);
        }
      }
    } catch (error) {
      console.error("Failed to load draft order:", error);
    }
  }, [placedOrder]);

  useEffect(() => {
    if (placedOrder) return;
    const draft = {
      items: orderItems,
      notes,
      customer: selectedCustomer,
      table: selectedTable,
      type: orderType,
      reward: appliedReward,
      deliveryCharge: orderType === 'delivery' ? deliveryCharge : 0,
    };
    localStorage.setItem(DRAFT_ORDER_KEY, JSON.stringify(draft));
  }, [orderItems, notes, selectedCustomer, selectedTable, orderType, appliedReward, placedOrder, deliveryCharge]);

  useEffect(() => {
    if (orderToPrint && printRef.current) {
        printRef.current.style.display = 'block';
        // Delay so embedded content (e.g. QR code) has time to generate before print
        const printDelay = printType === 'customer' ? 350 : 100;
        const timer = setTimeout(() => {
            window.print();
            setTimeout(() => {
                if (printRef.current) {
                    printRef.current.style.display = 'none';
                }
                setOrderToPrint(null);
            }, 100);
        }, printDelay);
        return () => clearTimeout(timer);
    }
  }, [orderToPrint, printType]);

  // Mirror of the orderToPrint effect for sub-bill prints (reprint or preview before payment).
  useEffect(() => {
    if (subBillToPrint && printRef.current) {
      printRef.current.style.display = 'block';
      const timer = setTimeout(() => {
        window.print();
        setTimeout(() => {
          if (printRef.current) printRef.current.style.display = 'none';
          setSubBillToPrint(null);
        }, 100);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [subBillToPrint]);

  const clearOrder = (clearTable = true, resetOrderType = true) => {
    setOrderItems([]);
    setNotes('');
    setSelectedCustomer(null);
    if(clearTable) setSelectedTable(null);
    setSelectedServingStaff(null);
    if (resetOrderType) {
      setOrderType(null);
      setUberDoordashDetails(null);
      setOrderCustomerName(null);
      setSelectedBuffetType(null);
      setSelectedBuffetPackage(null);
      selectedBuffetPackageRef.current = null;
      selectedBuffetTypeRef.current = null;
      setBuffetNumberOfPeople(1);
      setBuffetExtrasMenuOpen(false);
    }
    setAppliedReward(null);
    setAppliedMealBooking(null);
    setTodayMealBookings([]);
    setPendingMealCollectVerify(null);
    setManualDiscount(0);
    setDiscountRequiresApproval(false);
    setDiscountApprovedBy(undefined);
    setDiscountApprovalReason(undefined);
    setDiscountApprovalTimestamp(undefined);
    setCustomerNumber(undefined);
    setCustomerName(undefined);
    setActivePartySize(null);
    setActiveBookedPartySize(null);
    setDeliveryCharge(0);
    localStorage.removeItem(DRAFT_ORDER_KEY);
    setIsPlacingOrder(false);
  };
  
  const handleNewOrder = () => {
    clearOrder();
    setPlacedOrder(null);
    setLastKitchenPrintOrder(null);
    setOrderType(null);
    setActiveView('floor');
  };

  const handleSelectOrderType = (type: OrderType) => {
    if (type === 'uber-doordash') {
      setShowUberDoordashModal(true);
      return;
    }
    if (type === 'takeaway' || type === 'delivery') {
      setPendingOrderTypeForCustomerName(type);
      setShowCustomerNameModal(true);
      return;
    }
    setPlacedOrder(null);
    setUberDoordashDetails(null);
    setOrderCustomerName(null);
    setOrderType(type);
    if (type === 'buffet') {
      setBuffetExtrasMenuOpen(false);
      setShowBuffetTypeSelector(true);
      setPosView('floor');
      setActiveView('floor');
    } else if (type === 'dine-in') {
      setBuffetExtrasMenuOpen(false);
      setPosView('floor');
      setActiveView('floor');
    } else {
      setActiveView('menu');
    }
  };

  const handleCustomerNameModalSubmit = (name: string) => {
    setOrderCustomerName(name || null);
    // Starting a fresh takeaway/delivery order — ensure any previously loaded order is cleared.
    setPlacedOrder(null);
    if (pendingOrderTypeForCustomerName) {
      setOrderType(pendingOrderTypeForCustomerName);
      if (pendingOrderTypeForCustomerName === 'delivery') {
        setDeliveryCharge(0);
      }
      setPendingOrderTypeForCustomerName(null);
    }
    setShowCustomerNameModal(false);
    setActiveView('menu');
  };

  const handleSelectBuffetPackage = (pkg: BuffetPackage) => {
    setSelectedBuffetPackage(pkg);
    setSelectedBuffetType(pkg.mealPeriod);
    selectedBuffetPackageRef.current = pkg;
    selectedBuffetTypeRef.current = pkg.mealPeriod;
    setOrderType('buffet');
    setBuffetExtrasMenuOpen(false);
    setShowBuffetTypeSelector(false);
    // Covers → payment only — never open à la carte menu here
    setPosView('floor');
    setActiveView('floor');
  };

  /** Open buffet covers modal for a table — never fall through to dine-in menu. */
  const beginBuffetCoversForTable = (
    table: Table,
    context?: { partySize?: number; customerName?: string; bookedPartySize?: number },
  ) => {
    const pkg = selectedBuffetPackageRef.current ?? selectedBuffetPackage;
    const meal = selectedBuffetTypeRef.current ?? selectedBuffetType ?? pkg?.mealPeriod ?? null;
    setSelectedTable(table);
    setSelectedServingStaff(currentUser ?? null);
    setOrderType('buffet');
    setBuffetExtrasMenuOpen(false);
    setPlacedOrder(null);
    setOrderItems([]);
    setPosView('floor');
    setActiveView('floor');
    if (context?.customerName) setCustomerName(context.customerName);
    if (context?.partySize && context.partySize > 0) {
      setBuffetNumberOfPeople(Math.max(1, Math.min(50, Math.floor(context.partySize))));
      setActiveBookedPartySize(context.bookedPartySize ?? context.partySize);
      setActivePartySize(context.partySize);
    }
    if (!pkg) {
      setShowBuffetTypeSelector(true);
      return;
    }
    if (pkg) setSelectedBuffetPackage(pkg);
    if (meal) setSelectedBuffetType(meal);
    setBuffetPlaceError(null);
    setShowBuffetPeopleModal(true);
  };

  const handleConfirmBuffetPeople = async (numberOfPeople: number) => {
    // Capture selections up front — avoid relying on state that clearOrder / floor clicks may race.
    const table = selectedTable;
    const buffetPkg = selectedBuffetPackageRef.current ?? selectedBuffetPackage;
    const buffetType =
      selectedBuffetTypeRef.current ?? selectedBuffetType ?? buffetPkg?.mealPeriod ?? null;
    if (!table || !buffetType || !buffetPkg) {
      setBuffetPlaceError('Please select a table and buffet package first.');
      return;
    }
    // Re-bind state so staging / payment UI stays consistent after async create.
    setSelectedBuffetPackage(buffetPkg);
    setSelectedBuffetType(buffetType);

    const buffetPrice = Number(buffetPkg.price);
    if (!Number.isFinite(buffetPrice) || buffetPrice <= 0) {
      setBuffetPlaceError('This buffet package has no price. Edit it in Menu Management.');
      return;
    }
    const covers = Math.max(1, Math.min(50, Math.floor(numberOfPeople) || 1));
    const totalAmount = Math.round(buffetPrice * covers * 100) / 100;
    const { subtotal, taxAmount } = totalsFromGstInclusiveItemSum(totalAmount);

    setBuffetNumberOfPeople(covers);
    setBuffetPlaceError(null);

    const buffetStartTime = Date.now();
    const buffetEndTime = buffetStartTime + (60 * 60 * 1000); // 1 hour
    const packageLineId = `buffet-pkg-${buffetPkg.id}-${buffetStartTime}`;
    const preferredOrderId = generateOrderId();
    const orderGuestName =
      (typeof customerName === 'string' && customerName.trim()) ||
      selectedCustomer?.name ||
      `${buffetPkg.name} - ${covers} ${covers === 1 ? 'person' : 'people'}`;
    const notes =
      activeBookedPartySize != null && covers !== activeBookedPartySize
        ? `${buffetPkg.name} - ${covers} ${covers === 1 ? 'person' : 'people'} (booked ${activeBookedPartySize})`
        : `${buffetPkg.name} - ${covers} ${covers === 1 ? 'person' : 'people'}`;
    setActivePartySize(covers);

    const buffetItem: OrderItem = {
      id: -Math.abs(
        Number.parseInt(buffetPkg.id.replace(/\D/g, '').slice(-8) || '1', 10) ||
          Date.now() % 1_000_000_000,
      ),
      name: buffetPkg.name,
      categoryId: 0,
      price: buffetPrice,
      imageUrl: '',
      quantity: covers,
      status: 'completed',
      quantityCompleted: covers,
      served: false,
      isBuffetPackage: true,
      buffetMealPeriod: buffetType,
      lineId: packageLineId,
    };

    const serving =
      selectedServingStaff
        ? { servingStaffId: selectedServingStaff.id, servingStaffName: selectedServingStaff.name }
        : currentUser
          ? { servingStaffId: currentUser.id, servingStaffName: currentUser.name }
          : {};

    // Client-owned id + optimistic bind so Pay opens even while Firestore write is in flight.
    const localOrder: Order = {
      id: preferredOrderId,
      items: [buffetItem],
      orderType: 'buffet',
      status: 'ready',
      paymentStatus: 'unpaid',
      payments: [],
      timestamp: buffetStartTime,
      readyAt: buffetStartTime,
      totalAmount,
      subtotal,
      taxAmount,
      tableId: Number(table.id),
      tableNumber: Number(table.id),
      tableName: table.name,
      buffetType,
      buffetPackageId: buffetPkg.id,
      buffetStartTime,
      buffetEndTime,
      customerId: selectedCustomer?.id,
      customerName: orderGuestName,
      notes,
      ...serving,
    };

    setOrderItems([buffetItem]);
    setOrderType('buffet');
    setBuffetExtrasMenuOpen(false);
    setPlacedOrder(localOrder);
    setShowBuffetPeopleModal(false);
    setPosView('new-order');
    // Keep activeView off the à la carte menu until staff explicitly Add extras.
    setActiveView('floor');
    setShouldAutoOpenPaymentModal(false);
    // Bind cart/order immediately; open PaymentModal only after durable create succeeds
    // so Card/Cash cannot fire against a document that is not written yet.
    setIsPlacingOrder(true);
    pinOrderBriefly(localOrder);
    localStorage.removeItem(DRAFT_ORDER_KEY);

    const rollbackPlace = (message: string) => {
      setIsPaymentModalOpen(false);
      setShouldAutoOpenPaymentModal(false);
      setPlacedOrder(null);
      setOrderItems([]);
      setBuffetPlaceError(message);
      setShowBuffetPeopleModal(true);
    };

    try {
      const summaryOrder = await onPlaceOrder([buffetItem], 'buffet', {
        preferredOrderId,
        tableId: Number(table.id),
        tableName: table.name,
        customerId: selectedCustomer?.id,
        customerNumber: undefined,
        customerName,
        notes,
        appliedReward: undefined,
        manualDiscount: 0,
        discountRequiresApproval: false,
        buffetType,
        buffetPackageId: buffetPkg.id,
        buffetStartTime,
        buffetEndTime,
        ...serving,
      });

      if (!summaryOrder?.id) {
        rollbackPlace('Buffet order was not created. Please try again.');
        return;
      }

      const rawItems = coerceOrderItemsArray(summaryOrder.items);
      const hasPackage = rawItems.some((i) => i.isBuffetPackage || i.lineId === packageLineId);
      const placedItems = hasPackage ? rawItems : [buffetItem, ...rawItems];
      const computedFromItems = placedItems.reduce(
        (sum, i) => sum + (i.removed ? 0 : Number(i.price || 0) * Number(i.quantity || 0)),
        0,
      );
      const durableTotal = Math.max(computedFromItems, Number(summaryOrder.totalAmount) || 0, totalAmount);
      const durableTotals = totalsFromGstInclusiveItemSum(durableTotal);

      const durableOrder: Order = {
        ...localOrder,
        ...summaryOrder,
        id: summaryOrder.id,
        items: placedItems,
        totalAmount: durableTotal,
        subtotal: durableTotals.subtotal,
        taxAmount: durableTotals.taxAmount,
        status: 'ready',
        readyAt: summaryOrder.readyAt ?? localOrder.readyAt,
        buffetPackageId: buffetPkg.id,
        buffetType,
        tableId: Number(table.id),
        tableName: table.name,
        paymentStatus: summaryOrder.paymentStatus ?? 'unpaid',
      };

      setPlacedOrder(orderReadyForPayment(durableOrder));
      setOrderItems(durableOrder.items);
      pinOrderBriefly(durableOrder);
      setIsPaymentModalOpen(true);

      void onUpdateOrder(durableOrder).catch((err) => {
        console.warn('Buffet post-place status update failed (payment still available):', err);
      });
    } catch (err) {
      console.error('Buffet place failed:', err);
      rollbackPlace(err instanceof Error ? err.message : 'Buffet order was not created. Please try again.');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleOpenBuffetPeopleModal = () => {
    if (!selectedTable) {
      alert('Please select a table for this buffet order first.');
      setPosView('floor');
      setActiveView('floor');
      return;
    }
    if (!selectedBuffetPackage) {
      alert('Please select a buffet package first.');
      setShowBuffetTypeSelector(true);
      return;
    }
    setShowBuffetPeopleModal(true);
  };

  const handleCompleteBuffetOrder = async () => {
    if (!resolvedPlacedOrder || !isActiveBuffetSession(resolvedPlacedOrder)) return;
    await handleEndBuffetSessionForOrder(resolvedPlacedOrder);
  };

  /** End buffet session (complete early or on time) for any buffet order. */
  const handleEndBuffetSessionForOrder = async (order: Order) => {
    if (!isActiveBuffetSession(order)) return;
    try {
      setIsCompletingBuffet(true);
      const now = Date.now();
      const updatedOrder: Order = {
        ...order,
        status: 'served',
        buffetEndTime: now,
        servedAt: now,
      };
      await onUpdateOrder(updatedOrder);
      if (resolvedPlacedOrder?.id === order.id) setPlacedOrder(updatedOrder);
      setSelectedHistoryOrder(null);
      alert('Buffet session ended.');
    } catch (error) {
      console.error('Failed to end buffet session:', error);
      alert('Failed to end buffet session. Please try again.');
    } finally {
      setIsCompletingBuffet(false);
    }
  };

  // Create individual orders for each person after payment (legacy multi-cover split)
  const handleCreateBuffetPersonOrders = async () => {
    if (!resolvedPlacedOrder || !selectedTable || !selectedBuffetType || !selectedBuffetPackage || !buffetNumberOfPeople) return;
    
    const summaryOrder = resolvedPlacedOrder;
    const pricePerPerson = selectedBuffetPackage.price;
    
    for (let i = 1; i <= buffetNumberOfPeople; i++) {
      const buffetStartTime = Date.now();
      const buffetEndTime = buffetStartTime + (60 * 60 * 1000);
      
      const buffetItem: OrderItem = {
        id: -Math.abs(
          Number.parseInt(selectedBuffetPackage.id.replace(/\D/g, '').slice(-8) || '1', 10) ||
            (Date.now() + i) % 1_000_000_000,
        ),
        name: selectedBuffetPackage.name,
        categoryId: 0,
        price: pricePerPerson,
        imageUrl: '',
        quantity: 1,
        status: 'completed',
        quantityCompleted: 1,
        served: false,
        isBuffetPackage: true,
        buffetMealPeriod: selectedBuffetType,
      };

      const servingStaff = selectedServingStaff ?? currentUser;
      const newOrder = await onPlaceOrder([buffetItem], 'buffet', {
        tableId: selectedTable.id,
        customerId: selectedCustomer?.id,
        customerNumber: i,
        customerName: selectedCustomer?.name ? `${selectedCustomer.name} - Person ${i}` : `Person ${i}`,
        notes: `${selectedBuffetPackage.name} - Person ${i}`,
        appliedReward: undefined,
        manualDiscount: 0,
        discountRequiresApproval: false,
        buffetType: selectedBuffetType,
        buffetPackageId: selectedBuffetPackage.id,
        buffetStartTime: buffetStartTime,
        buffetEndTime: buffetEndTime,
        ...(servingStaff && { servingStaffId: servingStaff.id, servingStaffName: servingStaff.name }),
      });
      
      if (newOrder) {
        const splitPayments: Payment[] = summaryOrder.payments.map((p) => ({
          ...p,
          amount: Math.round((p.amount / buffetNumberOfPeople) * 100) / 100,
        }));
        
        const updatedOrder: Order = {
          ...newOrder,
          totalAmount: pricePerPerson,
          status: 'ready' as OrderStatus,
          readyAt: newOrder.readyAt ?? Date.now(),
          paymentStatus: 'paid',
          payments: splitPayments,
          buffetPackageId: selectedBuffetPackage.id,
        };
        
        await onUpdateOrder(updatedOrder);
      }
    }
  };

  const handleCancelBuffetType = () => {
    setShowBuffetTypeSelector(false);
    setOrderType(null);
    setSelectedBuffetType(null);
    setSelectedBuffetPackage(null);
    selectedBuffetPackageRef.current = null;
    selectedBuffetTypeRef.current = null;
  };

  const handleSelectTableFromFloor = (tableId: number) => {
      // Don't handle table clicks in merge mode - that's handled by handleTableSelect
      if (isMergeMode) return;
      if (buffetPaymentLocked) return;
      
      const table = tables.find(t => t.id === tableId);
      if (!table) return;

      // Check if it's a merged table - if so, use the primary merged table for ordering
      let tableToUse = table;
      if (table.mergedWith && table.mergedWith.length > 1) {
        const primaryId = table.mergedWith[0];
        const isPrimaryMerged = primaryId != null && Number(primaryId) === Number(table.id);
        if (!isPrimaryMerged) {
          // If clicking a secondary merged table, find the primary one
          const primaryTable = tables.find(t => Number(t.id) === Number(primaryId));
          if (primaryTable) {
            tableToUse = primaryTable;
          } else {
            // If primary table not found, just use the clicked table
            tableToUse = table;
          }
        }
      }

      const activeOrders = pendingOrdersSource.filter((o) =>
        isFloorPlanActiveTableOrder(o, tableToUse.id, businessTimeZone) ||
        isFloorPlanActiveTableOrder(o, tableId, businessTimeZone),
      );
      const hasActiveOrders = activeOrders.length > 0;
      const seatsUsed = getTableSeatsUsed(activeOrders);
      const spareSeats = Math.max(0, tableToUse.capacity - seatsUsed);

      if (tableToUse.status === 'available' && !hasActiveOrders) {
          // Table is available - start new order (clear any previously-loaded order)
          const pkg = selectedBuffetPackageRef.current ?? selectedBuffetPackage;
          const isBuffetFlow = orderType === 'buffet' || !!pkg;
          if (isBuffetFlow) {
            // Never clear buffet package / fall through to dine-in menu
            clearOrder(true, false);
            setPlacedOrder(null);
            beginBuffetCoversForTable(tableToUse);
            return;
          }

          clearOrder(true, true);
          setPlacedOrder(null);
          setSelectedTable(tableToUse);
          setSelectedServingStaff(currentUser ?? null);
          // For regular dine-in orders
          setOrderType('dine-in');
          setCustomerNumber(1); // Default to customer 1 for new table
          setCustomerName(undefined);
          setPosView('new-order');
          setActiveView('menu');
      } else {
          if (hasActiveOrders) {
            const pkg = selectedBuffetPackageRef.current ?? selectedBuffetPackage;
            // Buffet package already chosen: seat covers on this table — never fall through to dine-in menu.
            if ((orderType === 'buffet' || !!pkg) && pkg && spareSeats > 0) {
              beginBuffetCoversForTable(tableToUse);
              return;
            }
            // Show customer selection when: multiple orders, or single order but table has spare capacity (so we can seat new party)
            if (activeOrders.length > 1 || spareSeats > 0) {
              setPendingTableSelection(tableToUse);
              setShowTableCustomerModal(true);
            } else {
              // Single order and no spare seats - load it directly
              const activeOrder = activeOrders[0];
              setPlacedOrder(activeOrder);
              setLastKitchenPrintOrder(null);
              setOrderItems(activeOrder.items);
              setNotes(activeOrder.notes || '');
              setSelectedTable(tableToUse);
              setCustomerNumber(activeOrder.customerNumber);
              setCustomerName(activeOrder.customerName);
              const customer = customers.find(c => c.id === activeOrder.customerId);
              setSelectedCustomer(customer || null);
              setAppliedReward(activeOrder.appliedReward || null);
              setOrderType(activeOrder.orderType);
              const servingStaff = activeOrder.servingStaffId != null ? staffMembers.find(s => s.id === activeOrder.servingStaffId) ?? null : null;
              setSelectedServingStaff(servingStaff);
              setPosView('new-order');
              if (activeOrder.orderType === 'buffet') {
                setBuffetExtrasMenuOpen(false);
                setActiveView('floor');
                if (activeOrder.buffetType) setSelectedBuffetType(activeOrder.buffetType);
                if (activeOrder.buffetPackageId || activeOrder.buffetType) {
                  const pkgs = getBuffetPackages(integrationSettings);
                  const pkg =
                    (activeOrder.buffetPackageId
                      ? pkgs.find((p) => p.id === activeOrder.buffetPackageId)
                      : undefined) ||
                    pkgs.find((p) => p.mealPeriod === activeOrder.buffetType);
                  if (pkg) setSelectedBuffetPackage(pkg);
                }
                if (hasBalanceDue(activeOrder)) {
                  setShouldAutoOpenPaymentModal(true);
                } else if (activeOrder.paymentStatus === 'unpaid' && onMarkAsPaid) {
                  // Payments cover the ticket but status lagged — sync so Pending/UI stay consistent
                  onMarkAsPaid(activeOrder.id);
                }
              } else {
                setActiveView('menu');
              }
            }
          } else {
             // Table marked occupied but no active orders - start new order
             const pkg = selectedBuffetPackageRef.current ?? selectedBuffetPackage;
             const isBuffetFlow = orderType === 'buffet' || !!pkg;
             if (isBuffetFlow) {
               clearOrder(true, false);
               beginBuffetCoversForTable(tableToUse);
               return;
             }

             clearOrder(true, true);
             setSelectedTable(tableToUse);
             setSelectedServingStaff(currentUser ?? null);
             // For regular dine-in orders
             setOrderType('dine-in');
             // Determine next customer number
             const existingOrders = orders.filter(o => {
               if (o.tableId !== tableId && o.tableId !== tableToUse.id) return false;
               if (o.status === 'cancelled' || o.status === 'refunded') return false;
               const orderDateKey = new Date(o.timestamp).toDateString();
               if (orderDateKey !== todayKey) return false;
               return o.customerNumber !== undefined;
             });
             const nextCustomerNumber = existingOrders.length > 0
               ? Math.max(...existingOrders.map(o => o.customerNumber!)) + 1
               : 1;
             setCustomerNumber(nextCustomerNumber);
             setCustomerName(undefined);
             setPosView('new-order');
             setActiveView('menu');
          }
      }
  };

  const openOrderDetails = useCallback((order: Order) => {
    const cached = pendingOrdersSource.find((o) => o.id === order.id) ?? hydrateOrder(order);
    setSelectedHistoryOrder(cached);
    void fetchOrderFromServer(
      order.id,
      cached,
      ordersService.getById.bind(ordersService),
      menuItems,
      persistRepairedOrder,
    ).then((fresh) => {
      setSelectedHistoryOrder(fresh);
    });
  }, [pendingOrdersSource, menuItems, persistRepairedOrder, hydrateOrder]);

  const handleSelectPendingOrder = (order: Order) => {
    const cached = pendingOrdersSource.find((o) => o.id === order.id) ?? hydrateOrder(order);
    const applyOrder = (liveOrder: Order) => {
    // Load the existing order for editing/payment
    setPlacedOrder(liveOrder);
    setLastKitchenPrintOrder(null);
    setOrderItems(liveOrder.items);
    setNotes(liveOrder.notes || '');
    setOrderType(liveOrder.orderType);
    
    // Load customer if exists
    if (liveOrder.customerId) {
      const customer = customers.find(c => c.id === liveOrder.customerId);
      setSelectedCustomer(customer || null);
    } else {
      setSelectedCustomer(null);
    }
    
    // Load table if dine-in
    if ((liveOrder.orderType === 'dine-in' || liveOrder.orderType === 'buffet') && liveOrder.tableId) {
      const table = tables.find(t => t.id === liveOrder.tableId);
      setSelectedTable(table || null);
    } else {
      setSelectedTable(null);
    }
    
    // Load serving staff if set on order
    const servingStaff = liveOrder.servingStaffId != null ? staffMembers.find(s => s.id === liveOrder.servingStaffId) ?? null : null;
    setSelectedServingStaff(servingStaff);
    
    // Load applied reward if exists
    setAppliedReward(liveOrder.appliedReward || null);
    
    // Load discount info if exists
    setManualDiscount(
      (liveOrder.couponDiscountAmount ?? 0) > 0
        ? (liveOrder.couponDiscountAmount ?? 0)
        : liveOrder.discountMethod === 'first-table'
          ? (liveOrder.firstTableDiscountAmount || 0)
          : (liveOrder.manualDiscount || 0),
    );
    setDiscountMethod(liveOrder.discountMethod);
    setFirstTableDiscountAmount(liveOrder.firstTableDiscountAmount || 0);
    setDiscountRequiresApproval(liveOrder.discountRequiresApproval || false);
    setDiscountApprovedBy(liveOrder.discountApprovedBy);
    setDiscountApprovalReason(liveOrder.discountApprovalReason);
    setDiscountApprovalTimestamp(liveOrder.discountApprovalTimestamp);
    setCouponCode(liveOrder.couponCode ?? null);
    setCouponCampaignId(liveOrder.couponCampaignId ?? null);
    setCouponDiscountType(liveOrder.couponDiscountType ?? null);
    setCouponDiscountValue(liveOrder.couponDiscountValue ?? 0);
    setCouponDiscountAmount(liveOrder.couponDiscountAmount ?? 0);
    setCouponUnitLabel(liveOrder.couponUnitLabel);
    setCouponBuildingWing(liveOrder.couponBuildingWing);
    
    // Open ordering UI — buffet stays off the à la carte menu unless adding extras
    setPosView('new-order');
    if (liveOrder.orderType === 'buffet') {
      setBuffetExtrasMenuOpen(false);
      setActiveView('floor');
      if (liveOrder.buffetType) setSelectedBuffetType(liveOrder.buffetType);
      if (liveOrder.buffetPackageId || liveOrder.buffetType) {
        const pkgs = getBuffetPackages(integrationSettings);
        const pkg =
          (liveOrder.buffetPackageId
            ? pkgs.find((p) => p.id === liveOrder.buffetPackageId)
            : undefined) ||
          pkgs.find((p) => p.mealPeriod === liveOrder.buffetType);
        if (pkg) setSelectedBuffetPackage(pkg);
      }
      if (hasBalanceDue(liveOrder)) {
        setShouldAutoOpenPaymentModal(true);
      } else if (liveOrder.paymentStatus === 'unpaid' && onMarkAsPaid) {
        onMarkAsPaid(liveOrder.id);
      }
    } else {
      setActiveView('menu');
    }
    };

    applyOrder(cached);
    void fetchOrderFromServer(
      order.id,
      cached,
      ordersService.getById.bind(ordersService),
      menuItems,
      persistRepairedOrder,
    ).then(applyOrder);
  };

  const [meatOptionModalItem, setMeatOptionModalItem] = useState<MenuItemType | null>(null);
  
  // Table merge/split/transfer state
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);
  const [selectedTableForSplit, setSelectedTableForSplit] = useState<Table | null>(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showPosReservationModal, setShowPosReservationModal] = useState(false);
  const [editingPosReservation, setEditingPosReservation] = useState<Reservation | null>(null);
  const [showMenuManagementOverlay, setShowMenuManagementOverlay] = useState(false);
  const [showCustomerManagementOverlay, setShowCustomerManagementOverlay] = useState(false);
  const [showMealPlanOverlay, setShowMealPlanOverlay] = useState(false);
  const [mealPlanRefund, setMealPlanRefund] = useState<{ order: Order; subscription: MealPlanSubscription } | null>(null);
  const [mealPlanRefresh, setMealPlanRefresh] = useState(0);
  const [tableToSplit, setTableToSplit] = useState<Table | null>(null);
  const [orderToTransfer, setOrderToTransfer] = useState<{ order: Order; table: Table } | null>(null);
  const [reservationToTransfer, setReservationToTransfer] = useState<{
    reservation: Reservation;
    table: Table;
  } | null>(null);

  /** In merge mode: single selected table that is the primary of a merged group (split target). */
  const mergedPrimarySelectedInMergeMode = useMemo(() => {
    if (!isMergeMode || selectedTableIds.length !== 1) return null;
    const id = selectedTableIds[0];
    const t = tables.find(x => Number(x.id) === Number(id));
    if (!t?.mergedWith || t.mergedWith.length < 2) return null;
    const primaryId = t.mergedWith[0];
    if (Number(primaryId) !== Number(t.id)) return null;
    return t;
  }, [isMergeMode, selectedTableIds, tables]);
  
  // Override, remake, and fire mode state
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showRemakeModal, setShowRemakeModal] = useState(false);
  const [showFireModeModal, setShowFireModeModal] = useState(false);
  const [showCancelApprovalModal, setShowCancelApprovalModal] = useState(false);
  const [pendingCancelOrder, setPendingCancelOrder] = useState<Order | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [orderToOverride, setOrderToOverride] = useState<Order | null>(null);
  const [orderToRemake, setOrderToRemake] = useState<Order | null>(null);
  const [showBuffetExtensionModal, setShowBuffetExtensionModal] = useState(false);
  const [orderToExtend, setOrderToExtend] = useState<Order | null>(null);

  const handleAddItemToOrder = (itemToAdd: MenuItemType, selectedMeatOption?: string) => {
    // Allow extras on an active buffet session even after the cover was paid
    const buffetExtrasOk =
      !!resolvedPlacedOrder &&
      isActiveBuffetSession(resolvedPlacedOrder) &&
      orderType === 'buffet' &&
      buffetExtrasMenuOpen;
    if (orderFullySettled && !buffetExtrasOk) return;
    
    // If item has meat options and no option was selected, show modal
    if (itemToAdd.meatOptions && itemToAdd.meatOptions.length > 0 && !selectedMeatOption) {
      setMeatOptionModalItem(itemToAdd);
      return;
    }
    
    // Only match non-removed lines so we can add the same item again after removing it
    const existingItem = orderItems.find(item => 
      !item.removed &&
      item.id === itemToAdd.id && 
      (!itemToAdd.meatOptions || item.selectedMeatOption === selectedMeatOption)
    );
    const quantityToAdd = existingItem ? existingItem.quantity + 1 : 1;
    
    // Check daily availability using reserved quantity calculation
    if (itemToAdd.hasDailyAvailability) {
      const availableQuantity = getAvailableQuantity(itemToAdd, orders);
      if (availableQuantity <= 0) {
        alert('This item is currently finished for today.');
        return;
      }
      if (quantityToAdd > availableQuantity) {
        alert(`Only ${availableQuantity} plate(s) available. Cannot add ${quantityToAdd} to order.`);
        return;
      }
    }
    
    // Do NOT update menu item database when adding to cart - reservation happens when order is placed
    setOrderItems(prevItems => {
      const existingItem = prevItems.find(item => 
        !item.removed &&
        item.id === itemToAdd.id && 
        (!itemToAdd.meatOptions || item.selectedMeatOption === selectedMeatOption)
      );
      if (existingItem) {
        return prevItems.map(item => {
          if (
            item.removed ||
            item.id !== itemToAdd.id ||
            (itemToAdd.meatOptions && item.selectedMeatOption !== selectedMeatOption)
          ) {
            return item;
          }
          const nextQty = item.quantity + 1;
          // Qty beyond kitchen-done must reopen the line (pink / editable); keep completed floor.
          const doneFloor = getKitchenCompletedFloor(item);
          const stillFullyDone = doneFloor > 0 && doneFloor + 0.001 >= nextQty;
          return {
            ...item,
            quantity: nextQty,
            status: stillFullyDone ? ('completed' as const) : ('pending' as const),
          };
        });
      }
      return [...prevItems, { 
        ...itemToAdd, 
        quantity: 1, 
        status: 'pending',
        selectedMeatOption: selectedMeatOption,
      }];
    });
  };

  const handleMeatOptionSelected = (item: MenuItemType, selectedOption: string) => {
    setMeatOptionModalItem(null);
    handleAddItemToOrder(item, selectedOption);
  };
  
  const handleAddComboToOrder = (combo: QuickAddItem | QuickAddCombo) => {
    // Defend against malformed/legacy quick-add records (e.g. missing `items` array from a
    // botched migration) — without this guard a bad record crashes the entire app (blank screen)
    // since there's no top-level error boundary to catch the render-phase exception.
    if (!Array.isArray(combo.items) || combo.items.length === 0) {
      console.error('Quick Add combo is missing a valid items array — refusing to add:', combo);
      alert(`"${combo.name || 'This quick add item'}" is misconfigured and can't be added. Please fix or remove it in Menu Management.`);
      return;
    }

    const buffetExtrasOk =
      !!resolvedPlacedOrder &&
      isActiveBuffetSession(resolvedPlacedOrder) &&
      orderType === 'buffet' &&
      buffetExtrasMenuOpen;
    // Only prevent adding items if order is paid - allow buffet extras while session is open
    if (orderFullySettled && !buffetExtrasOk) return;
    
    setOrderItems(prevItems => {
      const newItems = [...prevItems];
      
      // Always add individual items so they show in kitchen/service screens
      let individualItemsTotal = 0;
      
      combo.items.forEach(comboItem => {
        const menuItem = menuItems.find(mi => mi.id === comboItem.menuItemId);
        if (!menuItem) return;
        
        const existingItemIndex = newItems.findIndex(item => !item.removed && item.id === comboItem.menuItemId);
        if (existingItemIndex > -1) {
          const prev = newItems[existingItemIndex];
          const nextQty = prev.quantity + comboItem.quantity;
          const doneFloor = getKitchenCompletedFloor(prev);
          const stillFullyDone = doneFloor > 0 && doneFloor + 0.001 >= nextQty;
          newItems[existingItemIndex] = {
            ...prev,
            quantity: nextQty,
            status: stillFullyDone ? ('completed' as const) : ('pending' as const),
          };
          individualItemsTotal += menuItem.price * comboItem.quantity;
        } else {
          newItems.push({ ...menuItem, quantity: comboItem.quantity, status: 'pending' });
          individualItemsTotal += menuItem.price * comboItem.quantity;
        }
      });
      
      // If combo has a special price, add a discount item to adjust the total
      const comboPrice = (combo as QuickAddCombo).price;
      const numericPrice = typeof comboPrice === 'string' ? parseFloat(comboPrice) : comboPrice;
      const hasComboPrice = numericPrice !== undefined && numericPrice !== null && !isNaN(numericPrice) && numericPrice > 0;
      
      if (hasComboPrice && numericPrice < individualItemsTotal) {
        // Calculate discount amount
        const discountAmount = individualItemsTotal - numericPrice;
        
        // Add a discount item (negative price to reduce total)
        // Note: Discount items are automatically marked as completed when all preparable items are done
        const discountItem: OrderItem = {
          id: `combo-discount-${combo.id}-${Date.now()}`,
          name: `${combo.name} - Combo Discount`,
          price: -discountAmount, // Negative price for discount
          categoryId: 0,
          imageUrl: '',
          quantity: 1,
          status: 'pending', // Status will be auto-updated when all preparable items are completed
        };
        newItems.push(discountItem);
      }
      
      return newItems;
    });
  };

  const handleUpdateQuantity = (itemId: number, newQuantity: number) => {
    const idx = orderItems.findIndex(i => i.id === itemId);
    if (idx < 0) return;
    const item = orderItems[idx];
    // Buffet package covers are fixed after place
    if (item.isBuffetPackage && orderType === 'buffet' && placedOrder) return;
    if (newQuantity <= 0) {
      handleRemoveItem(idx);
      return;
    }
    const minCompleted = getKitchenCompletedFloor(item);
    if (minCompleted > 0 && newQuantity < minCompleted) return; // Can't reduce below kitchen-completed
    setOrderItems(prevItems =>
      prevItems.map((i, iidx) => {
        if (iidx !== idx) return i;
        const stillFullyDone = minCompleted > 0 && minCompleted + 0.001 >= newQuantity;
        return {
          ...i,
          quantity: newQuantity,
          status: stillFullyDone ? ('completed' as const) : ('pending' as const),
        };
      })
    );
  };

  const handleRemoveItem = (index: number) => {
    const item = orderItems[index];
    if (item?.isBuffetPackage && orderType === 'buffet' && placedOrder) return;
    setOrderItems(prevItems => prevItems.map((it, i) => i === index ? { ...it, removed: true } : it));
  };

  const handleUpdateItemNote = (itemId: number, note: string) => {
    setOrderItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, notes: note || undefined } : item
      )
    );
  };
  
  const handleSelectCustomer = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setIsCustomerModalOpen(false);
    setAppliedMealBooking(null);
    setTodayMealBookings([]);
    if (customer?.id && integrationSettings?.businessInfo?.mealPlansEnabled === true) {
      void weeklyBookingsService
        .getForCustomerDay(customer.id, nzDateKey())
        .then((rows) => setTodayMealBookings(rows.filter((b) => b.status === 'booked')))
        .catch(() => setTodayMealBookings([]));
    }
  };
  
  const handleSelectReward = (reward: LoyaltyReward) => {
      setAppliedReward(reward);
      setIsRewardModalOpen(false);
  };

  const applyMealPlanCollect = useCallback(
    async (booking: WeeklyBooking) => {
      setAppliedMealBooking(booking);
      setPendingMealCollectVerify(null);
      try {
        const week = await mealPlanWeeksService.getById(booking.weekId);
        const day = week?.menu?.[booking.dayKey];
        const lines = messItemsForPeriod(day, booking.period as MealPeriod);
        if (lines.length === 0) {
          alert('No meal is written for this period. Add it under Settings → Meal plans.');
          return;
        }
        const newItems: OrderItem[] = lines.map((line, i) => ({
          id: Date.now() + i,
          name: line.name,
          price: 0,
          quantity: 1,
          categoryId: 'meal-plan',
          imageUrl: '',
          notes: line.notes,
          lineId: `mp-${booking.id}-${i}`,
          status: 'pending',
        }));
        setOrderItems((prev) => [...prev, ...newItems]);
      } catch (e) {
        console.error(e);
        alert('Could not load the meal for this booking.');
      }
    },
    [],
  );

  const handleCollectMealPlan = useCallback(
    (booking: WeeklyBooking) => {
      if (selectedCustomer && !selectedCustomer.studentVerified) {
        setPendingMealCollectVerify(booking);
        return;
      }
      void applyMealPlanCollect(booking);
    },
    [selectedCustomer, applyMealPlanCollect],
  );

  const handleMarkStudentVerifiedAndCollect = useCallback(async () => {
    const booking = pendingMealCollectVerify;
    if (!booking || !selectedCustomer) return;
    try {
      const now = Date.now();
      const staffLabel = currentUser?.name || 'Staff';
      const updated = await customersService.update(selectedCustomer.id, {
        studentVerified: true,
        studentVerifiedAt: now,
        studentVerifiedBy: staffLabel,
      });
      setSelectedCustomer(updated);
      await applyMealPlanCollect(booking);
    } catch (e) {
      console.error(e);
      alert('Could not save student ID verification. You can still continue Collect.');
    }
  }, [pendingMealCollectVerify, selectedCustomer, currentUser, applyMealPlanCollect]);

  /** Overlay cart discount UI state onto an order so Pay / totals match the sidebar. */
  const orderWithLocalDiscounts = (
    base: Order,
    overrides?: {
      manualDiscount?: number;
      discountMethod?: 'manual' | 'first-table' | undefined;
      firstTableDiscountAmount?: number;
      discountRequiresApproval?: boolean;
      discountApprovedBy?: number;
      discountApprovalReason?: string;
      discountApprovalTimestamp?: number;
      couponCode?: string | null;
      couponCampaignId?: string | null;
      couponDiscountType?: 'percentage' | 'fixed' | null;
      couponDiscountValue?: number;
      couponDiscountAmount?: number;
      couponUnitLabel?: string;
      couponBuildingWing?: 'hotel_wing' | 'tower_wing';
      clearDiscounts?: boolean;
    },
  ): Order => {
    const clear = overrides?.clearDiscounts === true;
    const nextManual = clear ? 0 : (overrides?.manualDiscount ?? manualDiscount);
    const nextMethod = clear
      ? undefined
      : (overrides?.discountMethod !== undefined ? overrides.discountMethod : discountMethod);
    const nextFirst = clear
      ? 0
      : (overrides?.firstTableDiscountAmount ?? firstTableDiscountAmount ?? (nextMethod === 'first-table' ? nextManual : 0));
    const nextCouponAmt = clear ? 0 : (overrides?.couponDiscountAmount ?? couponDiscountAmount);
    const hasCoupon = nextCouponAmt > 0.005;
    // Coupon lives on couponDiscountAmount only — avoid double-count with manualDiscount.
    const orderManual = hasCoupon ? 0 : nextMethod === 'manual' ? nextManual : 0;
    const items = orderItems.length > 0 ? orderItems : base.items;
    const reward = clear ? undefined : (appliedReward || undefined);
    const residentPct =
      !clear && selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : 0;
    const totalBefore = items.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
    const rewardVal = reward?.discountValue ?? 0;
    const discountForResidentBase =
      hasCoupon ? nextCouponAmt : nextMethod === 'first-table' ? nextFirst : nextManual;
    const residentDiscountAmount =
      residentPct > 0
        ? ((totalBefore - rewardVal - discountForResidentBase) * residentPct) / 100
        : 0;
    const draft: Order = {
      ...base,
      items,
      appliedReward: reward,
      discountMethod: nextMethod,
      manualDiscount: orderManual,
      firstTableDiscountAmount: nextMethod === 'first-table' ? nextFirst : 0,
      discountRequiresApproval: clear ? false : (overrides?.discountRequiresApproval ?? discountRequiresApproval),
      discountApprovedBy: clear ? undefined : (overrides?.discountApprovedBy ?? discountApprovedBy),
      discountApprovalReason: clear ? undefined : (overrides?.discountApprovalReason ?? discountApprovalReason),
      discountApprovalTimestamp: clear ? undefined : (overrides?.discountApprovalTimestamp ?? discountApprovalTimestamp),
      couponCode: clear ? undefined : (overrides?.couponCode !== undefined ? overrides.couponCode ?? undefined : couponCode ?? undefined),
      couponCampaignId: clear
        ? undefined
        : (overrides?.couponCampaignId !== undefined ? overrides.couponCampaignId ?? undefined : couponCampaignId ?? undefined),
      couponDiscountType: clear
        ? undefined
        : (overrides?.couponDiscountType !== undefined ? overrides.couponDiscountType ?? undefined : couponDiscountType ?? undefined),
      couponDiscountValue: clear
        ? undefined
        : (overrides?.couponDiscountValue !== undefined ? overrides.couponDiscountValue : couponDiscountValue || undefined),
      couponDiscountAmount: hasCoupon ? nextCouponAmt : undefined,
      couponUnitLabel: clear
        ? undefined
        : (overrides?.couponUnitLabel !== undefined ? overrides.couponUnitLabel : couponUnitLabel),
      couponBuildingWing: clear
        ? undefined
        : (overrides?.couponBuildingWing !== undefined ? overrides.couponBuildingWing : couponBuildingWing),
      residentDiscountPercent: residentPct > 0 ? residentPct : undefined,
      residentDiscountAmount: residentDiscountAmount > 0 ? residentDiscountAmount : undefined,
      deliveryCharge: orderType === 'delivery' ? deliveryCharge : base.deliveryCharge,
    };
    const totals = getOrderTotals(draft);
    const discountAmount =
      rewardVal +
      orderManual +
      (nextMethod === 'first-table' ? nextFirst : 0) +
      (hasCoupon ? nextCouponAmt : 0) +
      residentDiscountAmount;
    return { ...draft, ...totals, discountAmount };
  };

  /**
   * Prepare an order for PaymentModal.
   * If the order already carries discount fields (Firestore / place payload), recompute totals from those.
   * Otherwise overlay the open-cart discount UI so Pay does not freeze a pre-discount total.
   * Do not blindly overlay local UI onto a different history order (would wipe persisted discounts).
   */
  const orderReadyForPayment = (base: Order): Order => {
    if (orderHasAppliedDiscount(base)) {
      const totals = getOrderTotals(base);
      const discountAmount =
        (base.appliedReward?.discountValue ?? 0) +
        (base.discountMethod === 'first-table'
          ? (base.firstTableDiscountAmount ?? 0)
          : (base.manualDiscount ?? 0)) +
        (base.couponDiscountAmount ?? 0) +
        (base.residentDiscountAmount ?? 0);
      return { ...base, ...totals, discountAmount };
    }
    return orderWithLocalDiscounts(base);
  };

  const persistOrderDiscounts = async (updated: Order) => {
    setPlacedOrder(updated);
    await onUpdateOrder(updated);
  };

  const handleApplyDiscount = async (discount: number, reason: string | undefined, method: 'manual' | 'first-table') => {
    const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
    const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
    const subtotal = totalBeforeDiscount - rewardDiscount;
    const discountPercentage = subtotal > 0 ? (discount / subtotal) * 100 : 0;
    
    // Manager approval threshold applies only to manual discounts, not First Table.
    if (method === 'manual' && discountPercentage > 20) {
      setPendingDiscount(discount);
      setPendingDiscountReason(reason);
      setPendingDiscountMethod(method);
      setIsApprovalModalOpen(true);
    } else {
      setManualDiscount(discount);
      setDiscountMethod(method);
      setFirstTableDiscountAmount(method === 'first-table' ? discount : 0);
      setDiscountRequiresApproval(false);
      setDiscountApprovalReason(reason);
      setCouponDiscountAmount(0);
      setCouponCode(null);

      // Write onto placedOrder immediately — PaymentModal freezes a copy without local UI state.
      if (placedOrder) {
        await persistOrderDiscounts(
          orderWithLocalDiscounts(placedOrder, {
            manualDiscount: discount,
            discountMethod: method,
            firstTableDiscountAmount: method === 'first-table' ? discount : 0,
            discountRequiresApproval: false,
            discountApprovalReason: reason,
            couponDiscountAmount: 0,
            couponCode: null,
          }),
        );
      }
    }
  };

  const handleApproveDiscount = async (staffId: number, reason: string) => {
    const finalReason = reason || pendingDiscountReason;
    const approvedAt = Date.now();
    setManualDiscount(pendingDiscount);
    setDiscountMethod(pendingDiscountMethod);
    setFirstTableDiscountAmount(pendingDiscountMethod === 'first-table' ? pendingDiscount : 0);
    setDiscountRequiresApproval(true);
    setDiscountApprovedBy(staffId);
    setDiscountApprovalReason(finalReason);
    setDiscountApprovalTimestamp(approvedAt);
    setCouponDiscountAmount(0);
    setCouponCode(null);

    if (placedOrder) {
      await persistOrderDiscounts(
        orderWithLocalDiscounts(placedOrder, {
          manualDiscount: pendingDiscount,
          discountMethod: pendingDiscountMethod,
          firstTableDiscountAmount: pendingDiscountMethod === 'first-table' ? pendingDiscount : 0,
          discountRequiresApproval: true,
          discountApprovedBy: staffId,
          discountApprovalReason: finalReason,
          discountApprovalTimestamp: approvedAt,
          couponDiscountAmount: 0,
          couponCode: null,
        }),
      );
    }
    
    setPendingDiscount(0);
    setPendingDiscountReason(undefined);
    setPendingDiscountMethod('manual');
  };

  const handleRemoveDiscount = async () => {
    setManualDiscount(0);
    setDiscountMethod(undefined);
    setFirstTableDiscountAmount(0);
    setDiscountRequiresApproval(false);
    setDiscountApprovedBy(undefined);
    setDiscountApprovalReason(undefined);
    setDiscountApprovalTimestamp(undefined);
    setCouponCode(null);
    setCouponCampaignId(null);
    setCouponDiscountType(null);
    setCouponDiscountValue(0);
    setCouponDiscountAmount(0);
    setCouponUnitLabel(undefined);
    setCouponBuildingWing(undefined);
    setCouponError(null);
    if (placedOrder) {
      await persistOrderDiscounts(orderWithLocalDiscounts(placedOrder, { clearDiscounts: true }));
    }
  };

  const handleApplyCoupon = async (code: string) => {
    const normalizedCode = code.trim().toUpperCase().replace(/^CPN-/, '').replace(/^UNIT-/, '');
    try {
      if (discountMethod === 'first-table') {
        throw new Error('Remove First Table discount before applying a code.');
      }
      const coupon = await couponsService.getByCode(normalizedCode);
      const now = Date.now();
      if (!coupon) throw new Error('Coupon not found');
      const campaign = coupon.campaignId ? await couponCampaignsService.getById(coupon.campaignId) : null;
      if (campaign && campaign.status && campaign.status !== 'published') {
        throw new Error(`Campaign is ${campaign.status}. Coupon not available in POS yet`);
      }
      const validation = await couponsService.isValidForRedemption(coupon, now);
      if (!validation.ok) throw new Error(validation.reason || 'Coupon is not valid');
      if (coupon.appliesTo !== 'all') {
        const eligible = orderItems.some((item) => !item.removed && (coupon.appliesTo as number[]).includes(Number(item.id)));
        if (!eligible) throw new Error('Coupon does not apply to these items');
      }
      const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
      const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
      const base = Math.max(0, totalBeforeDiscount - rewardDiscount);
      const discount = coupon.discountType === 'percentage'
        ? Math.min(base, (base * coupon.discountValue) / 100)
        : Math.min(base, coupon.discountValue);

      setManualDiscount(discount);
      setDiscountMethod('manual');
      setCouponCode(coupon.code);
      setCouponCampaignId(coupon.campaignId);
      setCouponDiscountType(coupon.discountType);
      setCouponDiscountValue(coupon.discountValue);
      setCouponDiscountAmount(discount);
      setCouponUnitLabel(coupon.unitLabel ?? undefined);
      setCouponBuildingWing(
        coupon.buildingWing ?? campaign?.buildingWing ?? undefined
      );
      setCouponError(null);
      if (placedOrder) {
        await persistOrderDiscounts(
          orderWithLocalDiscounts(placedOrder, {
            manualDiscount: 0,
            discountMethod: 'manual',
            couponCode: coupon.code,
            couponCampaignId: coupon.campaignId,
            couponDiscountType: coupon.discountType,
            couponDiscountValue: coupon.discountValue,
            couponDiscountAmount: discount,
            couponUnitLabel: coupon.unitLabel ?? undefined,
            couponBuildingWing: coupon.buildingWing ?? campaign?.buildingWing ?? undefined,
          }),
        );
      }
      await couponUsageLogsService.create({
        couponCode: coupon.code,
        couponId: coupon.id,
        campaignId: coupon.campaignId,
        staffId: currentUser?.id,
        attemptedAt: now,
        result: 'applied',
      });
      await couponRedemptionsService.create({
        couponCode: coupon.code,
        couponId: coupon.id,
        campaignId: coupon.campaignId,
        staffId: currentUser?.id,
        redeemedAt: now,
        status: 'applied',
      });
      await auditLogsService.add({
        timestamp: now,
        actorId: currentUser?.id,
        actorName: currentUser?.name,
        actorRole: currentUser?.role,
        action: 'coupon.applied',
        entityType: 'coupon',
        entityId: coupon.id,
        details: {
          code: coupon.code,
          campaignId: coupon.campaignId,
          discountAmount: discount,
          unitLabel: coupon.unitLabel,
          buildingWing: coupon.buildingWing ?? campaign?.buildingWing,
        },
      });
    } catch (error) {
      const message = (error as Error)?.message || 'Coupon validation failed';
      setCouponError(message);
      try {
        await couponUsageLogsService.create({
          couponCode: normalizedCode,
          staffId: currentUser?.id,
          attemptedAt: Date.now(),
          result: 'rejected',
          reason: message,
        });
      } catch {
        /* best-effort audit; avoid uncaught if rules/network fail */
      }
      try {
        await couponRedemptionsService.create({
          couponCode: normalizedCode,
          couponId: undefined,
          campaignId: undefined,
          staffId: currentUser?.id,
          redeemedAt: Date.now(),
          status: 'rejected',
          reason: message,
        });
      } catch {
        /* best-effort */
      }
      try {
        await auditLogsService.add({
          timestamp: Date.now(),
          actorId: currentUser?.id,
          actorName: currentUser?.name,
          actorRole: currentUser?.role,
          action: 'coupon.rejected',
          entityType: 'coupon',
          details: { code: normalizedCode, reason: message },
        });
      } catch {
        /* best-effort */
      }
    }
  };

  const finalizeCouponRedemption = async (orderId: string) => {
    if (!couponCode) return;
    const coupon = await couponsService.getByCode(couponCode);
    if (!coupon) return;
    await couponsService.markUsed({
      couponId: coupon.id,
      orderId,
      staffId: currentUser?.id,
      discountAmount: couponDiscountAmount,
    });
    await couponUsageLogsService.create({
      couponCode: coupon.code,
      couponId: coupon.id,
      campaignId: coupon.campaignId,
      orderId,
      staffId: currentUser?.id,
      attemptedAt: Date.now(),
      result: 'redeemed',
    });
    await couponRedemptionsService.create({
      couponCode: coupon.code,
      couponId: coupon.id,
      campaignId: coupon.campaignId,
      orderId,
      staffId: currentUser?.id,
      redeemedAt: Date.now(),
      status: 'redeemed',
    });
  };

  const handleSendToKitchen = () => {
    if (!orderType) {
        alert("Please select an order type first.");
        return;
    }
    // For buffet orders, items are not required (buffet is all-inclusive)
    if (orderType !== 'buffet' && orderItems.length === 0) {
        return;
    }
    if ((orderType === 'dine-in' || orderType === 'buffet') && !selectedTable) {
        alert(`Please select a table for the ${orderType === 'buffet' ? 'buffet' : 'dine-in'} order.`);
        return;
    }
    if (orderType === 'uber-doordash' && orderItems.length === 0) {
        alert('Please add at least one item to the order.');
        return;
    }
    if (orderType === 'buffet' && !selectedBuffetType) {
        alert("Please select a buffet type (Breakfast, Lunch, or Dinner).");
        return;
    }
    // Show customer confirmation display instead of directly placing the order
    setIsConfirmationDisplayOpen(true);
  };

  const handleConfirmOrder = async () => {
    if (isPlacingOrder) return;
    if (!orderType) return;
    // For buffet, items are optional (all-inclusive), but for others require items
    if (orderType !== 'buffet' && orderItems.length === 0) return;
    // Uber/DoorDash: place using stored platform + customer name (collected when they clicked Uber/DoorDash)
    if (orderType === 'uber-doordash') {
      if (!uberDoordashDetails) {
        alert('Uber/DoorDash details are missing. Please select Uber/DoorDash again and enter platform and customer name.');
        return;
      }
      setIsPlacingOrder(true);
      try {
        const newOrder = await onPlaceOrder(orderItems, 'uber-doordash', {
          notes,
          manualDiscount: couponDiscountAmount > 0 ? undefined : (manualDiscount > 0 ? manualDiscount : undefined),
          discountRequiresApproval: discountRequiresApproval || undefined,
          discountApprovedBy: discountApprovedBy,
          discountApprovalReason: discountApprovalReason,
          discountApprovalTimestamp: discountApprovalTimestamp,
          couponCode: couponCode ?? undefined,
          couponCampaignId: couponCampaignId ?? undefined,
          couponDiscountType: couponDiscountType ?? undefined,
          couponDiscountValue: couponDiscountValue || undefined,
          couponDiscountAmount: couponDiscountAmount || undefined,
          couponAppliedByStaffId: currentUser?.id,
          couponAppliedAt: couponCode ? Date.now() : undefined,
          couponUnitLabel: couponCode ? couponUnitLabel : undefined,
          couponBuildingWing: couponCode ? couponBuildingWing : undefined,
          residentDiscountPercent: selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : undefined,
          residentDiscountAmount: selectedCustomer?.isResident ? (((orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0) - (appliedReward?.discountValue ?? 0) - (orderMealPlanDiscount || 0) - (manualDiscount || 0)) * (selectedCustomer.discountPercentage ?? 0)) / 100) : undefined,
          mealPlanBookingId: appliedMealBooking?.id,
          mealPlanWeekId: appliedMealBooking?.weekId,
          mealPlanPeriod: appliedMealBooking?.period,
          mealPlanEntitlementId: undefined,
          mealPlanDiscountAmount: undefined,
          deliveryInfo: { platform: uberDoordashDetails.platform, customerName: uberDoordashDetails.customerName.trim() || 'Customer' },
        });
        if (newOrder) {
          await finalizeCouponRedemption(newOrder.id);
          setPlacedOrder(newOrder);
          pinOrderBriefly(newOrder);
          localStorage.removeItem(DRAFT_ORDER_KEY);
          setIsConfirmationDisplayOpen(false);
          setUberDoordashDetails(null);
          clearOrder(true, true);
          setPlacedOrder(null);
          setPosView('pending');
          setActiveView('menu');
        }
      } finally {
        setIsPlacingOrder(false);
      }
      return;
    }
    setIsPlacingOrder(true);
    try {
      // For takeaway/delivery/buffet, we need to process payment immediately
      if (orderType === 'takeaway' || orderType === 'delivery' || orderType === 'buffet') {
        // For buffet, calculate start and end times
        let buffetStartTime: number | undefined;
        let buffetEndTime: number | undefined;
        if (orderType === 'buffet' && selectedBuffetType) {
          buffetStartTime = Date.now();
          buffetEndTime = buffetStartTime + (60 * 60 * 1000); // 1 hour in milliseconds
        }
        
        const servingStaff = selectedServingStaff ?? currentUser;
        // First place the order
        const newOrder = await onPlaceOrder(orderItems, orderType, { 
            tableId: orderType === 'buffet' ? selectedTable?.id : undefined,
            customerId: selectedCustomer?.id,
            customerNumber: orderType === 'buffet' ? customerNumber : undefined,
            customerName: orderType === 'buffet' ? customerName : (orderCustomerName || selectedCustomer?.name),
            deliveryCharge: orderType === 'delivery' ? Math.max(0, deliveryCharge) : undefined,
            notes,
            appliedReward: appliedReward || undefined,
            manualDiscount: couponDiscountAmount > 0 ? undefined : (discountMethod === 'manual' && manualDiscount > 0 ? manualDiscount : undefined),
            discountMethod,
            firstTableDiscountAmount: discountMethod === 'first-table' && manualDiscount > 0 ? manualDiscount : undefined,
            discountRequiresApproval: discountRequiresApproval || undefined,
            discountApprovedBy: discountApprovedBy,
            discountApprovalReason: discountApprovalReason,
            discountApprovalTimestamp: discountApprovalTimestamp,
            couponCode: couponCode ?? undefined,
            couponCampaignId: couponCampaignId ?? undefined,
            couponDiscountType: couponDiscountType ?? undefined,
            couponDiscountValue: couponDiscountValue || undefined,
            couponDiscountAmount: couponDiscountAmount || undefined,
            couponAppliedByStaffId: currentUser?.id,
            couponAppliedAt: couponCode ? Date.now() : undefined,
          couponUnitLabel: couponCode ? couponUnitLabel : undefined,
          couponBuildingWing: couponCode ? couponBuildingWing : undefined,
            residentDiscountPercent: selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : undefined,
            residentDiscountAmount: selectedCustomer?.isResident ? (((orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0) - (appliedReward?.discountValue ?? 0) - (orderMealPlanDiscount || 0) - (manualDiscount || 0)) * (selectedCustomer.discountPercentage ?? 0)) / 100) : undefined,
            mealPlanBookingId: appliedMealBooking?.id,
            mealPlanWeekId: appliedMealBooking?.weekId,
            mealPlanPeriod: appliedMealBooking?.period,
            mealPlanEntitlementId: undefined,
            mealPlanDiscountAmount: undefined,
            buffetType: orderType === 'buffet' ? selectedBuffetType : undefined,
            buffetPackageId: orderType === 'buffet' ? selectedBuffetPackage?.id : undefined,
            buffetStartTime: buffetStartTime,
            buffetEndTime: buffetEndTime,
            ...(servingStaff && (orderType === 'dine-in' || orderType === 'buffet') ? { servingStaffId: servingStaff.id, servingStaffName: servingStaff.name } : {}),
        });
        
        if (newOrder) {
            await finalizeCouponRedemption(newOrder.id);
            setPlacedOrder(newOrder);
            pinOrderBriefly(newOrder);
            localStorage.removeItem(DRAFT_ORDER_KEY);
            setIsConfirmationDisplayOpen(false);
            // Open payment modal immediately for takeaway/delivery
            setPlacedOrder(orderReadyForPayment(newOrder));
            setIsPaymentModalOpen(true);
        }
      } else {
        // For dine-in, just place the order (payment happens later)
        const servingStaff = selectedServingStaff ?? currentUser;
        const newOrder = await onPlaceOrder(orderItems, orderType, { 
            tableId: selectedTable?.id, 
            customerId: selectedCustomer?.id,
            customerNumber: customerNumber,
            customerName: customerName,
            notes,
            appliedReward: appliedReward || undefined,
            manualDiscount: couponDiscountAmount > 0 ? undefined : (discountMethod === 'manual' && manualDiscount > 0 ? manualDiscount : undefined),
            discountMethod,
            firstTableDiscountAmount: discountMethod === 'first-table' && manualDiscount > 0 ? manualDiscount : undefined,
            discountRequiresApproval: discountRequiresApproval || undefined,
            discountApprovedBy: discountApprovedBy,
            discountApprovalReason: discountApprovalReason,
            discountApprovalTimestamp: discountApprovalTimestamp,
            couponCode: couponCode ?? undefined,
            couponCampaignId: couponCampaignId ?? undefined,
            couponDiscountType: couponDiscountType ?? undefined,
            couponDiscountValue: couponDiscountValue || undefined,
            couponDiscountAmount: couponDiscountAmount || undefined,
            couponAppliedByStaffId: currentUser?.id,
            couponAppliedAt: couponCode ? Date.now() : undefined,
          couponUnitLabel: couponCode ? couponUnitLabel : undefined,
          couponBuildingWing: couponCode ? couponBuildingWing : undefined,
            residentDiscountPercent: selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : undefined,
            residentDiscountAmount: selectedCustomer?.isResident ? (((orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0) - (appliedReward?.discountValue ?? 0) - (orderMealPlanDiscount || 0) - (manualDiscount || 0)) * (selectedCustomer.discountPercentage ?? 0)) / 100) : undefined,
            mealPlanBookingId: appliedMealBooking?.id,
            mealPlanWeekId: appliedMealBooking?.weekId,
            mealPlanPeriod: appliedMealBooking?.period,
            mealPlanEntitlementId: undefined,
            mealPlanDiscountAmount: undefined,
            ...(servingStaff && { servingStaffId: servingStaff.id, servingStaffName: servingStaff.name }),
        });
        
        if (newOrder) {
            await finalizeCouponRedemption(newOrder.id);
            setPlacedOrder(newOrder);
            pinOrderBriefly(newOrder);
            localStorage.removeItem(DRAFT_ORDER_KEY);
            setIsConfirmationDisplayOpen(false);
            // Clear cart but keep table selected and stay on floor so order stays visible
            clearOrder(false, false); // don't clear table, don't reset order type (keeps dine-in)
            setPlacedOrder(null);
            setPosView('floor');
            setActiveView('floor');
            // Keep orderType as 'dine-in' so floor plan remains visible
        }
      }
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleCancelConfirmation = () => {
    setIsConfirmationDisplayOpen(false);
  };

  const handleMarkUberDoordashPickedUp = (order: Order) => {
    patchPendingOrderFields(order.id, { status: 'served', servedAt: Date.now() });
    if (onUpdateOrderStatus) {
      onUpdateOrderStatus(order.id, 'served');
      return;
    }
    const { totalAmount } = getOrderTotals(order);
    const payments = order.payments?.length
      ? order.payments
      : [{ method: 'card' as const, amount: totalAmount, timestamp: Date.now() }];
    onUpdateOrder({
      ...order,
      status: 'served',
      paymentStatus: 'paid',
      payments,
    });
  };

  const handleSendUberReady = useCallback(async (order: Order) => {
    const uberOrderId = order.deliveryInfo?.orderId;
    const now = Date.now();
    const readyPatch = {
      status: 'ready' as OrderStatus,
      readyAt: now,
      deliveryInfo: {
        ...order.deliveryInfo,
        uberReadySentAt: now,
      },
    };
    patchPendingOrderFields(order.id, readyPatch);

    // Manual Uber orders created in POS won't have an upstream Uber order ID yet.
    // Allow those to progress locally while preserving API sync for real Uber orders.
    if (!uberOrderId) {
      onUpdateOrder({
        ...order,
        ...readyPatch,
      });
      return;
    }
    try {
      await markUberEatsOrderReady(uberOrderId);
      onUpdateOrder({
        ...order,
        ...readyPatch,
      });
    } catch (err) {
      console.error('markUberEatsOrderReady failed:', err);
      alert((err as any)?.message || 'Failed to mark order as ready in Uber.');
    }
  }, [onUpdateOrder, patchPendingOrderFields]);

  const handleUberDoordashModalSubmit = (platform: 'Uber Eats' | 'DoorDash', customerName: string) => {
    setUberDoordashDetails({ platform, customerName: customerName.trim() || 'Customer' });
    setOrderType('uber-doordash');
    setShowUberDoordashModal(false);
    setActiveView('menu');
  };
  
  const handleUpdateOrder = async () => {
    if (!placedOrder) return;
    
    // For takeaway/delivery orders, don't allow updates - they should create new orders
    // since payment is processed upfront (or for Uber/DoorDash, payment is when picked up)
    if (placedOrder.orderType === 'takeaway' || placedOrder.orderType === 'delivery' || placedOrder.orderType === 'uber-doordash') {
      alert('Cannot update takeaway, delivery, or Uber/DoorDash orders. Please create a new order.');
      return;
    }
    
    // Allow buffet extras updates after cover payment (balance due for add-ons)
    if (placedOrder.paymentStatus === 'paid' && placedOrder.orderType !== 'buffet') {
      alert('Cannot update paid orders.');
      return;
    }
    
    // Ensure new items have proper status and served flag; match existing items by lineId when available.
    // Never keep sticky status=completed after qty grew past kitchen-done — that locks the sidebar green.
    const processedItems = orderItems.map(item => {
      let existingItem = null;
      if (item.lineId) {
        existingItem = placedOrder.items.find(oi => oi.lineId === item.lineId);
      }
      if (!existingItem) {
        existingItem = placedOrder.items.find(oi =>
          oi.id === item.id &&
          oi.selectedMeatOption === item.selectedMeatOption
        );
      }
      if (!existingItem) {
        // New extras on buffet → kitchen; package lines stay completed
        if (item.isBuffetPackage) {
          return {
            ...item,
            status: 'completed' as const,
            quantityCompleted: item.quantityCompleted ?? item.quantity,
            served: item.served ?? false,
          };
        }
        return {
          ...item,
          status: 'pending' as const,
          served: false,
          quantityCompleted: undefined,
          quantityCompletedByBatch: undefined,
        };
      }
      const quantityCompleted = existingItem.quantityCompleted;
      const quantityCompletedByBatch = existingItem.quantityCompletedByBatch;
      const withKitchenProgress = {
        ...item,
        served: existingItem.served,
        quantityCompleted,
        quantityCompletedByBatch,
      };
      return {
        ...withKitchenProgress,
        status: isLineFullyKitchenDone(
          { ...withKitchenProgress, status: existingItem.status },
          { priorQuantity: existingItem.quantity },
        )
          ? ('completed' as const)
          : ('pending' as const),
      };
    });
    
    const orderWithItems = { ...placedOrder, items: processedItems, appliedReward: appliedReward || undefined };
    const { subtotal, taxAmount, totalAmount } = getOrderTotals(orderWithItems);
    const amountPaid = getAmountPaid(orderWithItems);
    const paymentStatus: Order['paymentStatus'] =
      amountPaid + 0.001 >= totalAmount ? 'paid' : 'unpaid';
    const hasExtras = processedItems.some((i) => !i.removed && !i.isBuffetPackage && i.price >= 0);
    const updatedOrder: Order = {
        ...placedOrder,
        items: processedItems,
        notes: notes || undefined,
        customerId: selectedCustomer?.id,
        customerNumber: customerNumber,
        customerName: customerName,
        appliedReward: appliedReward || undefined,
        ...(selectedServingStaff && (placedOrder.orderType === 'dine-in' || placedOrder.orderType === 'buffet') ? { servingStaffId: selectedServingStaff.id, servingStaffName: selectedServingStaff.name } : {}),
        subtotal,
        taxAmount,
        totalAmount,
        paymentStatus,
        // Extras reopen kitchen work; package-only stays ready
        status: hasExtras && processedItems.some((i) => !i.isBuffetPackage && i.status === 'pending')
          ? 'preparing'
          : placedOrder.status === 'buffet'
            ? 'ready'
            : placedOrder.status,
        hideFromKitchen: false,
    };
    
    // Baseline = last SAVED cart only. Never lastSentItemSnapshot — that lags and
    // re-prints items from earlier Updates on the next kitchen ticket.
    const kitchenPrintBaseline = snapshotKitchenLines(placedOrder);

    setPlacedOrder(updatedOrder);
    setOrderItems(processedItems);

    // Buffet add-ons only: open pay when covers were already settled and extras leave a balance.
    // Dine-in / running tickets must stay on Update → kitchen; staff use Pay Now when ready.
    const buffetExtrasNeedPay =
      updatedOrder.orderType === 'buffet' && hasExtras && hasBalanceDue(updatedOrder);
    // Lock payment modal open before await so live snapshots cannot clobber the just-updated items
    // (which previously could flash a $0 “Payment Successful” screen and clear the order).
    if (buffetExtrasNeedPay) {
      setPlacedOrder(orderReadyForPayment(updatedOrder));
      setIsPaymentModalOpen(true);
    }

    try {
      const saved = await onUpdateOrder(updatedOrder);
      const authoritative =
        saved && typeof saved === 'object' && Array.isArray((saved as Order).items)
          ? (saved as Order)
          : updatedOrder;
      // Frozen ticket for Print Order → Kitchen only. Do NOT put print baseline on
      // placedOrder — after a qty cut that baseline is the pre-cut cart, and leaving it
      // on placedOrder made the next same-line add print an empty delta (KDS still OK).
      const kitchenTicket = buildKitchenPrintOrder(authoritative, kitchenPrintBaseline);
      setPlacedOrder(authoritative);
      setOrderItems(authoritative.items ?? processedItems);
      setLastKitchenPrintOrder(kitchenTicket);
      pinOrderBriefly(authoritative);

      if (buffetExtrasNeedPay) {
        alert('Extras sent to kitchen. Collect payment for the add-ons.');
        return;
      }
      if (hasExtras && updatedOrder.orderType === 'buffet') {
        alert('Extras sent to kitchen.');
        // Stay in extras menu so staff can add more; they can tap Done when finished.
        setBuffetExtrasMenuOpen(true);
        setPosView('new-order');
        setActiveView('menu');
        return;
      }
      alert('Order updated and sent to kitchen.');
    } catch (err) {
      console.error('Failed to update order:', err);
      if (buffetExtrasNeedPay) setIsPaymentModalOpen(false);
      alert('Failed to update order. Please try again.');
    }
  };

  // Check if manager approval is required for cancellation
  const requiresManagerApproval = (order: Order): boolean => {
    // If order is 'new' or 'queued' (not started cooking), no approval needed
    if (order.status === 'new' || order.status === 'queued') {
      return false;
    }
    
    // If order has started preparing or is already served, approval is required
    if (order.status === 'preparing' || order.status === 'ready' || order.status === 'served') {
      return true;
    }
    
    // If customer doesn't have money after having food, approval is required
    // This is a special case that will be passed via cancellationReason
    // For now, if order is paid but we're cancelling, it might be this case
    if (order.paymentStatus === 'paid' && order.status !== 'new' && order.status !== 'queued') {
      return true;
    }
    
    return false;
  };

  const handleCancelOrder = async (orderOrEvent?: Order | MouseEvent<HTMLButtonElement>, reason?: string) => {
    // Handle case where this is called from a button click (event object) or with an order
    let orderToCancel: Order | undefined;
    
    // Check if first argument is an Order object (has 'id' property and is not an event)
    // Events have 'nativeEvent', 'target', 'currentTarget' properties
    // Orders have 'id', 'items', 'status' properties
    if (orderOrEvent && typeof orderOrEvent === 'object') {
      if ('id' in orderOrEvent && 'items' in orderOrEvent && !('nativeEvent' in orderOrEvent)) {
        // It's an Order object
        orderToCancel = orderOrEvent as Order;
      }
      // If it's an event (has nativeEvent or target), ignore it and use resolvedPlacedOrder
    }
    
    // If we don't have an order yet, use resolvedPlacedOrder
    if (!orderToCancel) {
      orderToCancel = resolvedPlacedOrder || null;
    }
    
    // If we still don't have an order, try placedOrder as last resort
    if (!orderToCancel && placedOrder) {
      orderToCancel = placedOrder;
    }
    
    // If we have an order with ID, try to get the full order from the orders array
    if (orderToCancel && orderToCancel.id) {
      const fullOrder = orders.find(o => o.id === orderToCancel!.id);
      if (fullOrder) {
        orderToCancel = fullOrder;
      }
    }
    
    if (!orderToCancel) {
      console.error('❌ Cannot cancel: No order found', {
        hasPlacedOrder: !!placedOrder,
        placedOrderId: placedOrder?.id,
        hasResolvedPlacedOrder: !!resolvedPlacedOrder,
        resolvedPlacedOrderId: resolvedPlacedOrder?.id,
        ordersCount: orders.length,
        orderIds: orders.map(o => o.id).slice(0, 5)
      });
      alert('Cannot cancel order: Order not found. Please try again.');
      return;
    }
    
    // Ensure we have a valid order with ID
    if (!orderToCancel.id) {
      console.error('❌ Cannot cancel: Order missing ID', {
        order: orderToCancel,
        hasId: !!orderToCancel.id,
        keys: Object.keys(orderToCancel),
        placedOrderId: placedOrder?.id,
        resolvedPlacedOrderId: resolvedPlacedOrder?.id
      });
      alert('Cannot cancel order: Order ID is missing. Please try again.');
      return;
    }
    
    console.log('🔄 Attempting to cancel order:', {
      orderId: orderToCancel.id,
      status: orderToCancel.status,
      paymentStatus: orderToCancel.paymentStatus,
      itemsCount: orderToCancel.items?.length || 0
    });
    
    const needsApproval = requiresManagerApproval(orderToCancel);
    
    // If approval is needed, show the approval modal (even for managers - they need to enter PIN and reason)
    if (needsApproval) {
      setPendingCancelOrder(orderToCancel);
      setCancelReason(reason || '');
      setShowCancelApprovalModal(true);
      return;
    }
    
    // If no approval needed, proceed with cancellation
    await processCancellation(orderToCancel, reason || 'Order cancelled');
  };

  const processCancellation = async (order: Order, reason: string, approvedBy?: number) => {
    try {
      // Validate order has required properties
      if (!order || !order.id) {
        throw new Error('Invalid order: order ID is missing');
      }
      
      // Ensure items array exists
      if (!order.items || !Array.isArray(order.items)) {
        console.warn('⚠️ Order missing items array, using empty array:', order.id);
        order.items = [];
      }
      
      // Determine if this is a "customer doesn't have money" scenario
      const isNoMoneyScenario = reason.toLowerCase().includes("doesn't have money") || 
                                reason.toLowerCase().includes("no money") ||
                                reason.toLowerCase().includes("can't pay") ||
                                reason.toLowerCase().includes("cannot pay") ||
                                reason.toLowerCase().includes("unable to pay");
      
      // Update order status
      const updates: Partial<Order> = {
        status: 'cancelled',
        // If customer doesn't have money after having food, mark as unpaid
        // If order was already paid and cancelled for other reasons, keep as paid (refund handled separately if needed)
        paymentStatus: isNoMoneyScenario ? 'unpaid' : order.paymentStatus,
      };
      
      // Store cancellation details
      (updates as any).cancelledAt = Date.now();
      (updates as any).cancellationReason = reason;
      if (approvedBy) {
        (updates as any).cancelledBy = approvedBy;
      }
      
      // Ensure items array is preserved when cancelling
      const orderToUpdate: Order = {
        ...order,
        ...updates,
        // Explicitly preserve items array to prevent undefined errors
        items: order.items || [],
        // Ensure other required fields are present
        payments: order.payments || [],
        subtotal: order.subtotal || 0,
        taxAmount: order.taxAmount || 0,
        totalAmount: order.totalAmount || 0,
      };
      
      console.log('🔄 Cancelling order:', {
        orderId: orderToUpdate.id,
        status: orderToUpdate.status,
        paymentStatus: orderToUpdate.paymentStatus,
        orderType: orderToUpdate.orderType,
        tableId: orderToUpdate.tableId,
        itemsCount: orderToUpdate.items.length,
        reason: reason
      });
      
      await onUpdateOrder(orderToUpdate);
      
      console.log('✅ Order cancellation update sent:', {
        orderId: orderToUpdate.id,
        status: orderToUpdate.status,
        tableId: orderToUpdate.tableId
      });

      // Note: Table release will be handled in App.tsx handleUpdateOrder
      // when it detects the order status is 'cancelled'
      // This ensures the table is released after the order update completes

      // If this was the placed order, clear it
      if (placedOrder && placedOrder.id === order.id) {
        clearOrder();
        setPlacedOrder(null);
        setPosView('cancelled'); // Switch to cancelled tab
        setOrderType(null);
      }
      
      alert("Order cancelled successfully!");
    } catch (error) {
      console.error('Failed to cancel order:', error);
      alert('Failed to cancel order. Please try again.');
    }
  };

  const handleCancelApproval = (staffId: number, reason: string) => {
    if (pendingCancelOrder) {
      processCancellation(pendingCancelOrder, reason, staffId);
      setPendingCancelOrder(null);
      setCancelReason('');
      setShowCancelApprovalModal(false);
    }
  };

  const handleReleaseTable = async () => {
    if (!selectedTable || orderItems.length > 0) return;
    
    if (!confirm(`Release table ${selectedTable.name}?`)) {
      return;
    }

    const tableId = selectedTable.id;

    // Open dine-in covers on this table (identity via shared matcher; includes empty tabs)
    const openDineInOrders = orders.filter(
      (o) =>
        o.orderType === 'dine-in' &&
        orderMatchesFloorPlanTable(o, tableId) &&
        !o.isDeleted &&
        o.paymentStatus === 'unpaid' &&
        o.status !== 'cancelled' &&
        o.status !== 'refunded' &&
        o.status !== 'comped',
    );

    const withItems = openDineInOrders.filter((o) => orderHasMeaningfulItems(o));
    const emptyTabs = openDineInOrders.filter((o) => !orderHasMeaningfulItems(o));

    if (withItems.length > 0) {
      alert(`Cannot release table. There are ${withItems.length} unpaid dine-in order(s) with items for this table.`);
      return;
    }

    if (emptyTabs.length > 0 && onRemoveOrder) {
      try {
        await Promise.all(emptyTabs.map((o) => onRemoveOrder(o.id)));
      } catch (error) {
        console.error('Failed to cancel orders:', error);
      }
    }

    // Mark buffet sessions as completed when releasing table
    const buffetSessions = orders.filter(
      (o) => o.orderType === 'buffet' && isFloorPlanActiveTableOrder(o, tableId),
    );

    if (buffetSessions.length > 0) {
      try {
        await Promise.all(buffetSessions.map(order => onUpdateOrder?.({
          ...order,
          status: 'served' as OrderStatus
        })));
      } catch (error) {
        console.error('Failed to finalize buffet sessions:', error);
      }
    }

    // Release table - clear selection (table.status freed by remove/sync when no siblings)
    setSelectedTable(null);
    clearOrder();
    alert(`Table ${selectedTable.name} released successfully!`);
  };

  const handleSelectTableCustomer = (customerNum: number, customerNameStr?: string, existingOrder?: Order) => {
    if (!pendingTableSelection) return;
    
    if (existingOrder) {
      // Load existing order
      setPlacedOrder(existingOrder);
      setLastKitchenPrintOrder(null);
      setOrderItems(existingOrder.items);
      setNotes(existingOrder.notes || '');
      setSelectedTable(pendingTableSelection);
      setCustomerNumber(existingOrder.customerNumber);
      setCustomerName(existingOrder.customerName);
      const customer = customers.find(c => c.id === existingOrder.customerId);
      setSelectedCustomer(customer || null);
      setAppliedReward(existingOrder.appliedReward || null);
      setOrderType(existingOrder.orderType);
      setPosView('new-order');
      if (existingOrder.orderType === 'buffet') {
        setBuffetExtrasMenuOpen(false);
        setActiveView('floor');
        if (existingOrder.buffetType) setSelectedBuffetType(existingOrder.buffetType);
        if (existingOrder.buffetPackageId || existingOrder.buffetType) {
          const pkgs = getBuffetPackages(integrationSettings);
          const pkg =
            (existingOrder.buffetPackageId
              ? pkgs.find((p) => p.id === existingOrder.buffetPackageId)
              : undefined) ||
            pkgs.find((p) => p.mealPeriod === existingOrder.buffetType);
          if (pkg) setSelectedBuffetPackage(pkg);
        }
        if (hasBalanceDue(existingOrder)) {
          setShouldAutoOpenPaymentModal(true);
        } else if (existingOrder.paymentStatus === 'unpaid' && onMarkAsPaid) {
          onMarkAsPaid(existingOrder.id);
        }
      } else {
        setActiveView('menu');
      }
    } else {
      // Start new order for this customer (seat new party at table with spare capacity)
      const wasBuffet = orderType === 'buffet' || !!selectedBuffetPackage;
      clearOrder(true, !wasBuffet); // preserve orderType/selectedBuffetPackage when adding buffet party
      setSelectedTable(pendingTableSelection);
      setSelectedServingStaff(currentUser ?? null);
      setCustomerNumber(customerNum);
      setCustomerName(customerNameStr);
      if (wasBuffet) {
        // Buffet flow: stay on floor, open covers modal (menu only via Add extras after paid session)
        beginBuffetCoversForTable(pendingTableSelection);
      } else {
        setOrderType('dine-in');
        setPosView('new-order');
        setActiveView('menu');
      }
    }
    
    setPendingTableSelection(null);
    setShowTableCustomerModal(false);
  };

  const handleSeatNewDineInParty = (covers: number) => {
    if (!pendingDineInTable) return;

    // Safety net: buffet must never fall through to à la carte after "Seat new party".
    const pkg = selectedBuffetPackageRef.current ?? selectedBuffetPackage;
    if (orderType === 'buffet' || pkg) {
      const table = pendingDineInTable;
      const booked = pendingDineInBookedPartySize;
      const guestName = pendingDineInCustomerName;
      setPendingDineInTable(null);
      setPendingDineInSpareSeats(0);
      setPendingDineInCustomerName(undefined);
      setPendingDineInBookedPartySize(null);
      setShowDineInCoversModal(false);
      setAdjustingReservationCovers(false);
      beginBuffetCoversForTable(table, {
        partySize: covers,
        customerName: guestName,
        bookedPartySize: booked ?? undefined,
      });
      return;
    }

    const todayKey = new Date().toDateString();
    const existingOrders = orders.filter(o => {
      if (o.tableId !== pendingDineInTable.id) return false;
      if (o.status === 'cancelled' || o.status === 'refunded') return false;
      const orderDateKey = new Date(o.timestamp).toDateString();
      if (orderDateKey !== todayKey) return false;
      return o.customerNumber !== undefined;
    });
    const nextCustomerNumber = existingOrders.length > 0
      ? Math.max(...existingOrders.map(o => o.customerNumber!)) + 1
      : 1;

    const booked = pendingDineInBookedPartySize;
    const guestName = pendingDineInCustomerName;
    const safeCovers = Math.max(1, Math.floor(covers) || 1);

    if (!adjustingReservationCovers) {
      clearOrder();
      setSelectedTable(pendingDineInTable);
      setSelectedServingStaff(currentUser ?? null);
      setOrderType('dine-in');
      setCustomerNumber(nextCustomerNumber);
      setPosView('new-order');
      setActiveView('menu');
    }

    setCustomerName(guestName);
    setNotes(
      booked != null && safeCovers !== booked
        ? `Party of ${safeCovers} (booked ${booked})`
        : `Party of ${safeCovers}`,
    );
    setActivePartySize(safeCovers);
    setActiveBookedPartySize(booked);

    setPendingDineInTable(null);
    setPendingDineInSpareSeats(0);
    setPendingDineInCustomerName(undefined);
    setPendingDineInBookedPartySize(null);
    setShowDineInCoversModal(false);
    setAdjustingReservationCovers(false);
  };

  /** Seat dine-in from a reservation without asking covers again. */
  const seatDineInFromReservation = (
    table: Table,
    partySize: number,
    customerName?: string,
  ) => {
    const covers = Math.max(1, Math.floor(partySize) || 1);
    const todayKey = new Date().toDateString();
    const existingOrders = orders.filter(o => {
      if (Number(o.tableId) !== Number(table.id)) return false;
      if (o.status === 'cancelled' || o.status === 'refunded') return false;
      const orderDateKey = new Date(o.timestamp).toDateString();
      if (orderDateKey !== todayKey) return false;
      return o.customerNumber !== undefined;
    });
    const nextCustomerNumber = existingOrders.length > 0
      ? Math.max(...existingOrders.map(o => o.customerNumber!)) + 1
      : 1;

    clearOrder();
    setSelectedTable(table);
    setSelectedServingStaff(currentUser ?? null);
    setOrderType('dine-in');
    setCustomerNumber(nextCustomerNumber);
    setCustomerName(customerName);
    setNotes(`Party of ${covers}`);
    setActivePartySize(covers);
    setActiveBookedPartySize(covers);
    setPosView('new-order');
    setActiveView('menu');
  };

  // Table merge/split/transfer handlers
  const handleToggleMergeMode = () => {
    setIsMergeMode(!isMergeMode);
    setSelectedTableIds([]);
    setSelectedTableForSplit(null);
  };

  const handleTableSelect = (tableId: number) => {
    if (!isMergeMode) {
      // If not in merge mode, check if this is a merged table for splitting
      const table = tables.find(t => Number(t.id) === Number(tableId));
      if (table && table.mergedWith && table.mergedWith.length > 1) {
        const primaryTableId = table.mergedWith[0];
        const isPrimaryMerged = Number(primaryTableId) === Number(table.id);
        if (isPrimaryMerged) {
          setSelectedTableForSplit(table);
        } else {
          // If clicking a secondary merged table, find the primary one
          const primaryTable = tables.find(t => Number(t.id) === Number(primaryTableId));
          if (primaryTable) {
            setSelectedTableForSplit(primaryTable);
          } else {
            setSelectedTableForSplit(null);
          }
        }
      } else {
        setSelectedTableForSplit(null);
      }
      return;
    }
    setSelectedTableIds(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  const handleMergeClick = () => {
    if (selectedTableIds.length < 2) {
      alert('Please select at least 2 tables to merge.');
      return;
    }
    setShowMergeModal(true);
  };

  const handleConfirmMerge = async () => {
    if (onMergeTables && selectedTableIds.length >= 2) {
      await onMergeTables(selectedTableIds);
      setSelectedTableIds([]);
      setIsMergeMode(false);
      setShowMergeModal(false);
    }
  };

  const handleSplitTable = (tableId: number) => {
    const table = tables.find(t => Number(t.id) === Number(tableId));
    if (table && table.mergedWith && table.mergedWith.length > 1) {
      setTableToSplit(table);
      setShowSplitModal(true);
    }
  };

  const handleConfirmSplit = async () => {
    if (onSplitTables && tableToSplit) {
      await onSplitTables(tableToSplit.id);
      setTableToSplit(null);
      setSelectedTableForSplit(null);
      setSelectedTableIds([]);
      setShowSplitModal(false);
    }
  };

  const handleInitiateTransfer = (order: Order) => {
    const table = tables.find(t => t.id === order.tableId);
    if (table) {
      setOrderToTransfer({ order, table });
      setShowTransferModal(true);
    }
  };

  const handleConfirmTransfer = async (targetTableId: number) => {
    if (onTransferOrder && orderToTransfer) {
      await onTransferOrder(orderToTransfer.order.id, targetTableId);
      setOrderToTransfer(null);
      setShowTransferModal(false);
    }
  };

  const handleInitiateReservationTransfer = (reservation: Reservation, sourceTable?: Table) => {
    const table =
      sourceTable ??
      tables.find((t) => Number(t.id) === Number(reservation.tableId));
    if (!table) {
      alert('Reservation has no assigned table.');
      return;
    }
    setReservationToTransfer({ reservation, table });
  };

  const handleConfirmReservationTransfer = async (targetTableId: number) => {
    if (onTransferReservation && reservationToTransfer) {
      await onTransferReservation(
        reservationToTransfer.reservation.id,
        targetTableId,
        reservationToTransfer.table.id,
      );
      setReservationToTransfer(null);
    }
  };

  // Override order handler
  const handleInitiateOverride = (order: Order) => {
    setOrderToOverride(order);
    setShowOverrideModal(true);
  };

  const handleConfirmOverride = async (reason: string) => {
    if (onOverrideOrder && orderToOverride) {
      await onOverrideOrder(orderToOverride.id, reason);
      setOrderToOverride(null);
      setShowOverrideModal(false);
      setSelectedHistoryOrder(null);
    }
  };

  // Remake order handler
  const handleInitiateRemake = (order: Order) => {
    setOrderToRemake(order);
    setShowRemakeModal(true);
  };

  const handleConfirmRemake = async (items: OrderItem[], reason: string) => {
    if (onRemakeOrder && orderToRemake) {
      await onRemakeOrder(orderToRemake.id, items, reason);
      setOrderToRemake(null);
      setShowRemakeModal(false);
      // Refresh order view
      if (orderToRemake.id === placedOrder?.id) {
        const updatedOrder = orders.find(o => o.id === orderToRemake.id);
        if (updatedOrder) {
          setPlacedOrder(updatedOrder);
          setOrderItems(updatedOrder.items);
        }
      }
    }
  };

  // Buffet extension handlers
  const handleInitiateBuffetExtension = (order: Order) => {
    setOrderToExtend(order);
    setShowBuffetExtensionModal(true);
  };

  const handleConfirmBuffetExtension = async (extensionMinutes: number, reason?: string) => {
    if (onExtendBuffetTime && orderToExtend) {
      await onExtendBuffetTime(orderToExtend.id, extensionMinutes, reason);
      setOrderToExtend(null);
      setShowBuffetExtensionModal(false);
      // Refresh order view
      if (orderToExtend.id === placedOrder?.id) {
        const updatedOrder = orders.find(o => o.id === orderToExtend.id);
        if (updatedOrder) {
          setPlacedOrder(updatedOrder);
          setOrderItems(updatedOrder.items);
        }
      }
    }
  };

  // Fire mode handlers
  const handleToggleFireMode = () => {
    setShowFireModeModal(true);
  };

  const handleConfirmFireModeActivate = async () => {
    if (onActivateFireMode) {
      await onActivateFireMode();
      setShowFireModeModal(false);
    }
  };

  const handleConfirmFireModeDeactivate = async () => {
    if (onDeactivateFireMode) {
      await onDeactivateFireMode();
      setShowFireModeModal(false);
    }
  };

  const handleInitiatePayment = () => {
    const base = placedOrder || resolvedPlacedOrder;
    if (!base) return;
    // Ensure Pay freezes the discounted total, not a stale pre-discount placedOrder.
    setPlacedOrder(orderWithLocalDiscounts(base));
    setIsPaymentModalOpen(true);
  };
  
  const handleClosePaymentModal = async () => {
      setIsPaymentModalOpen(false);
      setShouldAutoOpenPaymentModal(false);
      // Only tear down the cart after a real full settlement. Unpaid / mid-meal closes must
      // leave the running order on screen (Update → kitchen workflow).
      if (!orderFullySettled) return;

      // Buffet: keep the paid session loaded so staff can Add extras → KDS without re-finding the table.
      if (orderType === 'buffet' || resolvedPlacedOrder?.orderType === 'buffet') {
        setBuffetExtrasMenuOpen(false);
        setPosView('new-order');
        setActiveView('floor');
        setOrderType('buffet');
        return;
      }

      // Dine-in still on a table: keep the paid ticket loaded on the floor so Close ≠ wipe.
      // Staff start a fresh ticket via New Order when they are done.
      if (
        (resolvedPlacedOrder?.orderType === 'dine-in' || orderType === 'dine-in') &&
        (resolvedPlacedOrder?.tableId != null || selectedTable != null)
      ) {
        setPosView('new-order');
        setActiveView('floor');
        return;
      }

      setPlacedOrder(null);
      clearOrder();
      setPosView('new-order');
      setActiveView('menu');
      setOrderType(null);
      setSelectedTable(null);
      setSelectedCustomer(null);
      setSelectedBuffetType(null);
      setSelectedBuffetPackage(null);
      setBuffetNumberOfPeople(1);
  }

  const handlePrintReceipt = (order: Order) => {
    setPrintPendingOrder(order);
  };

  /**
   * Customer receipt must use the open cart (sidebar / placedOrder), not a stale live
   * snapshot and not lastKitchenPrintOrder (kitchen delta ticket only).
   * Never pick "more complete" by higher qty — that resurrected pre-cut totals after a cut.
   */
  const buildCustomerReceiptOrder = (base: Order): Order => {
    const id = base.id;
    let best: Order = base;

    if (placedOrder && placedOrder.id === id) {
      const livePay = resolvedPlacedOrder?.id === id ? resolvedPlacedOrder : null;
      const payments =
        (livePay?.payments?.length ?? 0) > (placedOrder.payments?.length ?? 0)
          ? livePay!.payments
          : placedOrder.payments;
      best = {
        ...placedOrder,
        payments: payments ?? placedOrder.payments,
        paymentStatus: livePay?.paymentStatus ?? placedOrder.paymentStatus,
        subBills: livePay?.subBills ?? placedOrder.subBills,
      };
    } else if (resolvedPlacedOrder && resolvedPlacedOrder.id === id) {
      best = resolvedPlacedOrder;
    }

    if (placedOrder && placedOrder.id === id && orderItems.length > 0) {
      best = { ...best, items: orderItems };
    }

    const totals = getOrderTotals(best);
    return {
      ...best,
      subtotal: totals.subtotal,
      taxAmount: totals.taxAmount,
      totalAmount: totals.totalAmount,
    };
  };

  const handlePrintTypeChosen = (type: 'customer' | 'kitchen') => {
    if (!printPendingOrder) return;
    // After Update, use the frozen ticket (baseline = pre-update cart) so Kitchen
    // only shows this send — not earlier lines and not a stale full-cart snapshot.
    let orderForPrint: Order =
      type === 'kitchen' &&
      lastKitchenPrintOrder &&
      lastKitchenPrintOrder.id === printPendingOrder.id
        ? lastKitchenPrintOrder
        : printPendingOrder;
    if (type === 'customer') {
      orderForPrint = buildCustomerReceiptOrder(orderForPrint);
    }
    onAuditPrint?.(type, orderForPrint);
    setOrderToPrint(orderForPrint);
    setPrintType(type);
    setPrintPendingOrder(null);
  };
  
  const handleFinalNewOrder = () => {
    setIsPaymentModalOpen(false);
    setPlacedOrder(null);
    setLastKitchenPrintOrder(null);
    clearOrder();
    setPosView('new-order');
    setOrderType(null);
    setSelectedTable(null);
    setSelectedCustomer(null);
  };

  // Filter menu items by category and channel availability
  const displayedItems = selectedCategoryId != null && selectedCategoryId !== ''
    ? orderTypeFilteredMenuItems.filter(
        (item) => String(item.categoryId) === String(selectedCategoryId)
      )
    : [];

  // Apply menu search: when query is set, search across all categories by name/description
  const searchTrimmed = menuSearchQuery.trim().toLowerCase();
  const displayedItemsFiltered = searchTrimmed
    ? orderTypeFilteredMenuItems.filter((item) => {
        const nameMatch = item.name?.toLowerCase().includes(searchTrimmed);
        const descMatch = item.description?.toLowerCase().includes(searchTrimmed);
        return nameMatch || descMatch;
      })
    : displayedItems;
  
  // Comprehensive menu debug logging (opt-in in dev via localStorage POS_MENU_DEBUG=1)
  useEffect(() => {
    if (orderType && activeView === 'menu' && selectedCategoryId) {
      const debugEnabled = import.meta.env.DEV && window.localStorage.getItem('POS_MENU_DEBUG') === '1';
      // Get all unique categoryIds from menu items
      const uniqueCategoryIds = [...new Set(menuItems.map(item => {
        const id = typeof item.categoryId === 'string' ? parseInt(item.categoryId, 10) : item.categoryId;
        return id;
      }))].sort((a, b) => a - b);
      
      // Get all category IDs
      const categoryIds = categories.map(c => {
        const id = typeof c.id === 'string' ? parseInt(c.id, 10) : c.id;
        return id;
      }).sort((a, b) => a - b);
      
      // Check for matches
      const matchingItems = menuItems.filter(item => {
        const itemCategoryId = typeof item.categoryId === 'string' ? parseInt(item.categoryId, 10) : item.categoryId;
        const selectedId = typeof selectedCategoryId === 'string' ? parseInt(selectedCategoryId, 10) : selectedCategoryId;
        return itemCategoryId === selectedId;
      });
      
      if (debugEnabled) {
        console.log('🔍 DETAILED MENU DEBUG:', {
          selectedCategoryId,
          selectedCategoryIdType: typeof selectedCategoryId,
          categoriesCount: categories.length,
          categoryIds: categoryIds,
          menuItemsCount: menuItems.length,
          uniqueCategoryIdsInMenuItems: uniqueCategoryIds,
          displayedItemsCount: displayedItems.length,
          matchingItemsCount: matchingItems.length,
          sampleCategories: categories.slice(0, 3).map(c => ({ id: c.id, name: c.name, idType: typeof c.id })),
          sampleMenuItems: menuItems.slice(0, 5).map(item => ({
            name: item.name,
            categoryId: item.categoryId,
            categoryIdType: typeof item.categoryId
          })),
          itemsForSelectedCategory: matchingItems.slice(0, 3).map(item => ({
            name: item.name,
            categoryId: item.categoryId
          }))
        });
      }
      
      // Check for mismatches
      const mismatches = uniqueCategoryIds.filter(catId => Number.isFinite(catId) && catId > 0 && !categoryIds.includes(catId));
      if (mismatches.length > 0) {
        console.warn('⚠️ WARNING: Menu items reference category IDs that don\'t exist:', mismatches);
      }
      
      if (displayedItems.length === 0 && menuItems.length > 0) {
        console.error('❌ NO ITEMS DISPLAYED!', {
          reason: 'Filter returned 0 items',
          selectedCategoryId,
          availableCategoryIds: categoryIds,
          menuItemCategoryIds: uniqueCategoryIds,
          possibleIssue: mismatches.length > 0 ? 'Category ID mismatch' : 'ID type mismatch or no items for this category'
        });
      }
    }
  }, [categories, menuItems, selectedCategoryId, displayedItems.length, orderType, activeView]);
  // Keep Order Details in sync with live KDS/Service updates while the modal is open.
  // selectedHistoryOrder was previously frozen at open time (one-shot fetch only).
  useEffect(() => {
    if (!selectedHistoryOrder) return;
    const live =
      pendingOrdersSource.find((o) => o.id === selectedHistoryOrder.id) ??
      hydratedOrders.find((o) => o.id === selectedHistoryOrder.id) ??
      orders.find((o) => o.id === selectedHistoryOrder.id);
    if (!live) return;
    const merged = mergeOrderPreferComplete(selectedHistoryOrder, live);
    if (placedOrderSyncSignature(merged) === placedOrderSyncSignature(selectedHistoryOrder)) return;
    setSelectedHistoryOrder(merged);
  }, [selectedHistoryOrder, pendingOrdersSource, hydratedOrders, orders]);

  const historyOrderCustomer = selectedHistoryOrder
    ? customers.find((c) => c.id === selectedHistoryOrder.customerId)
    : null;


  // Calculate order totals (exclude removed items)
  const calculateOrderTotals = () => {
    const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
    const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
    const mealPlanDiscount = 0;
    const residentDiscountPercent = selectedCustomer?.isResident ? (selectedCustomer.discountPercentage ?? 0) : 0;
    const residentDiscountAmount = residentDiscountPercent > 0
      ? ((totalBeforeDiscount - rewardDiscount - mealPlanDiscount - manualDiscount) * residentDiscountPercent) / 100
      : 0;
    let totalAfterDiscount = totalBeforeDiscount - rewardDiscount - mealPlanDiscount - manualDiscount - residentDiscountAmount;
    if (orderType === 'delivery') {
      totalAfterDiscount += Math.max(0, deliveryCharge);
    }
    if (orderType === 'uber-doordash') {
      const pct = integrationSettings?.uberDoordashPriceIncreasePercent ?? 0;
      const multiplier = 1 + pct / 100;
      totalAfterDiscount = totalAfterDiscount * multiplier;
    }
    const tax = totalAfterDiscount * (15 / 115);
    const subtotal = totalAfterDiscount - tax;
    return { subtotal, tax, totalAfterDiscount, mealPlanDiscount };
  };

  const { subtotal: orderSubtotal, tax: orderTax, totalAfterDiscount: orderTotal, mealPlanDiscount: orderMealPlanDiscount } = calculateOrderTotals();

  // If register is not open, show guard
  if (!registerSession) {

    return (
      <div className="flex flex-col h-full bg-gray-50">
        <POSHeader 
          registerSession={registerSession}
          activeSessions={activeSessions}
          terminalId={terminalId}
          terminalName={terminalName}
          onTerminalNameChange={onTerminalNameChange || (() => {})}
          todaysOrders={todaysOrders}
          sessionOrders={sessionOrders}
          inventoryItems={inventoryItems}
          recipes={recipes}
          onOpenRegister={onOpenRegister}
          onCloseRegister={onCloseRegister}
          onStockTake={onStockTake}
          onForceCloseSession={onForceCloseSession}
          onRequireManagerPin={onRequireManagerPin}
          currentUser={currentUser}
          showForceClose={showForceClose}
          onUpdateOrder={onUpdateOrder}
          onMarkAsPaid={onMarkAsPaid}
        />
        <RegisterClosedGuard 
          onOpenRegister={onOpenRegister}
          todaysOrders={todaysOrders}
          currentUser={currentUser}
          isLoading={!registerSessionsHydrated}
        />
      </div>
    );
  }

  // Check if user is manager
  const isManager = currentUser && (
    (currentUser as any).permissions?.has('access_reports') || 
    (currentUser as any).permissions?.has('manage_staff') ||
    currentUser.isAdmin
  );

  return (
    <>
      <FireModeBanner isActive={fireModeActive || false} />
      <div className="flex flex-col h-full bg-gray-50" data-testid="pos-shell">
      <POSHeader 
        registerSession={registerSession}
        activeSessions={activeSessions}
        terminalId={terminalId}
        terminalName={terminalName}
        onTerminalNameChange={onTerminalNameChange || (() => {})}
        sessionOrders={sessionOrders}
        todaysOrders={todaysOrders}
        inventoryItems={inventoryItems}
        recipes={recipes}
        onOpenRegister={onOpenRegister}
        onCloseRegister={onCloseRegister}
        onStockTake={onStockTake}
        onForceCloseSession={onForceCloseSession}
        onRequireManagerPin={onRequireManagerPin}
        currentUser={currentUser}
        showForceClose={showForceClose}
        onUpdateOrder={onUpdateOrder}
        onMarkAsPaid={onMarkAsPaid}
      />
      {uberPendingOrders.length > 0 && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-300 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-yellow-800">
              Uber / DoorDash incoming orders: {uberPendingOrders.length}
            </p>
            <p className="text-xs text-yellow-700">
              Review and accept or reject these delivery orders here. They will appear in POS and kitchen once accepted.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
             {/* Main Navigation Tabs */}
             <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center justify-between gap-2 min-h-0">
                <div className="flex items-center gap-2">
                {hasActiveOrders && (
                  <button 
                    onClick={() => {
                    if (buffetPaymentLocked) return;
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('active-orders');
                    setActiveView('menu');
                  }}
                    className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'active-orders' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    📋 Active Orders ({todaysOrders.length})
                  </button>
                )}
                <button 
                  onClick={() => {
                    if (buffetPaymentLocked) return;
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setSelectedTable(null);
                    setPosView('new-order');
                    setOrderType(null);
                    setActiveView('floor');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'new-order' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ➕ New Order
                </button>
                <button 
                  onClick={() => {
                    if (buffetPaymentLocked) return;
                    // Preserve in-progress buffet package + table when hopping to floor
                    const keepBuffet = orderType === 'buffet' || !!selectedBuffetPackage;
                    clearOrder(!keepBuffet, !keepBuffet);
                    if (!keepBuffet) {
                      setPlacedOrder(null);
                      setSelectedTable(null);
                      setOrderType('dine-in');
                    } else {
                      setOrderType('buffet');
                      setBuffetExtrasMenuOpen(false);
                      // Keep selectedTable — clearOrder(false) already preserved it
                    }
                    setPosView('floor');
                    setActiveView('floor');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'floor' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  🏪 Floor Plan
                </button>
                <button 
                  onClick={() => {
                    if (buffetPaymentLocked) return;
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('pending');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'pending' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ⏳ Pending
                </button>
                <button 
                  onClick={() => {
                    if (buffetPaymentLocked) return;
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('completed');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'completed' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ✅ Completed
                </button>
                <button 
                  onClick={() => {
                    if (buffetPaymentLocked) return;
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('cancelled');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'cancelled' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ❌ Cancelled ({cancelledTabBadgeCount})
                </button>
                <button 
                  onClick={() => {
                    if (buffetPaymentLocked) return;
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('refunded');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${posView === 'refunded' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  💸 Refunded ({refundedTabBadgeCount})
                </button>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-auto">
                <button 
                  onClick={() => {
                    clearOrder(true, true);
                    setPlacedOrder(null);
                    setPosView('reservations');
                    setActiveView('menu');
                  }}
                  className={`px-3 py-1 text-xs font-bold rounded-md transition-colors shrink-0 ${posView === 'reservations' ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  📅 Reservation
                </button>
                <button
                  type="button"
                  onClick={() => setShowMealPlanOverlay(true)}
                  className="px-3 py-1 text-xs font-bold rounded-md transition-colors shrink-0 bg-amber-100 text-amber-900 hover:bg-amber-200"
                >
                  MEAL PLN
                </button>
                {currentUser?.canManageMenu === true && onSaveMenuItem && onDeleteMenuItem && onSaveCategory && onDeleteCategory && onBulkAddMenuItems && onSaveCombo && onDeleteCombo && onSaveRecipe && (
                  <button
                    type="button"
                    onClick={() => setShowMenuManagementOverlay(true)}
                    className="px-3 py-1 text-xs font-bold rounded-md transition-colors shrink-0 bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                  >
                    MNG MENU
                  </button>
                )}
                {currentUser?.canManageCustomers === true && onSaveCustomer && onDeleteCustomer && (
                  <button
                    type="button"
                    onClick={() => setShowCustomerManagementOverlay(true)}
                    className="px-3 py-1 text-xs font-bold rounded-md transition-colors shrink-0 bg-teal-100 text-teal-800 hover:bg-teal-200"
                  >
                    MNG CUST
                  </button>
                )}
                </div>
            </div>
            
            {/* View-specific navigation for ordering */}
            {(posView === 'new-order' || posView === 'floor') && !orderType && (
              <div className="px-4 py-2 bg-gradient-to-r from-pink-50 to-rose-50 border-b border-pink-200">
                <p className="text-xs text-gray-600 mb-2">Select order type:</p>
                <div className="flex gap-2">
                  <button onClick={() => handleSelectOrderType('dine-in')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🪑 Dine-In</button>
                  <button onClick={() => handleSelectOrderType('takeaway')} data-testid="pos-order-type-takeaway" className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🛍️ Takeaway</button>
                  <button onClick={() => handleSelectOrderType('delivery')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🚚 Delivery</button>
                  <button onClick={() => handleSelectOrderType('buffet')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">🍽️ Buffet</button>
                  <button onClick={() => handleSelectOrderType('uber-doordash')} className="px-3 py-1 text-sm font-bold bg-white border-2 border-pink-300 text-pink-700 rounded-lg hover:bg-pink-100 transition-colors">📦 Uber/DoorDash</button>
                </div>
              </div>
            )}
            
            {/* Refunded Orders View */}
            {posView === 'refunded' && activeView === 'menu' && (() => {
              const refundedSource = mergeOrderSources(historyOrdersFetched, orders);
              const filteredRefunded = filterOrdersByHistoryDate(
                refundedSource.filter(
                  (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'refunded',
                ),
                historyOrdersDateFilter,
                historyDateFilterOpts,
              );

              return (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-800">Refunded Orders</h2>
                      <HistoryOrdersDateFilterBar
                        filter={historyOrdersDateFilter}
                        onFilterChange={setHistoryOrdersDateFilter}
                        customDateStart={customDateStart}
                        customDateEnd={customDateEnd}
                        onCustomDateStartChange={setCustomDateStart}
                        onCustomDateEndChange={setCustomDateEnd}
                      />
                    </div>
                    {historyOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading orders…</p>
                    ) : filteredRefunded.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No refunded orders found for the selected period.
                      </p>
                    ) : (
                      <div className={POS_ORDER_LIST_GRID_CLASS}>
                        {filteredRefunded.map(order => (
                          <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-gray-500 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { openOrderDetails(order); }}>
                                <p className="font-bold text-sm">
                                  {order.orderType === 'buffet'
                                    ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                    : order.orderType === 'dine-in'
                                    ? (order.tableName || (() => {
                                        const table = tables.find(t => t.id === order.tableId);
                                        return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                      })())
                                    : order.orderType === 'takeaway'
                                    ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`)
                                    : order.orderType === 'online'
                                    ? `Online Order`
                                    : order.orderType === 'event'
                                    ? `Event`
                                    : order.orderType === 'uber-doordash'
                                    ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                    : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                                </p>
                                {/* Only show customer name on second line when not already in title (e.g. Uber/DoorDash, dine-in) */}
                                {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                  <p className="text-sm text-pink-600 font-semibold">{order.customerName || order.deliveryInfo?.customerName}</p>
                                )}
                                {order.orderType === 'online' && order.onlineCustomerPhone?.trim() ? (
                                  <p className="text-sm text-gray-800 font-medium">
                                    <span className="text-gray-500 font-normal">Phone: </span>
                                    <a
                                      href={`tel:${order.onlineCustomerPhone.replace(/[^\d+]/g, '')}`}
                                      className="text-pink-700 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {order.onlineCustomerPhone.trim()}
                                    </a>
                                  </p>
                                ) : null}
                                <p className="text-xs text-gray-600">Order ID: {order.id}</p>
                                <p className="text-xs text-gray-500">
                                  {(() => {
                                    let orderDate: Date;
                                    if (typeof order.timestamp === 'number') {
                                      orderDate = new Date(order.timestamp);
                                    } else if (order.timestamp instanceof Date) {
                                      orderDate = order.timestamp;
                                    } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                                      orderDate = new Date((order.timestamp as any).seconds * 1000);
                                    } else {
                                      return 'Invalid Date';
                                    }
                                    if (isNaN(orderDate.getTime())) {
                                      return 'Invalid Date';
                                    }
                                    return orderDate.toLocaleString('en-NZ', {
                                      timeZone: 'Pacific/Auckland',
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                  })()}
                                </p>
                              </div>
                              <span className="text-xs font-bold px-2 py-1 rounded bg-gray-100 text-gray-700">
                                REFUNDED
                              </span>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { openOrderDetails(order); }}>
                                <p className="text-xs text-gray-600">{order.items.length} items • ${getOrderTotals(order).totalAmount.toFixed(2)}</p>
                                <p className="text-xs text-gray-500 mt-1 font-bold">💸 Refund processed</p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openOrderDetails(order); }}
                                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                >
                                  Details
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </main>
              );
            })()}
            {posView === 'active-orders' && activeView === 'menu' && (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className="max-w-4xl mx-auto space-y-3">
                    <h2 className="text-lg font-bold text-gray-800">Active Orders Today</h2>
                    <div className="grid gap-3 md:grid-cols-2">
                      {todaysOrders.map(order => (
                        <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-pink-500 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 cursor-pointer" onClick={() => handleSelectPendingOrder(order)}>
                                <p className="font-bold text-sm">
                                {order.orderType === 'buffet' 
                                  ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                  : order.orderType === 'dine-in' 
                                  ? (order.tableName || (() => {
                                      const table = tables.find(t => t.id === order.tableId);
                                      return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                    })())
                                  : order.orderType === 'takeaway' 
                                  ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`) 
                                  : order.orderType === 'online'
                                  ? `Online Order`
                                  : order.orderType === 'event'
                                  ? `Event`
                                  : order.orderType === 'uber-doordash'
                                  ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                  : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                              </p>
                              {/* Only show customer name on second line when not already in title */}
                              {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                <p className="text-sm text-pink-600 font-semibold">
                                  {order.customerName || order.deliveryInfo?.customerName}
                                </p>
                              )}
                              {order.orderType === 'online' && order.onlineCustomerPhone?.trim() ? (
                                <p className="text-sm text-gray-800 font-medium">
                                  <span className="text-gray-500 font-normal">Phone: </span>
                                  <a
                                    href={`tel:${order.onlineCustomerPhone.replace(/[^\d+]/g, '')}`}
                                    className="text-pink-700 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {order.onlineCustomerPhone.trim()}
                                  </a>
                                </p>
                              ) : null}
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-xs text-gray-600">Order ID: {order.id.slice(-6)}</p>
                                {formatPickupTimeDisplay(order, { style: 'short' }) ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-300">
                                    {`Pickup ${formatPickupTimeDisplay(order, { style: 'short' })}`}
                                  </span>
                                ) : null}
                              </div>
                              {order.orderType === 'buffet' && (
                                <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded mt-1 inline-block">
                                  BUFFET
                                </span>
                              )}
                              {order.orderType === 'buffet' && order.buffetEndTime && (
                                <div className="mt-1">
                                  <BuffetTimer endTime={order.buffetEndTime} />
                                </div>
                              )}
                            </div>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${order.status === 'ready' ? 'bg-green-100 text-green-700' : order.status === 'preparing' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                              {order.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <div className="flex-1 cursor-pointer" onClick={() => handleSelectPendingOrder(order)}>
                              <p className="text-xs text-gray-600">
                                {order.orderType === 'buffet' 
                                  ? `Buffet ${order.buffetType ? `(${order.buffetType})` : ''}${order.items.length > 0 ? ` + ${order.items.reduce((sum, item) => sum + item.quantity, 0)} items` : ''} • $${getOrderTotals(order).totalAmount.toFixed(2)}`
                                  : `${order.items.reduce((sum, item) => sum + item.quantity, 0)} items • $${getOrderTotals(order).totalAmount.toFixed(2)}`}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">Payment: {order.paymentStatus === 'paid' ? '✅ Paid' : '⏳ Unpaid'}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openOrderDetails(order); }}
                                className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              >
                                Details
                              </button>
                              {isActiveBuffetSession(order) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEndBuffetSessionForOrder(order); }}
                                  disabled={isCompletingBuffet}
                                  className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-60"
                                >
                                  {isCompletingBuffet ? 'Ending...' : 'Complete early'}
                                </button>
                              )}
                              {order.orderType === 'buffet' && onExtendBuffetTime && order.buffetEndTime && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateBuffetExtension(order); }}
                                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Extend
                                </button>
                              )}
                              {(order.orderType === 'dine-in' || order.orderType === 'buffet') && order.paymentStatus === 'unpaid' && order.status !== 'comped' && onTransferOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateTransfer(order); }}
                                  className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                                >
                                  Transfer
                                </button>
                              )}
                              {(order.paymentStatus === 'paid' || order.status === 'served' || order.status === 'ready') && onRemakeOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateRemake(order); }}
                                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Remake
                                </button>
                              )}
                              {order.orderType === 'uber-doordash' && onUpdateOrderStatus && (
                                <>
                                  {order.status !== 'ready' && order.status !== 'served' && !order.deliveryInfo?.uberReadySentAt && (
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const now = Date.now();
                                          patchPendingOrderFields(order.id, {
                                            status: 'ready',
                                            readyAt: now,
                                            deliveryInfo: {
                                              ...order.deliveryInfo,
                                              uberReadySentAt: order.deliveryInfo?.uberReadySentAt ?? now,
                                            },
                                          });
                                          onUpdateOrderStatus(order.id, 'ready');
                                        }}
                                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                      >
                                        Mark Ready
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setUberIssueOrder(order);
                                          setUberIssueSelectedItemIndices([]);
                                          setUberIssueReason('OUT_OF_STOCK');
                                          setUberIssueExplanation('');
                                        }}
                                        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                      >
                                        Report item issue
                                      </button>
                                    </>
                                  )}
                                  {(order.status === 'ready' || !!order.deliveryInfo?.uberReadySentAt) && order.status !== 'served' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        patchPendingOrderFields(order.id, { status: 'served', servedAt: Date.now() });
                                        onUpdateOrderStatus(order.id, 'served');
                                      }}
                                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                                    >
                                      Picked Up
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </main>
            )}
            
            {/* Floor Plan View */}
            {posView === 'floor' && activeView === 'floor' && (orderType === 'dine-in' || orderType === 'buffet') && (
                <div className="flex flex-col flex-1 overflow-hidden">
                    {/* Table Management Controls */}
                    <div className="p-4 bg-white border-b flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleToggleMergeMode}
                                className={`px-4 py-2 rounded transition-colors ${
                                    isMergeMode
                                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {isMergeMode ? 'Cancel Merge Mode' : 'Merge Tables'}
                            </button>
                            {orderType === 'buffet' && selectedBuffetPackage && selectedTable && !placedOrder && (
                              <button
                                type="button"
                                onClick={handleOpenBuffetPeopleModal}
                                className="px-4 py-2 rounded bg-pink-500 text-white hover:bg-pink-600 transition-colors font-semibold"
                              >
                                Set covers — {selectedTable.name}
                              </button>
                            )}
                            {isMergeMode && (
                                <>
                                    <button
                                        onClick={handleMergeClick}
                                        disabled={selectedTableIds.length < 2}
                                        className={`px-4 py-2 rounded transition-colors ${
                                            selectedTableIds.length >= 2
                                                ? 'bg-green-500 text-white hover:bg-green-600'
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                    >
                                        Merge Selected ({selectedTableIds.length})
                                    </button>
                                    {selectedTableIds.length > 0 && (
                                        <button
                                            onClick={() => setSelectedTableIds([])}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                        >
                                            Clear Selection
                                        </button>
                                    )}
                                </>
                            )}
                            {((!isMergeMode && selectedTableForSplit) ||
                              (isMergeMode && mergedPrimarySelectedInMergeMode)) && (
                                <button
                                    onClick={() => {
                                      const target =
                                        isMergeMode && mergedPrimarySelectedInMergeMode
                                          ? mergedPrimarySelectedInMergeMode
                                          : selectedTableForSplit;
                                      if (target) handleSplitTable(target.id);
                                    }}
                                    className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                    Split merged table:{' '}
                                    {(isMergeMode && mergedPrimarySelectedInMergeMode
                                      ? mergedPrimarySelectedInMergeMode
                                      : selectedTableForSplit
                                    )?.name}
                                </button>
                            )}
                        </div>
                        {isMergeMode && (
                            <p className="text-sm text-gray-600">
                                Select tables to merge (click to select/deselect)
                                {mergedPrimarySelectedInMergeMode &&
                                  ' — or select only this merged table and use Split to unmerge.'}
                            </p>
                        )}
                        {!isMergeMode && selectedTableForSplit && (
                            <p className="text-sm text-gray-600">
                                Click on a merged table to split it
                            </p>
                        )}
                    </div>
                    <FloorPlanView 
                        tables={tables} 
                        orders={pendingOrdersSource} 
                        reservations={reservations} 
                        layoutComponents={layoutComponents} 
                        businessTimeZone={businessTimeZone}
                        onTableClick={handleSelectTableFromFloor}
                        isMergeMode={isMergeMode}
                        selectedTableIds={selectedTableIds}
                        onTableSelect={handleTableSelect}
                        onSplitTable={handleSplitTable}
                        onTransferOrder={handleInitiateTransfer}
                        onTransferReservation={
                          onTransferReservation
                            ? (reservation, sourceTable) => {
                                handleInitiateReservationTransfer(reservation, sourceTable);
                              }
                            : undefined
                        }
                        onSeatNewParty={(table, context) => {
                          // Floor plan always opens table details first; "Seat new party" is the
                          // common path for available tables. Buffet must open BuffetPeopleModal —
                          // never DineInCoversModal (that clears the package and jumps to menu).
                          const pkg = selectedBuffetPackageRef.current ?? selectedBuffetPackage;
                          if (orderType === 'buffet' || pkg) {
                            beginBuffetCoversForTable(table, context);
                            return;
                          }
                          // Reservation seating: party size already known — skip covers prompt.
                          if (context?.partySize && context.partySize > 0) {
                            seatDineInFromReservation(
                              table,
                              context.partySize,
                              context.customerName,
                            );
                            return;
                          }
                          const tableActiveOrders = pendingOrdersSource.filter((o) =>
                            isFloorPlanActiveTableOrder(o, table.id, businessTimeZone),
                          );
                          const spareSeats = Math.max(0, table.capacity - getTableSeatsUsed(tableActiveOrders));
                          setPendingDineInTable(table);
                          setPendingDineInSpareSeats(spareSeats);
                          setPendingDineInCustomerName(undefined);
                          setPendingDineInBookedPartySize(null);
                          setAdjustingReservationCovers(false);
                          setShowDineInCoversModal(true);
                        }}
                        onSelectOrder={handleSelectPendingOrder}
                        onEndBuffetSession={handleEndBuffetSessionForOrder}
                        onUpdateReservation={onUpdateReservation}
                    />
                </div>
            )}

            {/* General reservations (non-event) — create/edit/cancel; seating primarily from floor */}
            {posView === 'reservations' && (
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Reservations</h2>
                    <p className="text-sm text-gray-600">Today’s general bookings. Seat guests from an amber table on the Floor Plan.</p>
                  </div>
                  {onSaveReservation && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingPosReservation(null);
                        setShowPosReservationModal(true);
                      }}
                      className="px-4 py-2 bg-pink-500 text-white text-sm font-semibold rounded-lg hover:bg-pink-600"
                    >
                      + New reservation
                    </button>
                  )}
                </div>
                {generalReservationsToday.length === 0 ? (
                  <p className="text-gray-500 text-sm">No general reservations for today.</p>
                ) : (
                  <ul className="space-y-2 max-w-3xl">
                    {generalReservationsToday.map((r) => {
                      const joinedNames =
                        r.assignedTableIds && r.assignedTableIds.length > 1
                          ? r.assignedTableIds
                              .map(
                                (id) =>
                                  tables.find((t) => Number(t.id) === Number(id))?.name ??
                                  `Table ${id}`,
                              )
                              .join(' + ')
                          : null;
                      const tableName =
                        joinedNames ??
                        tables.find((t) => Number(t.id) === Number(r.tableId))?.name ??
                        (r.tableId ? `Table ${r.tableId}` : '—');
                      return (
                        <li key={r.id} className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2 shadow-sm">
                          <div>
                            <p className="font-bold text-gray-900">{r.customerName}</p>
                            <p className="text-sm text-gray-600">
                              {r.reservationTime} · {tableName}
                              {r.requiresMerge ? ' (join)' : ''} · Party of {r.partySize}
                              <span className="ml-2 text-xs uppercase font-semibold text-amber-700">{r.status}</span>
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(r.status === 'pending' || r.status === 'confirmed') && onUpdateReservation && (
                              <button
                                type="button"
                                onClick={() => void onUpdateReservation(r.id, { status: 'seated' })}
                                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-amber-600 text-white hover:bg-amber-700"
                              >
                                Seat
                              </button>
                            )}
                            {onSaveReservation && (
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingPosReservation(r);
                                  setShowPosReservationModal(true);
                                }}
                                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200"
                              >
                                Edit
                              </button>
                            )}
                            {onTransferReservation &&
                              (r.status === 'pending' ||
                                r.status === 'confirmed' ||
                                r.status === 'seated') &&
                              r.tableId != null && (
                              <button
                                type="button"
                                onClick={() => handleInitiateReservationTransfer(r)}
                                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-orange-500 text-white hover:bg-orange-600"
                              >
                                Transfer
                              </button>
                            )}
                            {onUpdateReservation && r.status !== 'cancelled' && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (confirm(`Cancel reservation for ${r.customerName}?`)) {
                                    void onUpdateReservation(r.id, { status: 'cancelled' });
                                  }
                                }}
                                className="px-3 py-1.5 text-xs font-semibold rounded-md text-red-700 hover:bg-red-50"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {completedReservationsToday.length > 0 && (
                  <div className="mt-6 max-w-3xl">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">
                      Completed today ({completedReservationsToday.length})
                    </h3>
                    <ul className="space-y-2">
                      {completedReservationsToday.map((r) => {
                        const tableName =
                          r.assignedTableIds && r.assignedTableIds.length > 1
                            ? r.assignedTableIds
                                .map(
                                  (id) =>
                                    tables.find((t) => Number(t.id) === Number(id))?.name ??
                                    `Table ${id}`,
                                )
                                .join(' + ')
                            : tables.find((t) => Number(t.id) === Number(r.tableId))?.name ??
                              (r.tableId ? `Table ${r.tableId}` : '—');
                        return (
                          <li
                            key={r.id}
                            className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2"
                          >
                            <div>
                              <p className="font-semibold text-gray-800">{r.customerName}</p>
                              <p className="text-sm text-gray-600">
                                {r.reservationTime} · {tableName} · Party of {r.partySize}
                                <span className="ml-2 text-xs uppercase font-semibold text-green-700">
                                  completed
                                </span>
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {/* Buffet: package covers → pay; then add drinks/desserts from menu */}
            {posView === 'new-order' && orderType === 'buffet' && !buffetExtrasMenuOpen && (
                <main className="flex-1 p-6 flex flex-col items-center justify-center bg-gray-50 gap-3">
                  {!resolvedPlacedOrder ? (
                    <>
                      <p className="text-lg font-semibold text-gray-800 mb-1">
                        {selectedBuffetPackage ? selectedBuffetPackage.name : 'Buffet'}
                      </p>
                      <p className="text-sm text-gray-600 mb-2 text-center max-w-md">
                        Select a table on the floor plan, set covers, and take payment. Add drinks or desserts after the session is open.
                      </p>
                      <button
                        type="button"
                        onClick={() => { setPosView('floor'); setActiveView('floor'); }}
                        className="px-6 py-3 bg-pink-500 text-white font-semibold rounded-lg hover:bg-pink-600 transition-colors"
                      >
                        View floor plan
                      </button>
                      {selectedBuffetPackage && selectedTable && (
                        <button
                          type="button"
                          onClick={handleOpenBuffetPeopleModal}
                          className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Set covers &amp; pay — {selectedTable.name}
                        </button>
                      )}
                    </>
                  ) : !orderFullySettled ? (
                    <>
                      <p className="text-lg font-semibold text-gray-800">
                        {selectedBuffetPackage?.name || resolvedPlacedOrder.notes || 'Buffet'} — payment due
                      </p>
                      <p className="text-sm text-gray-600 text-center max-w-md">
                        {isPlacingOrder
                          ? 'Creating order… payment will open when ready.'
                          : getAmountPaid(resolvedPlacedOrder) > 0
                            ? `Balance due${selectedTable ? ` on ${selectedTable.name}` : ''} for extras or remaining covers.`
                            : `Covers are set${selectedTable ? ` on ${selectedTable.name}` : ''}. Take payment to open the buffet session.`}
                      </p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${Math.max(
                          getOrderTotals(resolvedPlacedOrder).totalAmount,
                          Number(resolvedPlacedOrder.totalAmount) || 0,
                        ).toFixed(2)}
                      </p>
                      {getAmountPaid(resolvedPlacedOrder) > 0 && (
                        <p className="text-sm text-gray-600">
                          Paid ${getAmountPaid(resolvedPlacedOrder).toFixed(2)} · due $
                          {Math.max(
                            0,
                            getOrderTotals(resolvedPlacedOrder).totalAmount - getAmountPaid(resolvedPlacedOrder),
                          ).toFixed(2)}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (resolvedPlacedOrder) {
                            setPlacedOrder(orderWithLocalDiscounts(resolvedPlacedOrder));
                          }
                          setIsPaymentModalOpen(true);
                        }}
                        disabled={isPlacingOrder}
                        className="px-8 py-4 bg-green-600 text-white text-lg font-bold rounded-lg hover:bg-green-700 transition-colors shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {isPlacingOrder ? 'Creating order…' : 'Pay now'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPosView('floor'); setActiveView('floor'); }}
                        className="px-4 py-2 text-sm text-pink-600 hover:text-pink-700"
                      >
                        Back to floor plan
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-semibold text-gray-800">Buffet session open</p>
                      <p className="text-sm text-gray-600 text-center max-w-md">
                        Covers paid{selectedTable ? ` on ${selectedTable.name}` : ''}. Add drinks or desserts if needed — those lines go to the kitchen.
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setBuffetExtrasMenuOpen(true);
                          setPosView('new-order');
                          setActiveView('menu');
                        }}
                        className="px-8 py-4 bg-amber-500 text-white text-lg font-bold rounded-lg hover:bg-amber-600 transition-colors shadow-md"
                      >
                        Add extras
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPosView('floor'); setActiveView('floor'); }}
                        className="px-4 py-2 text-sm text-pink-600 hover:text-pink-700"
                      >
                        Done — back to floor plan
                      </button>
                    </>
                  )}
                </main>
            )}
            {/* Menu View — dine-in/takeaway/etc, or buffet extras only when explicitly opened */}
            {posView === 'new-order' && orderType && (orderType !== 'buffet' || buffetExtrasMenuOpen) && activeView === 'menu' && (
                <div className="flex flex-1 overflow-hidden flex-col min-h-0">
                    {orderType === 'buffet' && (
                      <div className="w-full px-3 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-900 shrink-0 flex flex-wrap items-center justify-between gap-2">
                        <span>
                          Adding extras — buffet cover stays on the order. Tap <strong>Send extras to kitchen</strong> when ready.
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setBuffetExtrasMenuOpen(false);
                            setPosView('new-order');
                            setActiveView('floor');
                          }}
                          className="px-3 py-1.5 text-sm font-semibold rounded-md bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                        >
                          Done adding extras
                        </button>
                      </div>
                    )}
                    <div className="flex flex-1 overflow-hidden flex-col md:flex-row min-h-0">
                    <CategoryList 
                        categories={visibleCategories}
                        selectedCategoryId={selectedCategoryId}
                        onSelectCategory={(id) => { setSelectedCategoryId(id); setMenuSearchQuery(''); }}
                        onMoveCategory={onMoveCategory}
                        onReorderCategory={onReorderCategory}
                    />
                    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
                        <div className="flex-shrink-0 px-2 py-2 bg-white border-b border-gray-200">
                          <input
                            type="search"
                            placeholder="Search menu..."
                            value={menuSearchQuery}
                            onChange={(e) => setMenuSearchQuery(e.target.value)}
                            className="w-full px-3 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                            aria-label="Search menu items"
                          />
                        </div>
                        <QuickAddBar items={quickAddCombos} onAdd={handleAddComboToOrder} />
                        <MenuItemsGrid 
                            items={displayedItemsFiltered}
                            onAddItem={handleAddItemToOrder}
                            orders={orders}
                        />
                    </div>
                    </div>
                </div>
            )}
            
            {/* Pending Orders View: unpaid orders + takeaway paid but not yet picked up (today only for takeaway to avoid old completed orders) */}
            {posView === 'pending' && activeView === 'menu' && (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <h2 className="text-lg font-bold text-gray-800">Pending Orders</h2>
                    {pendingOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading pending orders…</p>
                    ) : (
                    <div className={POS_ORDER_LIST_GRID_CLASS}>
                      {pendingOrdersSource.filter(order => {
                        if (isSampleOrDemoOrderId(String(order.id))) return false;
                        return isPendingPosOrder(order);
                      }).map(order => {
                        const pickupLabel = formatPickupTimeDisplay(order, { style: 'short' });
                        const pickupLong = formatPickupTimeDisplay(order);
                        const itemQty = order.items.reduce((sum, item) => sum + item.quantity, 0);
                        const totals = getOrderTotals(order);
                        const statusLine = pendingPaymentStatusLine(order);
                        return (
                        <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-yellow-500 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-2.5">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0 cursor-pointer space-y-0.5" onClick={() => handleSelectPendingOrder(order)}>
                                <p className="font-bold text-sm leading-snug">
                                {order.orderType === 'buffet' 
                                  ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                  : order.orderType === 'dine-in' 
                                  ? (order.tableName || (() => {
                                      const table = tables.find(t => t.id === order.tableId);
                                      return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                    })())
                                  : order.orderType === 'takeaway' 
                                  ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`) 
                                  : order.orderType === 'online'
                                  ? `Online Order`
                                  : order.orderType === 'event'
                                  ? `Event`
                                  : order.orderType === 'uber-doordash'
                                  ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                  : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                              </p>
                              {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                <p className="text-sm text-pink-600 font-semibold truncate">
                                  {order.customerName || order.deliveryInfo?.customerName}
                                </p>
                              )}
                              {order.orderType === 'online' && order.onlineCustomerPhone?.trim() ? (
                                <p className="text-sm text-gray-800 font-medium">
                                  <span className="text-gray-500 font-normal">Phone: </span>
                                  <a
                                    href={`tel:${order.onlineCustomerPhone.replace(/[^\d+]/g, '')}`}
                                    className="text-pink-700 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {order.onlineCustomerPhone.trim()}
                                  </a>
                                </p>
                              ) : null}
                              <p className="text-xs text-gray-600">
                                Order ID: {order.id.slice(-6)}
                                <span className="text-gray-400"> · </span>
                                {order.orderType === 'online' ? `Placed ${formatOrderPlacedDisplay(order)}` : formatOrderPlacedDisplay(order)}
                              </p>
                              {order.orderType === 'online' && (pickupLabel || pickupLong) ? (
                                <p className="pt-0.5">
                                  <span className="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs font-bold text-blue-900">
                                    Pickup {pickupLabel || pickupLong}
                                  </span>
                                </p>
                              ) : null}
                              {order.orderType === 'buffet' && (
                                <span className="text-xs font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded mt-0.5 inline-block">
                                  BUFFET
                                </span>
                              )}
                              {order.orderType === 'buffet' && order.buffetEndTime && (
                                <div className="pt-0.5">
                                  <BuffetTimer endTime={order.buffetEndTime} />
                                </div>
                              )}
                            </div>
                            <span className={`shrink-0 text-xs font-bold px-2 py-1 rounded ${order.status === 'ready' ? 'bg-green-100 text-green-700' : order.status === 'preparing' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                              {order.status.toUpperCase()}
                            </span>
                          </div>

                          <div className="border-t border-gray-100 pt-2 space-y-2">
                            <div
                              className="cursor-pointer space-y-0.5"
                              onClick={() => handleSelectPendingOrder(order)}
                            >
                              <p className="text-xs text-gray-600">
                                {order.orderType === 'buffet' 
                                  ? `Buffet ${order.buffetType ? `(${order.buffetType})` : ''}${order.items.length > 0 ? ` + ${itemQty} items` : ''} • $${totals.totalAmount.toFixed(2)}`
                                  : `${itemQty} items • $${totals.totalAmount.toFixed(2)}`}
                              </p>
                              <p className={`text-xs font-bold ${order.paymentStatus === 'paid' ? 'text-green-700' : 'text-amber-700'}`}>
                                {order.paymentStatus === 'paid' ? '✅ ' : '⏳ '}
                                {statusLine}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openOrderDetails(order); }}
                                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              >
                                Details
                              </button>
                              {isActiveBuffetSession(order) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEndBuffetSessionForOrder(order); }}
                                  disabled={isCompletingBuffet}
                                  className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors disabled:opacity-60"
                                >
                                  {isCompletingBuffet ? 'Ending...' : 'Complete early'}
                                </button>
                              )}
                              {(order.orderType === 'takeaway' || order.orderType === 'online') && order.paymentStatus === 'paid' && order.status !== 'served' && onUpdateOrderStatus && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onUpdateOrderStatus(order.id, 'served');
                                  }}
                                  className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                >
                                  Order Picked Up
                                </button>
                              )}
                              {order.orderType === 'uber-doordash' && onUpdateOrderStatus && (
                                <>
                                  {!order.deliveryInfo?.uberReadySentAt && order.status !== 'ready' && order.status !== 'served' && (
                                    <>
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (order.deliveryInfo?.platform === 'Uber Eats') {
                                            await handleSendUberReady(order);
                                          } else {
                                            const now = Date.now();
                                            patchPendingOrderFields(order.id, {
                                              status: 'ready',
                                              readyAt: now,
                                              deliveryInfo: {
                                                ...order.deliveryInfo,
                                                uberReadySentAt: now,
                                              },
                                            });
                                            onUpdateOrderStatus(order.id, 'ready');
                                          }
                                        }}
                                        className="px-3 py-1.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                                      >
                                        Ready for Pick Up
                                      </button>
                                      {order.deliveryInfo?.platform === 'Uber Eats' && order.deliveryInfo?.orderId && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setUberIssueOrder(order);
                                            setUberIssueSelectedItemIndices([]);
                                            setUberIssueReason('OUT_OF_STOCK');
                                            setUberIssueExplanation('');
                                          }}
                                          className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                        >
                                          Report item issue
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {(order.deliveryInfo?.uberReadySentAt || order.status === 'ready') && order.status !== 'served' && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkUberDoordashPickedUp(order);
                                      }}
                                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                                    >
                                      Mark Picked Up
                                    </button>
                                  )}
                                </>
                              )}
                              {order.orderType === 'buffet' && onExtendBuffetTime && order.buffetEndTime && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateBuffetExtension(order); }}
                                  className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Extend
                                </button>
                              )}
                              {(order.orderType === 'dine-in' || order.orderType === 'buffet') && order.paymentStatus === 'unpaid' && order.status !== 'comped' && onTransferOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateTransfer(order); }}
                                  className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                                >
                                  Transfer
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                </main>
            )}
            
            {/* Completed Orders View */}
            {posView === 'completed' && activeView === 'menu' && (() => {
              const completedOrdersSource = mergeOrderSources(historyOrdersFetched, orders);

              // Filter orders based on selected filter
              const getFilteredCompletedOrders = () => {
                const baseFiltered = completedOrdersSource.filter((order) => {
                  if (isSampleOrDemoOrderId(String(order.id))) return false;
                  return isCompletedPosOrder(order);
                });
                return filterOrdersByHistoryDate(
                  baseFiltered,
                  historyOrdersDateFilter,
                  historyDateFilterOpts,
                );
              };

              let filteredOrders = getFilteredCompletedOrders();
              if (completedOrderTypeFilter !== 'all') {
                filteredOrders = filteredOrders.filter(order => order.orderType === completedOrderTypeFilter);
              }

              return (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-800">Completed Orders (Paid)</h2>
                      <HistoryOrdersDateFilterBar
                        filter={historyOrdersDateFilter}
                        onFilterChange={setHistoryOrdersDateFilter}
                        customDateStart={customDateStart}
                        customDateEnd={customDateEnd}
                        onCustomDateStartChange={setCustomDateStart}
                        onCustomDateEndChange={setCustomDateEnd}
                        extraControls={
                          <>
                            <span className="text-xs font-medium text-gray-500 mr-1">Type:</span>
                            <select
                              value={completedOrderTypeFilter}
                              onChange={(e) => setCompletedOrderTypeFilter(e.target.value as OrderType | 'all')}
                              className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white"
                            >
                              <option value="all">All</option>
                              <option value="dine-in">Dine-in</option>
                              <option value="takeaway">Takeaway</option>
                              <option value="online">Online</option>
                              <option value="delivery">Delivery</option>
                              <option value="uber-doordash">Uber/DoorDash</option>
                              <option value="buffet">Buffet</option>
                              <option value="event">Event</option>
                            </select>
                          </>
                        }
                      />
                    </div>
                    {historyOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading orders…</p>
                    ) : filteredOrders.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No completed orders found for the selected period.
                      </p>
                    ) : (
                      <div className={POS_ORDER_LIST_GRID_CLASS}>
                        {filteredOrders.map(order => (
                        <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-green-500 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 cursor-pointer" onClick={() => { openOrderDetails(order); }}>
                              <p className="font-bold text-sm flex items-center gap-2">
                                {order.orderType === 'buffet'
                                  ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                  : order.orderType === 'dine-in'
                                  ? (order.tableName || (() => {
                                      const table = tables.find(t => t.id === order.tableId);
                                      return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                    })())
                                  : order.orderType === 'takeaway'
                                  ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`)
                                  : order.orderType === 'online'
                                  ? `Online Order`
                                  : order.orderType === 'event'
                                  ? `Event`
                                  : order.orderType === 'uber-doordash'
                                  ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                  : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                                {order.orderType === 'buffet' && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                                    BUFFET
                                  </span>
                                )}
                                {order.orderType === 'event' && (
                                  <span className="text-xs font-bold px-2 py-0.5 bg-amber-100 text-amber-900 rounded tabular-nums">
                                    Ticket{' '}
                                    {formatEventTokenShortBadge(getEventTokenDisplayLabel(order, completedEventTokenLabelById) ?? '') || '—'}
                                  </span>
                                )}
                              </p>
                              {/* Only show customer name on second line when not already in title */}
                              {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                <p className="text-sm text-pink-600 font-semibold">{order.customerName || order.deliveryInfo?.customerName}</p>
                              )}
                              {order.orderType === 'online' && order.onlineCustomerPhone?.trim() ? (
                                <p className="text-sm text-gray-800 font-medium">
                                  <span className="text-gray-500 font-normal">Phone: </span>
                                  <a
                                    href={`tel:${order.onlineCustomerPhone.replace(/[^\d+]/g, '')}`}
                                    className="text-pink-700 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {order.onlineCustomerPhone.trim()}
                                  </a>
                                </p>
                              ) : null}
                              <p className="text-xs text-gray-600">Order ID: {order.id}</p>
                              {order.orderType === 'event' && (
                                <p className="text-xs font-semibold text-amber-900 mt-0.5">
                                  Ticket{' '}
                                  {formatEventTokenShortBadge(getEventTokenDisplayLabel(order, completedEventTokenLabelById) ?? '') || '—'}
                                </p>
                              )}
                              <p className="text-xs text-gray-500">
                                {(() => {
                                  let orderDate: Date;
                                  if (typeof order.timestamp === 'number') {
                                    orderDate = new Date(order.timestamp);
                                  } else if (order.timestamp instanceof Date) {
                                    orderDate = order.timestamp;
                                  } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                                    orderDate = new Date((order.timestamp as any).seconds * 1000);
                                  } else {
                                    return 'Invalid Date';
                                  }
                                  if (isNaN(orderDate.getTime())) {
                                    return 'Invalid Date';
                                  }
                                  return orderDate.toLocaleString('en-NZ', {
                                    timeZone: 'Pacific/Auckland',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  });
                                })()}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-xs font-bold px-2 py-1 rounded bg-green-100 text-green-700">
                                PAID
                              </span>
                              {orderHasAppliedDiscount(order) && (
                                <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 text-blue-700">
                                  Discount
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <div className="flex-1 cursor-pointer" onClick={() => { openOrderDetails(order); }}>
                              <p className="text-xs text-gray-600">{order.items.length} items • ${getOrderTotals(order).totalAmount.toFixed(2)}</p>
                              <p className="text-xs text-green-600 mt-1 font-bold">✅ Completed</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); openOrderDetails(order); }}
                                className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                              >
                                Details
                              </button>
                              {(order.paymentStatus === 'paid' || order.status === 'served' || order.status === 'ready') && onRemakeOrder && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleInitiateRemake(order); }}
                                  className="px-3 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                >
                                  Remake
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        ))}
                      </div>
                    )}
                  </div>
                </main>
              );
            })()}
            {/* Cancelled Orders View */}
            {posView === 'cancelled' && activeView === 'menu' && (() => {
              const cancelledSource = mergeOrderSources(historyOrdersFetched, orders);
              const filteredCancelled = filterOrdersByHistoryDate(
                cancelledSource.filter(
                  (o) => !isSampleOrDemoOrderId(String(o.id)) && o.status === 'cancelled',
                ),
                historyOrdersDateFilter,
                historyDateFilterOpts,
              );

              return (
                <main className="flex-1 p-4 overflow-y-auto">
                  <div className={POS_ORDER_LIST_SECTION_CLASS}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-800">Cancelled Orders</h2>
                      <HistoryOrdersDateFilterBar
                        filter={historyOrdersDateFilter}
                        onFilterChange={setHistoryOrdersDateFilter}
                        customDateStart={customDateStart}
                        customDateEnd={customDateEnd}
                        onCustomDateStartChange={setCustomDateStart}
                        onCustomDateEndChange={setCustomDateEnd}
                      />
                    </div>
                    {historyOrdersFetchLoading ? (
                      <p className="text-sm text-gray-500 text-center py-8">Loading orders…</p>
                    ) : filteredCancelled.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-8">
                        No cancelled orders found for the selected period.
                      </p>
                    ) : (
                      <div className={POS_ORDER_LIST_GRID_CLASS}>
                        {filteredCancelled.map(order => (
                          <div key={order.id} className="bg-white rounded-lg p-3 border-l-4 border-red-500 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { openOrderDetails(order); }}>
                                <p className="font-bold text-sm">
                                  {order.orderType === 'buffet'
                                    ? `${order.buffetType ? order.buffetType.charAt(0).toUpperCase() + order.buffetType.slice(1) : 'Buffet'} Buffet - ${order.tableName || `Table ${order.tableNumber || order.tableId}`}`
                                    : order.orderType === 'dine-in'
                                    ? (order.tableName || (() => {
                                        const table = tables.find(t => t.id === order.tableId);
                                        return table ? table.name : `Table ${order.tableNumber || order.tableId}`;
                                      })())
                                    : order.orderType === 'takeaway'
                                    ? (order.customerName ? <><span>Takeaway - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Takeaway #${order.takeawayNumber}`)
                                    : order.orderType === 'online'
                                    ? `Online Order`
                                    : order.orderType === 'event'
                                    ? `Event`
                                    : order.orderType === 'uber-doordash'
                                    ? (order.deliveryInfo?.platform ?? 'Uber Eats / DoorDash')
                                    : (order.customerName ? <><span>Delivery - </span><span className="text-pink-600 font-semibold">{order.customerName}</span></> : `Delivery #${order.deliveryNumber}`)}
                                </p>
                                {/* Only show customer name on second line when not already in title (e.g. Uber/DoorDash, dine-in) */}
                                {(order.customerName || order.deliveryInfo?.customerName) && !((order.orderType === 'takeaway' || order.orderType === 'delivery') && order.customerName) && (
                                  <p className="text-sm text-pink-600 font-semibold">{order.customerName || order.deliveryInfo?.customerName}</p>
                                )}
                                {order.orderType === 'online' && order.onlineCustomerPhone?.trim() ? (
                                  <p className="text-sm text-gray-800 font-medium">
                                    <span className="text-gray-500 font-normal">Phone: </span>
                                    <a
                                      href={`tel:${order.onlineCustomerPhone.replace(/[^\d+]/g, '')}`}
                                      className="text-pink-700 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {order.onlineCustomerPhone.trim()}
                                    </a>
                                  </p>
                                ) : null}
                                <p className="text-xs text-gray-600">Order ID: {order.id}</p>
                                <p className="text-xs text-gray-500">
                                  {(() => {
                                    let orderDate: Date;
                                    if (typeof order.timestamp === 'number') {
                                      orderDate = new Date(order.timestamp);
                                    } else if (order.timestamp instanceof Date) {
                                      orderDate = order.timestamp;
                                    } else if (order.timestamp && typeof order.timestamp === 'object' && 'seconds' in order.timestamp) {
                                      orderDate = new Date((order.timestamp as any).seconds * 1000);
                                    } else {
                                      return 'Invalid Date';
                                    }
                                    if (isNaN(orderDate.getTime())) {
                                      return 'Invalid Date';
                                    }
                                    return orderDate.toLocaleString('en-NZ', {
                                      timeZone: 'Pacific/Auckland',
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                  })()}
                                </p>
                              </div>
                              <span className="text-xs font-bold px-2 py-1 rounded bg-red-100 text-red-700">
                                CANCELLED
                              </span>
                            </div>
                            <div className="flex justify-between items-center mt-2">
                              <div className="flex-1 cursor-pointer" onClick={() => { openOrderDetails(order); }}>
                                <p className="text-xs text-gray-600">{order.items.length} items • ${getOrderTotals(order).totalAmount.toFixed(2)}</p>
                                <p className="text-xs text-red-600 mt-1 font-bold">⚠️ Order cancelled</p>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openOrderDetails(order); }}
                                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                >
                                  Details
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </main>
              );
            })()}
        </div>
        {showOrderSummary && activeBookedPartySize != null && orderType === 'dine-in' && selectedTable && !placedOrder && (
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex flex-wrap items-center justify-between gap-2 text-sm">
            <p className="text-amber-950">
              Party of <span className="font-semibold">{activePartySize ?? activeBookedPartySize}</span>
              {activePartySize != null && activePartySize !== activeBookedPartySize
                ? ` (booked ${activeBookedPartySize})`
                : ` · booked ${activeBookedPartySize}`}
              {customerName ? ` · ${customerName}` : ''}
            </p>
            <button
              type="button"
              onClick={() => {
                setPendingDineInTable(selectedTable);
                setPendingDineInSpareSeats(
                  Math.max(
                    selectedTable.capacity,
                    activeBookedPartySize ?? 0,
                    activePartySize ?? 0,
                    20,
                  ),
                );
                setPendingDineInCustomerName(customerName);
                setPendingDineInBookedPartySize(activeBookedPartySize);
                setAdjustingReservationCovers(true);
                setShowDineInCoversModal(true);
              }}
              className="text-xs font-semibold text-amber-900 underline hover:no-underline"
            >
              More guests arrived?
            </button>
          </div>
        )}
        {showOrderSummary && (
        <OrderSummary 
            key={selectedTable?.id ?? 'no-table'}
            orderItems={orderItems}
            notes={notes}
            setNotes={setNotes}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onUpdateItemNote={handleUpdateItemNote}
            onSendToKitchen={handleSendToKitchen}
            onUpdateOrder={handleUpdateOrder}
            onCancelOrder={handleCancelOrder}
            onReleaseTable={handleReleaseTable}
            onInitiatePayment={handleInitiatePayment}
            onClearOrder={handleNewOrder} // Clear order now always goes to new order state
            onNewOrder={handleNewOrder}
            onSelectCustomer={() => setIsCustomerModalOpen(true)}
            onSelectTable={() => {
              setPosView('floor');
              setActiveView('floor');
            }}
            onRedeemReward={() => setIsRewardModalOpen(true)}
            selectedCustomer={selectedCustomer}
            selectedTable={selectedTable}
            selectedServingStaff={selectedServingStaff}
            staffMembers={staffMembers}
            currentUser={currentUser}
            onSelectServingStaff={setSelectedServingStaff}
            orderType={orderType || 'dine-in'}
            setOrderType={(type) => {
              if (type !== 'uber-doordash') setUberDoordashDetails(null);
              if (type !== 'takeaway' && type !== 'delivery') setOrderCustomerName(null);
              if (type !== 'delivery') setDeliveryCharge(0);
              if (type === 'buffet') {
                setBuffetExtrasMenuOpen(false);
                setShowBuffetTypeSelector(true);
                setOrderType('buffet');
                setPosView('floor');
                setActiveView('floor');
                return;
              }
              setBuffetExtrasMenuOpen(false);
              setOrderType(type);
              if (type === 'dine-in' && !selectedTable) {
                setActiveView('floor');
              }
            }}
            appliedReward={appliedReward}
            onRemoveReward={() => setAppliedReward(null)}
            todayMealBookings={integrationSettings?.businessInfo?.mealPlansEnabled === true ? todayMealBookings : []}
            appliedMealBooking={integrationSettings?.businessInfo?.mealPlansEnabled === true ? appliedMealBooking : null}
            onCollectMealPlan={integrationSettings?.businessInfo?.mealPlansEnabled === true ? handleCollectMealPlan : undefined}
            onRemoveMealPlan={() => setAppliedMealBooking(null)}
            isSent={!!placedOrder}
            isPaid={orderFullySettled}
            onPrintReceipt={() =>
              resolvedPlacedOrder && handlePrintReceipt(buildCustomerReceiptOrder(resolvedPlacedOrder))
            }
            rewards={rewards}
            orderStatus={resolvedPlacedOrder?.status}
            manualDiscount={manualDiscount}
            discountRequiresApproval={discountRequiresApproval}
            discountApprovedBy={discountApprovedBy}
            discountApprovalReason={discountApprovalReason}
            discountMethod={discountMethod}
            firstTableDiscountAmount={firstTableDiscountAmount}
            onApplyDiscount={() => setIsDiscountModalOpen(true)}
            onRemoveDiscount={handleRemoveDiscount}
            onRemake={resolvedPlacedOrder && orderFullySettled && resolvedPlacedOrder.orderType !== 'buffet' ? () => handleInitiateRemake(resolvedPlacedOrder) : undefined}
            onTransfer={resolvedPlacedOrder && (resolvedPlacedOrder.orderType === 'dine-in' || resolvedPlacedOrder.orderType === 'buffet') && resolvedPlacedOrder.paymentStatus === 'unpaid' && resolvedPlacedOrder.status !== 'comped' ? () => handleInitiateTransfer(resolvedPlacedOrder) : undefined}
            currentOrder={resolvedPlacedOrder || undefined}
            isManager={isManager}
            buffetPricing={undefined}
            selectedBuffetType={selectedBuffetType}
            selectedBuffetPackageName={selectedBuffetPackage?.name}
            buffetPackagePrice={selectedBuffetPackage?.price}
            onContinueBuffet={orderType === 'buffet' && selectedBuffetPackage ? handleOpenBuffetPeopleModal : undefined}
            onAddBuffetExtras={
              resolvedPlacedOrder && isActiveBuffetSession(resolvedPlacedOrder)
                ? () => {
                    setBuffetExtrasMenuOpen(true);
                    setPosView('new-order');
                    setActiveView('menu');
                    setOrderType('buffet');
                    if (resolvedPlacedOrder.buffetType) setSelectedBuffetType(resolvedPlacedOrder.buffetType);
                    if (resolvedPlacedOrder.buffetPackageId || resolvedPlacedOrder.buffetType) {
                      const pkgs = getBuffetPackages(integrationSettings);
                      const pkg =
                        (resolvedPlacedOrder.buffetPackageId
                          ? pkgs.find((p) => p.id === resolvedPlacedOrder.buffetPackageId)
                          : undefined) ||
                        pkgs.find((p) => p.mealPeriod === resolvedPlacedOrder.buffetType);
                      if (pkg) setSelectedBuffetPackage(pkg);
                    }
                  }
                : undefined
            }
            buffetExtrasMenuOpen={buffetExtrasMenuOpen}
            uberDoordashPriceIncreasePercent={integrationSettings?.uberDoordashPriceIncreasePercent ?? 0}
            orderCustomerDisplayName={orderType === 'uber-doordash' ? (resolvedPlacedOrder?.deliveryInfo?.customerName ?? resolvedPlacedOrder?.customerName ?? uberDoordashDetails?.customerName ?? null) : (orderType === 'takeaway' || orderType === 'delivery') ? (resolvedPlacedOrder?.customerName ?? orderCustomerName ?? null) : null}
            pendingDeliveryCharge={deliveryCharge}
            onPendingDeliveryChargeChange={setDeliveryCharge}
            onExtendBuffetTime={resolvedPlacedOrder && resolvedPlacedOrder.orderType === 'buffet' ? () => handleInitiateBuffetExtension(resolvedPlacedOrder) : undefined}
            onCompleteBuffet={resolvedPlacedOrder && isActiveBuffetSession(resolvedPlacedOrder) ? handleCompleteBuffetOrder : undefined}
            isCompletingBuffet={isCompletingBuffet}
            onMarkAsPaid={isManager && resolvedPlacedOrder && (resolvedPlacedOrder.paymentStatus === 'unpaid' || hasBalanceDue(resolvedPlacedOrder)) ? () => onMarkAsPaid?.(resolvedPlacedOrder.id) : undefined}
            onMarkPickedUp={
              resolvedPlacedOrder &&
              (resolvedPlacedOrder.orderType === 'takeaway' || resolvedPlacedOrder.orderType === 'online') &&
              resolvedPlacedOrder.paymentStatus === 'paid' &&
              resolvedPlacedOrder.status !== 'served' &&
              onUpdateOrderStatus
                ? () => onUpdateOrderStatus(resolvedPlacedOrder.id, 'served')
                : undefined
            }
            onMarkUberPickedUp={
              resolvedPlacedOrder?.orderType === 'uber-doordash' &&
              resolvedPlacedOrder.status !== 'served' &&
              (!!resolvedPlacedOrder.deliveryInfo?.uberReadySentAt || resolvedPlacedOrder.status === 'ready')
                ? () => handleMarkUberDoordashPickedUp(resolvedPlacedOrder)
                : undefined
            }
            onMarkUberReady={
              resolvedPlacedOrder?.orderType === 'uber-doordash' &&
              !resolvedPlacedOrder.deliveryInfo?.uberReadySentAt &&
              resolvedPlacedOrder.status !== 'ready' &&
              resolvedPlacedOrder.status !== 'served'
                ? () => handleSendUberReady(resolvedPlacedOrder)
                : undefined
            }
        />
        )}
        {uberPendingOrders.length > 0 && (
          <div className="border-t border-gray-200 px-3 pb-4 pt-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Uber / DoorDash – Incoming</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {uberPendingOrders.map((order) => (
                <div key={order.id} className="border rounded-md px-3 py-2 bg-green-50 border-green-300">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-green-800">Uber Eats Order</span>
                    <span className="text-[11px] font-mono text-gray-600 truncate">#{order.platformOrderId}</span>
                  </div>
                  <p className="text-xs text-gray-700 mb-1">
                    Customer: <span className="font-medium">{order.customerName}</span>
                  </p>
                  <ul className="text-xs text-gray-700 mb-2">
                    {order.items.map((item, idx) => (
                      <li key={idx}>
                        {item.quantity}x {item.name}
                        {item.specialInstructions && (
                          <span className="text-[11px] text-gray-500"> – {item.specialInstructions}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => onRejectUberDeliveryOrder && onRejectUberDeliveryOrder(order)}
                      className="flex-1 px-2 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => onAcceptUberDeliveryOrder && onAcceptUberDeliveryOrder(order)}
                      className="flex-1 px-2 py-1 rounded-md text-xs font-semibold bg-green-600 text-white hover:bg-green-700"
                    >
                      Accept
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
      {isCustomerModalOpen && (
        <CustomerSelectModal
          customers={customers}
          onSelect={handleSelectCustomer}
          onClose={() => setIsCustomerModalOpen(false)}
          onCreateCustomer={onCreateCustomer}
        />
      )}
      {uberIssueOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl border border-gray-200">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">Report item issue (Uber Eats)</h3>
                <p className="text-xs text-gray-500 truncate">
                  Order: {uberIssueOrder.id} • Uber ID: {uberIssueOrder.deliveryInfo?.orderId || '—'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (isSubmittingUberIssue) return;
                  setUberIssueOrder(null);
                }}
                className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Close
              </button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Select affected items</p>
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  {uberIssueOrder.items.map((it, idx) => {
                    const checked = uberIssueSelectedItemIndices.includes(idx);
                    return (
                      <label key={idx} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setUberIssueSelectedItemIndices((prev) => {
                              if (prev.includes(idx)) return prev.filter((x) => x !== idx);
                              return [...prev, idx];
                            });
                          }}
                        />
                        <span className="text-sm text-gray-800">
                          {it.quantity}x {it.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Reason</label>
                  <select
                    value={uberIssueReason}
                    onChange={(e) => setUberIssueReason(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  >
                    <option value="OUT_OF_STOCK">Out of stock</option>
                    <option value="ITEM_INCORRECT">Item incorrect</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Note (optional)</label>
                  <input
                    value={uberIssueExplanation}
                    onChange={(e) => setUberIssueExplanation(e.target.value)}
                    maxLength={200}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    placeholder="Short explanation"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">{uberIssueExplanation.length}/200</p>
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (isSubmittingUberIssue) return;
                  setUberIssueOrder(null);
                }}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmittingUberIssue}
                onClick={handleSubmitUberIssue}
                className="px-3 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {isSubmittingUberIssue ? 'Sending…' : 'Send to Uber'}
              </button>
            </div>
          </div>
        </div>
      )}
       {pendingMealCollectVerify && selectedCustomer && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Check student ID</h3>
            <p className="text-sm text-gray-600">
              First visit for <span className="font-medium">{selectedCustomer.name}</span>. Please check their
              student ID and mark verified so we do not ask again. Collect still works either way.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                className="px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setPendingMealCollectVerify(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm rounded-md border border-indigo-200 text-indigo-800 hover:bg-indigo-50"
                onClick={() => {
                  const booking = pendingMealCollectVerify;
                  if (booking) void applyMealPlanCollect(booking);
                }}
              >
                Continue without verifying
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={() => void handleMarkStudentVerifiedAndCollect()}
              >
                Mark verified &amp; Collect
              </button>
            </div>
          </div>
        </div>
      )}
       {isRewardModalOpen && selectedCustomer && (
        <RewardRedemptionModal
          customer={selectedCustomer}
          rewards={rewards}
          onSelect={handleSelectReward}
          onClose={() => setIsRewardModalOpen(false)}
        />
      )}
      {isDiscountModalOpen && (
        <DiscountModal
          isOpen={isDiscountModalOpen}
          onClose={() => setIsDiscountModalOpen(false)}
          onApplyDiscount={handleApplyDiscount}
          currentTotal={(() => {
            const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
            const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
            return totalBeforeDiscount - rewardDiscount;
          })()}
          existingDiscount={manualDiscount}
          onApplyCoupon={handleApplyCoupon}
          couponError={couponError}
          activeCouponCode={
            couponCode
              ? couponUnitLabel
                ? `${couponUnitLabel} (${couponCode})`
                : couponCode
              : null
          }
          hasPaidSubBills={(placedOrder?.subBills?.length ?? 0) > 0}
          firstTableConfig={(() => {
            const cfg = integrationSettings?.firstTable;
            const hasCategoryRules = (cfg?.eligibleCategoryIds?.length ?? 0) > 0;
            const hasItemRules = (cfg?.eligibleMenuItemIds?.length ?? 0) > 0;
            if (!cfg?.enabled || !cfg.discountPercent || (!hasCategoryRules && !hasItemRules)) {
              return undefined;
            }
            const eligibleCategoryIds = (cfg.eligibleCategoryIds ?? []).map((id) => String(id));
            const eligibleMenuItemIds = new Set<number>(cfg.eligibleMenuItemIds ?? []);
            const percent = cfg.discountPercent;
            const orderItemsForDiscount = orderItems
              .filter((item) => !item.removed)
              .map((item) => {
                const originalTotal = item.price * item.quantity;
                const isEligible =
                  eligibleMenuItemIds.has(item.id as number) ||
                  eligibleCategoryIds.includes(String(item.categoryId));
                return {
                  name: item.name,
                  quantity: item.quantity,
                  originalTotal,
                  isEligible,
                };
              });
            const eligibleItems = orderItemsForDiscount
              .filter((item) => item.isEligible)
              .map((item) => ({
                name: item.name,
                quantity: item.quantity,
                originalTotal: item.originalTotal,
                discountedTotal: item.originalTotal * (1 - percent / 100),
              }));
            const eligibleSubtotal = eligibleItems.reduce((sum, it) => sum + it.originalTotal, 0);
            const eligibleCategoryNames = categories
              .filter((c) => eligibleCategoryIds.includes(String(c.id)))
              .map((c) => c.name)
              .sort((a, b) => a.localeCompare(b));
            return {
              enabled: true,
              discountPercent: cfg.discountPercent,
              eligibleSubtotal,
              eligibleCategoryNames,
              eligibleItems,
              orderItems: orderItemsForDiscount,
            };
          })()}
        />
      )}
      {isApprovalModalOpen && (
        <ManagerApprovalModal
          isOpen={isApprovalModalOpen}
          onClose={() => setIsApprovalModalOpen(false)}
          onApprove={handleApproveDiscount}
          discountAmount={pendingDiscount}
          discountPercentage={(() => {
            const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
            const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
            const subtotal = totalBeforeDiscount - rewardDiscount;
            return subtotal > 0 ? (pendingDiscount / subtotal) * 100 : 0;
          })()}
          orderTotal={(() => {
            const totalBeforeDiscount = orderItems.reduce((sum, item) => sum + (item.removed ? 0 : item.price * item.quantity), 0);
            const rewardDiscount = appliedReward ? appliedReward.discountValue : 0;
            return totalBeforeDiscount - rewardDiscount;
          })()}
          staffMembers={staffMembers}
          currentUser={currentUser}
        />
      )}
      {isPaymentModalOpen && resolvedPlacedOrder && (
        <PaymentModal
          order={resolvedPlacedOrder}
          customer={resolvedPlacedOrder.customerId ? (customers.find(c => c.id === resolvedPlacedOrder.customerId) || null) : null}
          terminals={terminals}
          onClose={handleClosePaymentModal}
          onProcessPayment={onProcessPayment}
          onPrintReceipt={(order) => handlePrintReceipt(buildCustomerReceiptOrder(order))}
          onNewOrder={handleFinalNewOrder}
          onSyncPaymentStatus={onMarkAsPaid}
          integrationSettings={integrationSettings}
          onPrintSubBillPreview={(printOrder, preview) => {
            setSubBillToPrint({ order: printOrder, preview });
          }}
          onReprintSubBill={(printOrder, sb) => {
            setSubBillToPrint({ order: printOrder, subBill: sb });
          }}
        />
      )}
      {selectedHistoryOrder && (
        <OrderDetailsModal
          order={selectedHistoryOrder}
          customer={historyOrderCustomer}
          terminals={terminals}
          integrationSettings={integrationSettings}
          menuItems={menuItems}
          onClose={() => setSelectedHistoryOrder(null)}
          onPrintReceipt={(order) => handlePrintReceipt(buildCustomerReceiptOrder(order))}
          onReprintSubBill={(printOrder, sb) => {
            setSubBillToPrint({ order: printOrder, subBill: sb });
          }}
          onRefundSubBill={onRefundSubBill}
          onOverride={handleInitiateOverride}
          onRemake={handleInitiateRemake}
          onTransfer={handleInitiateTransfer}
          onExtendBuffetTime={selectedHistoryOrder?.orderType === 'buffet' && selectedHistoryOrder?.buffetEndTime ? (order) => handleInitiateBuffetExtension(order) : undefined}
          onCompleteBuffetOrder={selectedHistoryOrder && isActiveBuffetSession(selectedHistoryOrder) ? (order) => handleEndBuffetSessionForOrder(order) : undefined}
          onPayBalance={(order) => {
            const liveOrder = orders.find(o => o.id === order.id) || order;
            setPlacedOrder(orderReadyForPayment(liveOrder));
            setLastKitchenPrintOrder(null);
            setSelectedHistoryOrder(null);
            setIsPaymentModalOpen(true);
          }}
          isManager={isManager}
          onRefund={async (orderId, refundAmount, refundTransactionId) => {
            // Find the order and add a refund payment
            const order = orders.find(o => o.id === orderId);
            if (!order) return;

            const methodFromToken = refundTransactionId.startsWith('method:')
              ? (refundTransactionId.slice('method:'.length) as Payment['method'])
              : null;
            const isCashRefund = refundTransactionId === 'cash' || methodFromToken === 'cash';
            const method: Payment['method'] = methodFromToken || (isCashRefund ? 'cash' : 'card');
            const now = Date.now();
            const refundPayment: Payment = {
              method,
              amount: -refundAmount, // Negative amount for refund
              timestamp: now,
              ...(method === 'card'
                ? {
                    transactionId: refundTransactionId,
                    refundTransactionId: refundTransactionId,
                  }
                : {}),
            };

            const updatedPayments = [...order.payments, refundPayment];
            const updatedOrder = {
              ...order,
              payments: updatedPayments,
              paymentStatus: 'paid',
              status: 'refunded' as const,
            };
            await onUpdateOrder(updatedOrder);

            // Audit log for refund (cash or card)
            try {
              const actor = currentUser;
              await auditLogsService.add({
                timestamp: now,
                actorId: actor?.id,
                actorName: actor?.name,
                actorRole: actor?.role,
                action: 'order.refund',
                entityType: 'order',
                entityId: orderId,
                details: {
                  method,
                  amount: refundAmount,
                  refundTransactionId,
                  previousPaymentsCount: order.payments.length,
                },
              });
            } catch (e) {
              console.error('Failed to write refund audit log', e);
            }

            alert(`Refund of $${refundAmount.toFixed(2)} has been processed and recorded.`);
          }}
        />
      )}
      {isConfirmationDisplayOpen && orderType && (
        <CustomerConfirmationDisplay
          orderItems={orderItems}
          subtotal={orderSubtotal}
          taxAmount={orderTax}
          totalAmount={orderTotal}
          appliedReward={appliedReward}
          notes={notes}
          orderType={orderType}
          deliveryCharge={orderType === 'delivery' ? Math.max(0, deliveryCharge) : 0}
          onConfirm={handleConfirmOrder}
          onCancel={handleCancelConfirmation}
          isPlacingOrder={isPlacingOrder}
        />
      )}
      {showUberDoordashModal && (
        <UberDoordashOrderModal
          onConfirm={handleUberDoordashModalSubmit}
          onCancel={() => setShowUberDoordashModal(false)}
        />
      )}
      {showCustomerNameModal && pendingOrderTypeForCustomerName && (
        <CustomerNameModal
          orderTypeLabel={pendingOrderTypeForCustomerName === 'takeaway' ? 'Takeaway' : 'Delivery'}
          onConfirm={handleCustomerNameModalSubmit}
          onCancel={() => {
            setShowCustomerNameModal(false);
            setPendingOrderTypeForCustomerName(null);
          }}
        />
      )}
       {printPendingOrder && (
          <PrintTypeModal
            order={printPendingOrder}
            onChoose={handlePrintTypeChosen}
            onCancel={() => setPrintPendingOrder(null)}
          />
       )}
       {orderToPrint && (
          printType === 'customer' ? (
            <PrintableReceipt ref={printRef} order={orderToPrint} businessInfo={integrationSettings?.businessInfo} />
          ) : (
            <PrintableKitchenOrder ref={printRef} order={orderToPrint} businessInfo={integrationSettings?.businessInfo} />
          )
       )}
       {subBillToPrint && (
          <PrintableReceipt
            ref={printRef}
            order={subBillToPrint.order}
            businessInfo={integrationSettings?.businessInfo}
            subBill={subBillToPrint.subBill}
            subBillPreview={subBillToPrint.preview}
          />
       )}
       {meatOptionModalItem && (
         <MeatOptionModal
           item={meatOptionModalItem}
           onSelect={handleMeatOptionSelected}
           onCancel={() => setMeatOptionModalItem(null)}
         />
       )}
       {showMergeModal && (
         <TableMergeModal
           selectedTables={
             selectedTableIds
               .map(id => tables.find(t => Number(t.id) === Number(id)))
               .filter(Boolean) as Table[]
           }
           orders={orders}
           onConfirm={handleConfirmMerge}
           onCancel={() => {
             setShowMergeModal(false);
             setSelectedTableIds([]);
           }}
         />
       )}
       {showSplitModal && tableToSplit && (
         <TableSplitModal
           mergedTable={tableToSplit}
           originalTables={tableToSplit.mergedWith
             ? tableToSplit.mergedWith.map(id => tables.find(t => Number(t.id) === Number(id))).filter(Boolean) as Table[]
             : []}
           orders={orders}
           onConfirm={handleConfirmSplit}
           onCancel={() => {
             setShowSplitModal(false);
             setTableToSplit(null);
           }}
         />
       )}
       {showPosReservationModal && onSaveReservation && (
         <PosGeneralReservationModal
           isOpen={showPosReservationModal}
           onClose={() => {
             setShowPosReservationModal(false);
             setEditingPosReservation(null);
           }}
           reservation={editingPosReservation}
           tables={tables}
           reservations={reservations}
           onSave={async (data) => {
             if (editingPosReservation && onUpdateReservation) {
               await onUpdateReservation(editingPosReservation.id, data);
             } else {
               await onSaveReservation(data);
             }
           }}
         />
       )}
       {showTransferModal && orderToTransfer && (
         <OrderTransferModal
           sourceTable={orderToTransfer.table}
           order={orderToTransfer.order}
           availableTables={tables.filter(t => 
             t.status === 'available' && 
             t.id !== orderToTransfer.table.id &&
             (!t.mergedWith || t.mergedWith.length <= 1)
           )}
           onConfirm={handleConfirmTransfer}
           onCancel={() => {
             setShowTransferModal(false);
             setOrderToTransfer(null);
           }}
         />
       )}
       {reservationToTransfer && (
         <OrderTransferModal
           sourceTable={reservationToTransfer.table}
           reservationSummary={{
             customerName: reservationToTransfer.reservation.customerName,
             partySize: reservationToTransfer.reservation.partySize,
             time: reservationToTransfer.reservation.reservationTime,
             status: reservationToTransfer.reservation.status,
           }}
           availableTables={tables.filter(
             (t) =>
               t.status === 'available' &&
               Number(t.id) !== Number(reservationToTransfer.table.id) &&
               (!t.mergedWith || t.mergedWith.length <= 1),
           )}
           onConfirm={handleConfirmReservationTransfer}
           onCancel={() => setReservationToTransfer(null)}
         />
       )}
       {showOverrideModal && orderToOverride && onOverrideOrder && (
         <OrderOverrideModal
           order={orderToOverride}
           overrideReasons={integrationSettings?.overrideReasons || []}
           onConfirm={handleConfirmOverride}
           onCancel={() => {
             setShowOverrideModal(false);
             setOrderToOverride(null);
           }}
         />
       )}
       {showRemakeModal && orderToRemake && onRemakeOrder && (
         <RemakeOrderModal
           order={orderToRemake}
           onConfirm={handleConfirmRemake}
           onCancel={() => {
             setShowRemakeModal(false);
             setOrderToRemake(null);
           }}
         />
       )}
       {showCancelApprovalModal && pendingCancelOrder && staffMembers && (
         <CancelOrderApprovalModal
           isOpen={showCancelApprovalModal}
           onClose={() => {
             setShowCancelApprovalModal(false);
             setPendingCancelOrder(null);
             setCancelReason('');
           }}
           onApprove={handleCancelApproval}
           order={pendingCancelOrder}
           staffMembers={staffMembers}
           currentUser={currentUser}
           requiresApproval={requiresManagerApproval(pendingCancelOrder)}
           cancellationReason={cancelReason}
         />
       )}
       {showBuffetExtensionModal && orderToExtend && onExtendBuffetTime && (
         <BuffetExtensionModal
           order={orderToExtend}
           currentUser={currentUser}
           isManager={currentUser?.permissions?.has('manager') || false}
           onConfirm={handleConfirmBuffetExtension}
           onCancel={() => {
             setShowBuffetExtensionModal(false);
             setOrderToExtend(null);
           }}
         />
       )}
       {showFireModeModal && onActivateFireMode && onDeactivateFireMode && (
         <FireModeModal
           isActive={fireModeActive || false}
           orderAction={integrationSettings?.fireMode?.orderAction || 'pause'}
           onActivate={handleConfirmFireModeActivate}
           onDeactivate={handleConfirmFireModeDeactivate}
           onCancel={() => setShowFireModeModal(false)}
         />
       )}
       {showTableCustomerModal && pendingTableSelection && (() => {
         const tableActiveOrders = orders.filter(o => {
           if (!orderMatchesFloorPlanTable(o, pendingTableSelection.id)) return false;
           if (o.status === 'cancelled' || o.status === 'refunded') return false;
           if (!orderHasMeaningfulItems(o)) return false;
           return (o.orderType === 'dine-in' && o.paymentStatus === 'unpaid') || isActiveBuffetSession(o);
         });
         const spareSeats = Math.max(0, pendingTableSelection.capacity - getTableSeatsUsed(tableActiveOrders));
         return (
           <TableCustomerSelectModal
             table={pendingTableSelection}
             activeOrders={tableActiveOrders}
             spareSeats={spareSeats}
             onSelectCustomer={handleSelectTableCustomer}
             onClose={() => {
               setShowTableCustomerModal(false);
               setPendingTableSelection(null);
             }}
           />
         );
       })()}
       {showBuffetTypeSelector && (
         <BuffetTypeSelector
           packages={getBuffetPackages(integrationSettings, undefined, { includeUnavailable: false })}
           onSelect={handleSelectBuffetPackage}
           onCancel={() => {
             setShowBuffetTypeSelector(false);
             setOrderType(null);
             setSelectedBuffetPackage(null);
             setSelectedBuffetType(null);
             selectedBuffetPackageRef.current = null;
             selectedBuffetTypeRef.current = null;
             setBuffetExtrasMenuOpen(false);
           }}
         />
       )}
       {showDineInCoversModal && pendingDineInTable && (
         <DineInCoversModal
           key={`covers-${pendingDineInTable.id}-${pendingDineInBookedPartySize ?? 'walkin'}-${adjustingReservationCovers ? 'adj' : 'new'}`}
           tableName={pendingDineInTable.name}
           maxSeats={pendingDineInSpareSeats || pendingDineInTable.capacity}
           initialCovers={
             adjustingReservationCovers
               ? (activePartySize ?? pendingDineInBookedPartySize ?? undefined)
               : pendingDineInBookedPartySize ?? undefined
           }
           bookedPartySize={pendingDineInBookedPartySize ?? undefined}
           onConfirm={handleSeatNewDineInParty}
           onCancel={() => {
             setShowDineInCoversModal(false);
             setPendingDineInTable(null);
             setPendingDineInSpareSeats(0);
             setPendingDineInCustomerName(undefined);
             setPendingDineInBookedPartySize(null);
             setAdjustingReservationCovers(false);
           }}
         />
       )}
       {showBuffetPeopleModal && selectedBuffetType && selectedBuffetPackage && (
         <BuffetPeopleModal
           key={`buffet-people-${selectedBuffetPackage.id}-${activePartySize ?? buffetNumberOfPeople}-${activeBookedPartySize ?? 'none'}`}
           packageName={selectedBuffetPackage.name}
           mealPeriod={selectedBuffetType}
           price={Number(selectedBuffetPackage.price) || 0}
           initialPeople={activePartySize ?? buffetNumberOfPeople}
           bookedPartySize={activeBookedPartySize ?? undefined}
           confirming={isPlacingOrder}
           errorMessage={buffetPlaceError}
           onConfirm={handleConfirmBuffetPeople}
           onCancel={() => {
            if (isPlacingOrder) return;
            setShowBuffetPeopleModal(false);
            setBuffetPlaceError(null);
           }}
         />
       )}
       {showMenuManagementOverlay && onSaveMenuItem && onDeleteMenuItem && onSaveCategory && onDeleteCategory && onBulkAddMenuItems && onSaveCombo && onDeleteCombo && onSaveRecipe && (
         <PosMenuManagementOverlay
           onClose={() => setShowMenuManagementOverlay(false)}
           menuItems={menuItems}
           categories={categories}
           quickAddCombos={quickAddCombos}
           recipes={recipes}
           inventoryItems={inventoryItems}
           recipeInventoryMode={integrationSettings?.recipeInventoryMode ?? 'inventory_connected'}
           buffetPackages={getBuffetPackages(integrationSettings, undefined, { includeUnavailable: true })}
           onSaveBuffetPackages={onSaveBuffetPackages || (async () => undefined)}
           onSave={onSaveMenuItem}
           onDelete={onDeleteMenuItem}
           onSaveCategory={onSaveCategory}
           onDeleteCategory={onDeleteCategory}
           onBulkAddMenuItems={onBulkAddMenuItems}
           onSaveCombo={onSaveCombo}
           onDeleteCombo={onDeleteCombo}
           onSaveRecipe={onSaveRecipe}
         />
       )}
       {showMealPlanOverlay && (
         <PosMealPlanOverlay
           onClose={() => setShowMealPlanOverlay(false)}
           customers={customers}
           menuItems={menuItems}
           categories={categories}
           orders={orders}
           mealPlansEnabled={integrationSettings?.businessInfo?.mealPlansEnabled === true}
           currentStaffId={currentUser?.id}
           currentStaffName={currentUser?.name}
           isManager={Boolean(isManager)}
           onSell={async (customer, plans: MealPlanType[], weekId) => {
             const seed = Date.now();
             const lines = plans.map((plan, index) => mealPlanSaleLine(plan, weekId, seed + index));
             const sales: MealPlanSale[] = plans.map((plan) => ({
               planTypeId: plan.id,
               planTypeName: plan.name,
               weekId,
               customerId: String(customer.id),
               customerEmail: (customer.email || '').trim().toLowerCase(),
               priceCents: plan.priceCents,
             }));
             const newOrder = await onPlaceOrder(lines, 'takeaway', {
               customerId: customer.id,
               customerName: customer.name,
               mealPlanSale: sales[0],
               ...(sales.length > 1 ? { mealPlanSales: sales } : {}),
             });
             if (!newOrder) throw new Error('Could not create the meal plan ticket.');
             setSelectedCustomer(customer);
             setPlacedOrder(orderReadyForPayment(newOrder));
             setIsPaymentModalOpen(true);
             setShowMealPlanOverlay(false);
           }}
           onOpenSaleRefund={(order, subscription) => setMealPlanRefund({ order, subscription })}
           refreshToken={mealPlanRefresh}
         />
       )}
       {mealPlanRefund && (
         <OrderDetailsModal
           order={mealPlanRefund.order}
           customer={customers.find((c) => String(c.id) === String(mealPlanRefund.subscription.customerId)) || null}
           terminals={terminals}
           integrationSettings={integrationSettings}
           menuItems={menuItems}
           isManager={Boolean(isManager)}
           allowRefund
           layerClassName="z-[100]"
           initialRefundAmount={(() => {
             const order = mealPlanRefund.order;
             const paid = (order.payments || []).filter((p) => p.amount > 0).reduce((sum, p) => sum + p.amount, 0) || order.totalAmount;
             const sales = order.mealPlanSales?.length ? order.mealPlanSales : order.mealPlanSale ? [order.mealPlanSale] : [];
             const matched = sales.find((sale) => sale.planTypeId === mealPlanRefund.subscription.planTypeId);
             if (sales.length > 1 && matched) return Math.round(matched.priceCents) / 100;
             return paid;
           })()}
           refundNotice={mealPlanRefund.order.mealPlanSales && mealPlanRefund.order.mealPlanSales.length > 1
             ? 'Refund this plan only. The other plans on this sale stay active until each one is refunded. Card uses the EFTPOS terminal. Cash and other methods are recorded on the sale and in reports.'
             : 'Refund this payment to cancel the meal plan. The plan stays active until the refund is recorded. Card uses the EFTPOS terminal. Cash and other methods are recorded on the sale and in reports.'}
           onClose={() => setMealPlanRefund(null)}
           onPrintReceipt={(order) => handlePrintReceipt(buildCustomerReceiptOrder(order))}
           onRefund={async (orderId, refundAmount, refundTransactionId) => {
             const pending = mealPlanRefund;
             const order = pending?.order.id === orderId ? pending.order : orders.find((o) => o.id === orderId);
             if (!order || !pending) return;
             const methodFromToken = refundTransactionId.startsWith('method:')
               ? (refundTransactionId.slice('method:'.length) as Payment['method'])
               : null;
             const isCashRefund = refundTransactionId === 'cash';
             const method: Payment['method'] = methodFromToken || (isCashRefund ? 'cash' : 'card');
             const now = Date.now();
             const refundPayment: Payment = {
               method,
               amount: -refundAmount,
               timestamp: now,
               ...(isCashRefund || methodFromToken
                 ? {}
                 : {
                     transactionId: refundTransactionId,
                     refundTransactionId,
                   }),
             };
             const paidIn = (order.payments || []).filter((p) => p.amount > 0).reduce((sum, p) => sum + p.amount, 0);
             const alreadyRefunded = (order.payments || []).filter((p) => p.amount < 0).reduce((sum, p) => sum + Math.abs(p.amount), 0);
             const fullyRefunded = paidIn > 0 && alreadyRefunded + refundAmount >= paidIn - 0.009;
             const cancelledPlanId = pending.subscription.planTypeId;
             const cancelledWeekId = pending.subscription.coveredWeekId;
             const items = (order.items || []).map((item) =>
               item.isMealPlanSale && (
                 item.lineId === `mpsale-${cancelledPlanId}-${cancelledWeekId}`
                 || item.name === `Meal plan · ${pending.subscription.planTypeName || ''}`
               )
                 ? { ...item, removed: true }
                 : item,
             );
             const totals = getOrderTotals({ ...order, items });
             await onUpdateOrder({
               ...order,
               items,
               ...totals,
               payments: [...(order.payments || []), refundPayment],
               paymentStatus: 'paid',
               status: fullyRefunded ? 'refunded' : order.status,
             });
             try {
               const actor = currentUser;
               await auditLogsService.add({
                 timestamp: now,
                 actorId: actor?.id,
                 actorName: actor?.name,
                 actorRole: actor?.role,
                 action: 'order.refund',
                 entityType: 'order',
                 entityId: orderId,
                 details: {
                   method,
                   amount: refundAmount,
                   refundTransactionId,
                   source: 'meal_plan_cancel',
                 },
               });
             } catch (e) {
               console.error('Failed to write meal plan refund audit log', e);
             }
             await cancelMealPlanLocally(pending.subscription, true);
             setMealPlanRefund(null);
             setMealPlanRefresh((n) => n + 1);
             alert(`Refund of $${refundAmount.toFixed(2)} is recorded and the meal plan is cancelled.`);
           }}
         />
       )}
       {showCustomerManagementOverlay && onSaveCustomer && onDeleteCustomer && (
         <PosCustomerManagementOverlay
           onClose={() => setShowCustomerManagementOverlay(false)}
           customers={customers}
           orders={orders}
           onSave={onSaveCustomer}
           onDelete={onDeleteCustomer}
         />
       )}
    </>
  );
};

export default POSPage;